/**
 * Adds an observer to initialise a script when an element is scrolled into view.
 * @param {Element} element - Element to observe.
 * @param {Function} callback - Function to call when element is scrolled into view.
 * @param {number} [threshold=500] - Distance from viewport (in pixels) to trigger init.
 */
function initLazyScript(element, callback, threshold = 500) {
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (typeof callback === 'function') {
            callback();
            observer.unobserve(entry.target);
          }
        }
      });
    }, { rootMargin: `0px 0px ${threshold}px 0px` });

    io.observe(element);
  } else {
    callback();
  }
}

theme.stickyHeaderHeight = () => {
  const v = getComputedStyle(document.documentElement).getPropertyValue('--theme-sticky-header-height');
  if (v) {
    return parseInt(v, 10) || 0;
  }
  return 0;
};

theme.getOffsetTopFromDoc = (el) => el.getBoundingClientRect().top + window.scrollY;

theme.getOffsetLeftFromDoc = (el) => el.getBoundingClientRect().left + window.scrollX;

theme.getScrollParent = (node) => {
  const isElement = node instanceof HTMLElement;
  const overflowY = isElement && window.getComputedStyle(node).overflowY;
  const isScrollable = overflowY !== 'visible' && overflowY !== 'hidden';

  if (!node) {
    return null;
  }

  if (isScrollable && node.scrollHeight > node.clientHeight) {
    return node;
  }

  return theme.getScrollParent(node.parentNode) || document.scrollingElement || window;
};

theme.scrollToRevealElement = (el) => {
  const scrollContainer = theme.getScrollParent(el);
  const scrollTop = scrollContainer === window ? window.scrollY : scrollContainer.scrollTop;
  const scrollVisibleHeight = scrollContainer === window
    ? window.innerHeight : scrollContainer.clientHeight;
  const elTop = theme.getOffsetTopFromDoc(el);
  const elBot = elTop + el.offsetHeight;
  const inViewTop = scrollTop + theme.stickyHeaderHeight();
  const inViewBot = scrollTop + scrollVisibleHeight - 50;

  if (elTop < inViewTop || elBot > inViewBot) {
    scrollContainer.scrollTo({
      top: elTop - 100 - theme.stickyHeaderHeight(),
      left: 0,
      behavior: 'smooth'
    });
  }
};

theme.getEmptyOptionSelectors = (formContainer) => {
  const emptySections = [];

  formContainer.querySelectorAll('[data-selector-type="dropdown"].option-selector').forEach((el) => {
    if (!el.querySelector('[aria-selected="true"][data-value]:not([data-value=""])')) {
      emptySections.push(el);
    }
  });

  formContainer.querySelectorAll('[data-selector-type="listed"].option-selector').forEach((el) => {
    if (!el.querySelector('input:checked')) {
      emptySections.push(el);
    }
  });

  return emptySections;
};

theme.suffixIds = (container, prefix) => {
  const suffixCandidates = ['id', 'for', 'aria-describedby', 'aria-controls'];
  for (let i = 0; i < suffixCandidates.length; i += 1) {
    container.querySelectorAll(`[${suffixCandidates[i]}]`).forEach((el) => el.setAttribute(suffixCandidates[i], el.getAttribute(suffixCandidates[i]) + prefix));
  }
};

theme.addDelegateEventListener = (
  element,
  eventName,
  selector,
  callback,
  addEventListenerParams = null
) => {
  const cb = (evt) => {
    const el = evt.target.closest(selector);
    if (!el) return;
    if (!element.contains(el)) return;
    callback.call(el, evt, el);
  };
  element.addEventListener(eventName, cb, addEventListenerParams);
  return cb;
};

theme.hideAndRemove = (el) => {
  // disable
  el.querySelectorAll('input').forEach((input) => { input.disabled = true; });

  // wrap
  const wrapper = document.createElement('div');
  wrapper.className = 'merge-remove-wrapper';
  el.parentNode.insertBefore(wrapper, el);
  wrapper.appendChild(el);
  el.classList.add('merge-remove-item');
  wrapper.style.height = `${wrapper.clientHeight}px`;

  const cs = getComputedStyle(el);
  const fadeDuration = parseFloat(cs.getPropertyValue('--fade-duration')) * 1000;
  const slideDuration = parseFloat(cs.getPropertyValue('--slide-duration')) * 1000;

  setTimeout(() => {
    wrapper.classList.add('merge-remove-wrapper--fade');

    setTimeout(() => {
      wrapper.classList.add('merge-remove-wrapper--slide');

      setTimeout(() => wrapper.remove(), slideDuration);
    }, fadeDuration);
  }, 10);
};

theme.insertAndReveal = (el, target, iaeCmd, delay) => {
  const initialDelay = delay || 10;
  el.classList.add('merge-add-wrapper');
  target.insertAdjacentElement(iaeCmd, el);
  el.style.height = `${el.firstElementChild.clientHeight}px`;

  const cs = getComputedStyle(el);
  const fadeDuration = parseFloat(cs.getPropertyValue('--fade-duration')) * 1000;
  const slideDuration = parseFloat(cs.getPropertyValue('--slide-duration')) * 1000;

  setTimeout(() => {
    el.classList.add('merge-add-wrapper--slide');

    setTimeout(() => {
      el.classList.add('merge-add-wrapper--fade');

      setTimeout(() => {
        // tidy up
        el.style.height = '';
        el.classList.remove('merge-add-wrapper', 'merge-add-wrapper--slide', 'merge-add-wrapper--fade');
      }, fadeDuration);
    }, slideDuration);
  }, initialDelay);
};

theme.mergeNodes = (newContent, targetContainer) => {
  try {
    // merge: replace content if changed
    newContent.querySelectorAll('[data-merge]').forEach((newEl) => {
      const targetEl = targetContainer.querySelector(`[data-merge="${newEl.dataset.merge}"]`);
      if (!newEl.dataset.mergeCache
        || !targetEl.dataset.mergeCache
        || newEl.dataset.mergeCache !== targetEl.dataset.mergeCache) {
        targetEl.innerHTML = newEl.innerHTML;
        if (newEl.dataset.mergeCache || targetEl.dataset.mergeCache) {
          targetEl.dataset.mergeCache = newEl.dataset.mergeCache;
        }
      }
    });
    // merge: attributes only
    newContent.querySelectorAll('[data-merge-attributes]').forEach((newEl) => {
      const targetEl = targetContainer.querySelector(`[data-merge-attributes="${newEl.dataset.mergeAttributes}"]`);
      const newElAttributeNames = newEl.getAttributeNames();
      for (let i = 0; i < newElAttributeNames.length; i += 1) {
        const attributeName = newElAttributeNames[i];
        targetEl.setAttribute(attributeName, newEl.getAttribute(attributeName));
      }

      /*if(getCookie("enable_currency_codes") == "true"){
        document.querySelectorAll('.price:not(.fully-soldout)').forEach((price) => {
          if(price.dataset.currencyCode == 'CAD' || price.dataset.currencyCode == 'AUD'){
            // console.log(price);
            if(price.querySelector('.price__current') != undefined){
              mainPrice = price.querySelector('.price__current').dataset.currencyCode;
              price.querySelector('.price__current').innerHTML = mainPrice;
            }
            if(price.querySelector('.price__was') != null){
              compareAtPrice = price.querySelector('.price__was').dataset.currencyCode;
              price.querySelector('.price__was').innerHTML = compareAtPrice;
            }
          }
        });
      }*/
    });
    // merge: insert/remove/replace in list
    newContent.querySelectorAll('[data-merge-list]').forEach((newList) => {
      const targetList = targetContainer.querySelector(`[data-merge-list="${newList.dataset.mergeList}"]`);
      let targetListItems = Array.from(targetList.querySelectorAll('[data-merge-list-item]'));
      const newListItems = Array.from(newList.querySelectorAll('[data-merge-list-item]'));

      // remove
      targetListItems.forEach((targetListItem) => {
        // eslint-disable-next-line max-len
        const matchedItem = newListItems.find((item) => item.dataset.mergeListItem === targetListItem.dataset.mergeListItem);
        if (!matchedItem) {
          theme.hideAndRemove(targetListItem);
        }
      });

      // rebuild target list excluding removed items
      targetListItems = Array.from(targetList.querySelectorAll('[data-merge-list-item]:not(.merge-remove-item)'));

      for (let i = 0; i < newListItems.length; i += 1) {
        const newListItem = newListItems[i];
        // eslint-disable-next-line max-len
        const matchedItem = targetListItems.find((item) => item.dataset.mergeListItem === newListItem.dataset.mergeListItem);
        if (matchedItem) {
          // replace if changed
          if (!newListItem.dataset.mergeCache
            || !matchedItem.dataset.mergeCache
            || newListItem.dataset.mergeCache !== matchedItem.dataset.mergeCache) {
            matchedItem.innerHTML = newListItem.innerHTML;
            if (newListItem.dataset.mergeCache) {
              matchedItem.dataset.mergeCache = newListItem.dataset.mergeCache;
            }
          }
        } else {
          // add
          if (i === 0) {
            // first place
            theme.insertAndReveal(newListItem, targetList, 'afterbegin', 500);
          } else if (i >= targetListItems.length) {
            // at end
            theme.insertAndReveal(newListItem, targetList, 'beforeend', 500);
          } else {
            // before element currently at that index
            theme.insertAndReveal(newListItem, targetListItems[i], 'beforebegin', 500);
          }
          // update target list
          targetListItems.splice(i, 0, newListItem);
        }
      }
    });
  } catch (ex) {
    window.location.reload();
  }
};

// Show a short-lived text popup above an element
theme.showQuickPopup = (message, origin) => {
  const offsetLeft = theme.getOffsetLeftFromDoc(origin);
  const offsetTop = theme.getOffsetTopFromDoc(origin);
  const originLeft = origin.getBoundingClientRect().left;
  const popup = document.createElement('div');
  popup.className = 'simple-popup simple-popup--hidden';
  popup.innerHTML = message;
  popup.style.left = `${offsetLeft}px`;
  popup.style.top = `${offsetTop}px`;

  document.body.appendChild(popup);

  let marginLeft = -(popup.clientWidth - origin.clientWidth) / 2;
  if ((originLeft + marginLeft) < 0) {
    // Pull it away from the left edge of the screen
    marginLeft -= (originLeft + marginLeft) - 2;
  }
  // Pull from right edge + small gap
  const offsetRight = offsetLeft + marginLeft + popup.clientWidth + 5;
  if (offsetRight > window.innerWidth) {
    marginLeft -= (offsetRight - window.innerWidth);
  }
  popup.style.marginTop = -popup.clientHeight - 10;
  popup.style.marginLeft = marginLeft;
  setTimeout(() => {
    popup.classList.remove('simple-popup--hidden');
  }, 10);
  setTimeout(() => {
    popup.classList.add('simple-popup--hidden');
  }, 3500);
  setTimeout(() => {
    popup.remove();
  }, 4000);
};

theme.manuallyLoadImages = (container) => {
  container.querySelectorAll('img[data-manual-src]').forEach((el) => {
    el.src = el.dataset.manualSrc;
    el.removeAttribute('data-manual-src');
    if (el.dataset.manualSrcset) {
      el.srcset = el.dataset.manualSrcset;
      el.removeAttribute('data-manual-srcset');
    }
  });
};

theme.whenComponentLoaded = (component, callback) => {
  const components = Symbol.iterator in Object(component) ? [...component] : [component];
  if (!components.find((c) => !c.hasAttribute('loaded'))) {
    callback();
    return;
  }
  const onMutation = (mutationList, observer) => {
    for (let i = 0; i < mutationList.length; i += 1) {
      const mutation = mutationList[i];
      if (mutation.type === 'attributes') {
        if (!components.find((c) => !c.hasAttribute('loaded'))) {
          observer.disconnect();
          callback.call();
        }
      }
    }
  };
  const observer = new MutationObserver(onMutation);
  components.forEach((c) => observer.observe(c, { attributes: true, attributeFilter: ['loaded'] }));
};

/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

window.addEventListener(
  'resize',
  debounce(() => {
    window.dispatchEvent(new CustomEvent('on:debounced-resize'));
  })
);

window.addEventListener(
  'resize',
  debounce(() => {
    window.dispatchEvent(new CustomEvent('on:debounced-resize'));
  })
);

window.addEventListener(
  'scroll',
  debounce(() => {
    /* add custom top for show header properly */
    if(document.querySelector('#announcement-bar-with-slider') != null && document.querySelector('.section-header') != null){
      const topBar = document.querySelector('#announcement-bar-with-slider');
      let top = 0;
      if((topBar && getComputedStyle(topBar).position != '' && getComputedStyle(topBar).position == 'fixed') || (topBar && getComputedStyle(topBar).position != '' && getComputedStyle(topBar).position == 'sticky')){
        top = top + topBar.offsetHeight;
      }
      document.querySelector('.section-header').style.top= `${top}px`;
    }
    
    window.dispatchEvent(new CustomEvent('on:debounced-scroll'));
  })
);

/**
 * Creates a 'mediaMatches' object from the media queries specified in the theme,
 * and adds listeners for each media query. If a breakpoint is crossed, the mediaMatches
 * values are updated and a 'on:breakpoint-change' event is dispatched.
 */
(() => {
  const { mediaQueries } = theme;
  if (!mediaQueries) return;

  const mqKeys = Object.keys(mediaQueries);
  const mqLists = {};
  theme.mediaMatches = {};

  /**
   * Handles a media query (breakpoint) change.
   */
  const handleMqChange = () => {
    const newMatches = mqKeys.reduce((acc, media) => {
      acc[media] = !!(mqLists[media] && mqLists[media].matches);
      return acc;
    }, {});

    // Update mediaMatches values after breakpoint change.
    Object.keys(newMatches).forEach((key) => {
      theme.mediaMatches[key] = newMatches[key];
    });

    window.dispatchEvent(new CustomEvent('on:breakpoint-change'));
  };

  mqKeys.forEach((mq) => {
    // Create mqList object for each media query.
    mqLists[mq] = window.matchMedia(mediaQueries[mq]);

    // Get initial matches for each query.
    theme.mediaMatches[mq] = mqLists[mq].matches;

    // Add an event listener to each query.
    try {
      mqLists[mq].addEventListener('change', handleMqChange);
    } catch (err1) {
      // Fallback for legacy browsers (Safari < 14).
      mqLists[mq].addListener(handleMqChange);
    }
  });
})();

/**
 * Sets a 'viewport-height' custom property on the root element.
 */
function setViewportHeight() {
  document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
}

/**
 * Sets a 'header-height' custom property on the root element.
 */
function setHeaderHeight() {
  const header = document.getElementById('shopify-section-header');
  if (!header) return;
  let height = header.offsetHeight;

  // Add announcement bar height (if shown).
  const announcement = document.getElementById('shopify-section-announcement');
  if (announcement) height += announcement.offsetHeight;

  document.documentElement.style.setProperty('--header-height', `${height}px`);
}

/**
 * Sets a 'scrollbar-width' custom property on the root element.
 */
function setScrollbarWidth() {
  document.documentElement.style.setProperty(
    '--scrollbar-width',
    `${window.innerWidth - document.documentElement.clientWidth}px`
  );
}

/**
 * Sets the dimension variables.
 */
function setDimensionVariables() {
  setViewportHeight();
  setHeaderHeight();
  setScrollbarWidth();
}

// Set the dimension variables once the DOM is loaded
document.addEventListener('DOMContentLoaded', setDimensionVariables);

// Update the dimension variables if viewport resized.
window.addEventListener('resize', debounce(setDimensionVariables, 400));

// iOS alters screen width without resize event, if unexpectedly wide content is found
setTimeout(setViewportHeight, 3000);

/**
 * Pauses all media (videos/models) within an element.
 * @param {Element} [el=document] - Element to pause media within.
 */
function pauseAllMedia(el = document) {
  el.querySelectorAll('.js-youtube, .js-vimeo, video').forEach((video) => {
    const component = video.closest('video-component');
    if (component && component.dataset.background === 'true') return;

    if (video.matches('.js-youtube')) {
      video.contentWindow.postMessage('{ "event": "command", "func": "pauseVideo", "args": "" }', '*');
    } else if (video.matches('.js-vimeo')) {
      video.contentWindow.postMessage('{ "method": "pause" }', '*');
    } else {
      video.pause();
    }
  });

  el.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

class DeferredMedia extends HTMLElement {
  constructor() {
    super();

    const loadBtn = this.querySelector('.js-load-media');
    if (loadBtn) {
      loadBtn.addEventListener('click', this.loadContent.bind(this));
    } else {
      this.addObserver();
    }
  }

  /**
   * Adds an Intersection Observer to load the content when viewport scroll is near
   */
  addObserver() {
    if ('IntersectionObserver' in window === false) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.loadContent(false, false, 'observer');
          observer.unobserve(this);
        }
      });
    }, { rootMargin: '0px 0px 1000px 0px' });

    observer.observe(this);
  }

  /**
   * Loads the deferred media.
   * @param {boolean} [focus=true] - Focus the deferred media element after loading.
   * @param {boolean} [pause=true] - Whether to pause all media after loading.
   * @param {string} [loadTrigger='click'] - The action that caused the deferred content to load.
   */
  loadContent(focus = true, pause = true, loadTrigger = 'click') {
    if (pause) pauseAllMedia();
    if (this.getAttribute('loaded') !== null) return;

    this.loadTrigger = loadTrigger;
    const content = this.querySelector('template').content.firstElementChild.cloneNode(true);
    this.appendChild(content);
    this.setAttribute('loaded', '');

    const deferredEl = this.querySelector('video, model-viewer, iframe');
    if (deferredEl && focus) deferredEl.focus();
  }
}

customElements.define('deferred-media', DeferredMedia);

class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.disclosure = this.querySelector('details');
    this.toggle = this.querySelector('summary');
    this.panel = this.toggle.nextElementSibling;
    this.init();
  }

  init() {
    // Check if the content element has a CSS transition.
    if (window.getComputedStyle(this.panel).transitionDuration !== '0s') {
      this.toggle.addEventListener('click', this.handleToggle.bind(this));
      this.disclosure.addEventListener('transitionend', this.handleTransitionEnd.bind(this));
    }
  }

  /**
   * Handles 'click' events on the summary element.
   * @param {object} evt - Event object.
   */
  handleToggle(evt) {
    evt.preventDefault();

  //  const allDisclosures = document.querySelectorAll('.disclosure');

    if(evt.target.classList.contains('product-accordian-button')){
      document.querySelectorAll('.product-info-accordion').forEach((el) => {
        if(el.querySelector('details-disclosure').querySelector('details').open){
          el.querySelector('details-disclosure').close();
        }
      });
    }

    if(evt.target.classList.contains('product-accordian-button')){
      if(evt.target.closest('.product-info-accordion').classList.contains('description')){
        const handle = evt.target.closest('.main-product').dataset.title;
        gtag('event', 'Product Page Tracking', {'event_category': 'product page','event_label': 'description details click','value': handle});
      }
      if(evt.target.closest('.product-info-accordion').classList.contains('shipping&returns')){
        const handle = evt.target.closest('.main-product').dataset.title;
        gtag('event', 'Product Page Tracking', {'event_category': 'product page','event_label': 'shipping and returns details click','value': handle});
      }
      if(evt.target.closest('.product-info-accordion').classList.contains('product-size-info-accordian')){
        const handle = evt.target.closest('.main-product').dataset.title;
        gtag('event', 'Product Page Tracking', {'event_category': 'product page','event_label': 'size and fit details click','value': handle});
      }
    }


// Close all other disclosures
  document.querySelectorAll('.accordian-bx .disclosure').forEach(disclosure => {
    if (disclosure !== this.disclosure) {
      disclosure.open = false; // close others
    }
  });    

   // console.log('rse');
    if (!this.disclosure.open) {
      this.open();
        
      
    } else {
      this.close();
    }
  }

  /**
   * Handles 'transitionend' events on the details element.
   * @param {object} evt - Event object.
   */
  handleTransitionEnd(evt) {
    if (evt.target !== this.panel) return;

    if (this.disclosure.classList.contains('is-closing')) {
      this.disclosure.classList.remove('is-closing');
      this.disclosure.open = false;
    }

    this.panel.removeAttribute('style');
  }

  /**
   * Adds inline 'height' style to the content element, to trigger open transition.
   */
  addContentHeight() {
    this.panel.style.height = `${this.panel.scrollHeight}px`;
  }

  /**
   * Opens the details element.
   */
  open() {
    // Set content 'height' to zero before opening the details element.
    this.panel.style.height = '0';

    // Open the details element
    this.disclosure.open = true;

    // Set content 'height' to its scroll height, to enable CSS transition.
    this.addContentHeight();
  }

  /**
   * Closes the details element.
   */
  close() {
    // Set content height to its scroll height, to enable transition to zero.
    this.addContentHeight();

    // Add class to enable styling of content or toggle icon before or during close transition.
    this.disclosure.classList.add('is-closing');

    // Set content height to zero to trigger the transition.
    // Slight delay required to allow scroll height to be applied before changing to '0'.
    setTimeout(() => {
      this.panel.style.height = '0';
    });
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

if (!customElements.get('gift-card-recipient')) {
  class GiftCardRecipient extends HTMLElement {
    connectedCallback() {
      this.recipientEmail = this.querySelector('[name="properties[Recipient email]"]');
      this.recipientEmailLabel = this.querySelector(`label[for="${this.recipientEmail.id}"]`);
      this.recipientName = this.querySelector('[name="properties[Recipient name]"]');
      this.recipientMessage = this.querySelector('[name="properties[Message]"]');
      this.recipientSendOn = this.querySelector('[name="properties[Send on]"]');
      this.recipientOffsetProperty = this.querySelector('[name="properties[__shopify_offset]"]');

      // When JS is enabled, the recipient email field is required.
      // Input labels are changed to reflect this.
      if (this.recipientEmailLabel && this.recipientEmailLabel.dataset.jsLabel) {
        this.recipientEmailLabel.innerText = this.recipientEmailLabel.dataset.jsLabel;
      }

      // Set the timezone offset property input and enable it
      if (this.recipientOffsetProperty) {
        this.recipientOffsetProperty.value = new Date().getTimezoneOffset().toString();
        this.recipientOffsetProperty.removeAttribute('disabled');
      }

      this.recipientCheckbox = this.querySelector('.gift-card-recipient__checkbox');
      this.recipientCheckbox.addEventListener('change', () => this.synchronizeProperties());
      this.synchronizeProperties();
    }

    synchronizeProperties() {
      if (this.recipientCheckbox.checked) {
        this.recipientEmail.setAttribute('required', '');
        this.recipientEmail.removeAttribute('disabled');
        this.recipientName.removeAttribute('disabled');
        this.recipientMessage.removeAttribute('disabled');
        this.recipientSendOn.removeAttribute('disabled');
        if (this.recipientOffsetProperty) {
          this.recipientOffsetProperty.removeAttribute('disabled');
        }
      } else {
        this.recipientEmail.removeAttribute('required');
        this.recipientEmail.setAttribute('disabled', '');
        this.recipientName.setAttribute('disabled', '');
        this.recipientMessage.setAttribute('disabled', '');
        this.recipientSendOn.setAttribute('disabled', '');
        if (this.recipientOffsetProperty) {
          this.recipientOffsetProperty.setAttribute('disabled', '');
        }
      }
    }
  }

  customElements.define('gift-card-recipient', GiftCardRecipient);
}

const trapFocusHandlers = {};

/**
 * Removes focus trap event listeners and optionally focuses an element.
 * @param {Element} [elementToFocus=null] - Element to focus when trap is removed.
 */
function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

/**
 * Traps focus within a container, e.g. modal or side drawer.
 * @param {Element} container - Container element to trap focus within.
 * @param {Element} [elementToFocus=container] - Initial element to focus when trap is applied.
 */
function trapFocus(container, elementToFocus = container) {
  const focusableEls = Array.from(
    container.querySelectorAll('summary, a[href], area[href], button:not([disabled]), input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled]), object, iframe, audio[controls], video[controls], [tabindex]:not([tabindex^="-"])')
  );

  const firstEl = focusableEls[0];
  const lastEl = focusableEls[focusableEls.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (evt) => {
    if (evt.target !== container && evt.target !== lastEl && evt.target !== firstEl) return;
    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = () => {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = (evt) => {
    if (evt.code !== 'Tab') return;

    // If tab pressed on last focusable element, focus the first element.
    if (evt.target === lastEl && !evt.shiftKey) {
      evt.preventDefault();
      firstEl.focus();
    }

    //  If shift + tab pressed on the first focusable element, focus the last element.
    if ((evt.target === container || evt.target === firstEl) && evt.shiftKey) {
      evt.preventDefault();
      lastEl.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  (elementToFocus || container).focus();
}

class Modal extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.handleClick.bind(this));
  }

  /**
   * Handles 'click' events on the modal.
   * @param {object} evt - Event object.
   */
  handleClick(evt) {
    if (evt.target !== this && !evt.target.matches('.js-close-modal')) return;
    this.close();
  }

  /**
   * Opens the modal.
   * @param {Element} opener - Modal opener element.
   */
  open(opener) {
    // Prevent page behind from scrolling when side drawer is open
    this.scrollY = window.scrollY;
    document.body.classList.add('fixed');
    document.body.classList.add('modal-open');
    document.body.style.top = `-${this.scrollY}px`;

    this.setAttribute('open', '');
    this.openedBy = opener;

    trapFocus(this);
    window.pauseAllMedia();

    // Add event handler (so the bound event listener can be removed).
    this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();

    // Add event listener (for while modal is open).
    this.addEventListener('keyup', this.keyupHandler);

    // Wrap tables in a '.scrollable-table' element for a better mobile experience.
    this.querySelectorAll('table').forEach((table) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'scrollable-table';
      table.parentNode.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    });
  }

  /**
   * Closes the modal.
   */
  close() {
    // Restore page position and scroll behaviour.
    document.body.style.top = '';
    document.body.classList.remove('fixed');
    document.body.classList.remove('modal-open');
    window.scrollTo(0, this.scrollY);

    this.removeAttribute('open');

    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
    
    if(this.closest('.modal').classList.contains('modal-cart-product-edit-popup')){
      this.closest('.modal').querySelector('.modal__content .inner').innerHTML = '';
    }

    // Remove event listener added on modal opening.
    this.removeEventListener('keyup', this.keyupHandler);
  }
}

customElements.define('modal-dialog', Modal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');
    if (!button) return;

    button.addEventListener('click', () => {
      const modal = document.getElementById(this.dataset.modal);
      if (modal) modal.open(button);
    });
  }
}

customElements.define('modal-opener', ModalOpener);

class SideDrawer extends HTMLElement {
  constructor() {
    super();
    this.overlay = document.querySelector('.js-overlay');
  }

  /**
   * Handles a 'click' event on the drawer.
   * @param {object} evt - Event object.
   */
  handleClick(evt) {
    if (evt.target.matches('.js-close-drawer') || evt.target === this.overlay) {
      if(typeof gtag != "undefined"){
        gtag('event', 'Navigation Tracking', {'event_category': 'navigation','event_label': 'bag click','value': 'cart close'});
      }
      this.close();
    }
  }

  /**
   * Opens the drawer.
   * @param {Element} [opener] - Element that triggered opening of the drawer.
   * @param {Element} [elementToFocus] - Element to focus after drawer opened.
   * @param {Function} [callback] - Callback function to trigger after the open has completed
   */
  open(opener, elementToFocus, callback) {
    this.dispatchEvent(new CustomEvent(`on:${this.dataset.name}:before-open`, {
      bubbles: true
    }));

    // Prevent page behind from scrolling when side drawer is open.
    this.scrollY = window.scrollY;
    document.body.classList.add('fixed');
    document.body.style.top = `-${this.scrollY}px`;
    document.documentElement.style.height = '100vh';

    this.overlay.classList.add('is-visible');
    this.setAttribute('open', '');
    this.setAttribute('aria-hidden', 'false');
    this.opener = opener;

    /* Ap added for A/B Test for checkout Position 24-01-2025 start */
    // const checkout_Variant = getCookie("checkout_Variant");    
    // const checkoutButton = document.querySelector('.cart-drawer__footer .checkout-buttons');
    // if(checkoutButton != null){
    //   if(checkout_Variant == "checkout_Variant_1" && document.querySelector('.cart-drawer__footer .cart-policies') != null){
    //     document.querySelector('.cart-drawer__footer .cart-policies').insertAdjacentElement("afterend", checkoutButton.cloneNode(true));
    //     checkoutButton.classList.add('hidden');
    //   }
    //   if(checkout_Variant == "checkout_Variant_2" && document.querySelector('.cart-drawer__footer .cart-subtotal') != null){
    //     document.querySelector('.cart-drawer__footer .cart-subtotal').insertAdjacentElement("beforebegin", checkoutButton.cloneNode(true));
    //     checkoutButton.classList.add('hidden');
    //     checkoutButton.style.marginBottom = '10px';
    //   }
    // }
    /* Ap added for A/B Test for checkout Position 24-01-2025 end */

    trapFocus(this, elementToFocus);

    // Create event handler variables (so the bound event listeners can be removed).
    this.clickHandler = this.clickHandler || this.handleClick.bind(this);
    this.keyupHandler = (evt) => {
      if (evt.key !== 'Escape' || evt.target.closest('.cart-drawer-popup')) return;
      this.close();
    };

    // Add event listeners (for while drawer is open).
    this.addEventListener('click', this.clickHandler);
    this.addEventListener('keyup', this.keyupHandler);
    this.overlay.addEventListener('click', this.clickHandler);

    // Handle events after the drawer opens
    const transitionDuration = parseFloat(getComputedStyle(this).getPropertyValue('--longest-transition-in-ms'));
    setTimeout(() => {
      if (callback) callback();
      this.dispatchEvent(new CustomEvent(`on:${this.dataset.name}:after-open`, {
        bubbles: true
      }));
    }, transitionDuration);
  }

  /**
   * Closes the drawer.
   * @param {Function} [callback] - Call back function to trigger after the close has completed
   */
  close(callback) {
    this.dispatchEvent(new CustomEvent(`on:${this.dataset.name}:before-close`, {
      bubbles: true
    }));

    this.removeAttribute('open');
    this.setAttribute('aria-hidden', 'true');
    this.overlay.classList.remove('is-visible');

    removeTrapFocus(this.opener);

    // Restore page position and scroll behaviour.
    document.documentElement.style.height = '';
    document.body.style.top = '';
    document.body.classList.remove('fixed');
    window.scrollTo(0, this.scrollY);

    // Remove event listeners added on drawer opening.
    this.removeEventListener('click', this.clickHandler);
    this.removeEventListener('keyup', this.keyupHandler);
    this.overlay.removeEventListener('click', this.clickHandler);

    // Handle events after the drawer closes
    const transitionDuration = parseFloat(getComputedStyle(this).getPropertyValue('--longest-transition-in-ms'));
    setTimeout(() => {
      if (callback) callback();
      this.dispatchEvent(new CustomEvent(`on:${this.dataset.name}:after-close`, {
        bubbles: true
      }));
    }, transitionDuration);
  }
}

customElements.define('side-drawer', SideDrawer);

class BuyButtons extends HTMLElement {
  constructor() {
    super();
    window.initLazyScript(this, this.initLazySection.bind(this));
  }

  initLazySection() {
    this.dynamicPaymentButtonTemplate = this.querySelector('.dynamic-payment-button-template');

    if (this.dynamicPaymentButtonTemplate) {
      this.variantIdInput = this.querySelector('[name="id"]');

      if (this.variantIdInput.value) {
        this.tryInitDynamicPaymentButton();
      } else {
        this.boundTryInitDynamicPaymentButtonOnChange = this.tryInitDynamicPaymentButton.bind(this);
        this.variantIdInput.addEventListener('change', this.boundTryInitDynamicPaymentButtonOnChange);
      }
    }
  }

  /**
   * Initialise dynamic payment button, if variant exists
   */
  tryInitDynamicPaymentButton() {
    if (this.variantIdInput.value) {
      if (this.boundTryInitDynamicPaymentButtonOnChange) {
        this.variantIdInput.removeEventListener('change', this.boundTryInitDynamicPaymentButtonOnChange);
      }

      this.dynamicPaymentButtonTemplate.insertAdjacentHTML('afterend', this.dynamicPaymentButtonTemplate.innerHTML);
      this.dynamicPaymentButtonTemplate.remove();

      if (Shopify.PaymentButton) {
        Shopify.PaymentButton.init();
      }
    }
  }
}

customElements.define('buy-buttons', BuyButtons);

const CartForm = class extends HTMLElement {
  connectedCallback() {
    this.enableAjaxUpdate = this.dataset.ajaxUpdate;

    if (this.enableAjaxUpdate) {
      this.sectionId = this.dataset.sectionId;
      this.boundRefresh = this.refresh.bind(this);
      document.addEventListener('on:cart:change', this.boundRefresh);
      //console.log('data',this.sectionId.querySelector('#querySelector'));
      // gpp code start
     const sectionEl = document.querySelector('#shopify-section-'+this.sectionId);
 
      if (
        sectionEl &&
        (sectionEl.querySelector('#gpp_product_data') != null) &&
        (sectionEl.querySelector('.green-package-slide-cart') != null)
      ) {
     
      theme.addDelegateEventListener(this, 'click', '.checkout-buttons [href="/checkout"]', async (evt) => {
        evt.preventDefault();
        const status = await this.adjustGPPWidget(evt, true);
         //console.log(status);
        if(status == true || status.items.length > 0){
          location.href = evt.target.href;
        }
      });

      theme.addDelegateEventListener(this, 'click', '#gpp_toggle_button', async (evt) => {
        if(document.querySelector(".green-package-slide-cart") != null){
          document.querySelector(".green-package-slide-cart").classList.toggle('open-gpp-content');
          await this.adjustGPPWidget(evt, true);
        }
      });

      theme.addDelegateEventListener(this, 'click', '.continue-without-gpp-btn', async (evt) => {
        evt.preventDefault();
        const status = await this.adjustGPPWidget(evt, false);
        // console.log(evt, status);
        if(status == true){
          location.href = evt.target.href;
        }
      });
        }
      // gpp code end
      theme.addDelegateEventListener(this, 'click', '.cart-item__remove', (evt) => {
        evt.preventDefault();
        if(typeof gtag != "undefined"){
          if(document.querySelector('body').classList.contains('template-cart')){
            gtag('event', 'Cart Page Tracking', {'event_category': 'Cart Page','event_label': 'remove product','value': 'remove product click'});
          }else{
            gtag('event', 'Slideout Cart Tracking', {'event_category': 'Slideout Cart','event_label': 'remove product','value': 'remove product click'});
          }
        }
        this.adjustItemQuantity(evt.target.closest('.cart-item'), { to: 0 });
        
      });

      theme.addDelegateEventListener(this, 'click', '.quantity-down', (evt) => {
        evt.preventDefault();
        this.adjustItemQuantity(evt.target.closest('.cart-item'), { decrease: true });
      });

      theme.addDelegateEventListener(this, 'click', '.quantity-up', (evt) => {
        evt.preventDefault();
        this.adjustItemQuantity(evt.target.closest('.cart-item'), { increase: true });
      });

      theme.addDelegateEventListener(this, 'change', '.cart-item__quantity-input', (evt) => {
        if(evt.target.value < 1){
          if(document.querySelector('body').classList.contains('template-cart')){
            gtag('event', 'Cart Page Tracking', {'event_category': 'Cart Page','event_label': 'remove product','value': 'remove product click'});
          }else{
            gtag('event', 'Slideout Cart Tracking', {'event_category': 'Slideout Cart','event_label': 'remove product','value': 'remove product click'});
          }
        }
        this.adjustItemQuantity(evt.target.closest('.cart-item'), { currentValue: true });
      });
    }
  }

  disconnectedCallback() {
    if (this.enableAjaxUpdate) {
      document.removeEventListener('on:cart:change', this.boundRefresh);
    }
  }

  refresh() {
    this.classList.add('cart-form--refreshing');
    fetch(`${window.Shopify.routes.root}?section_id=${this.sectionId}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.text();
      })
      .then((response) => {
        this.refreshFromHtml(response);
      });
  }

  refreshFromHtml(html) {
    const frag = document.createDocumentFragment();
    const newContent = document.createElement('div');
    frag.appendChild(newContent);
    newContent.innerHTML = html;

    newContent.querySelectorAll('[data-cc-animate]').forEach((el) => el.removeAttribute('data-cc-animate'));
    theme.mergeNodes(newContent, this);

    this.classList.remove('cart-form--refreshing');
    this.querySelectorAll('.merge-item-refreshing').forEach((el) => el.classList.remove('merge-item-refreshing'));
    
    if(newContent.querySelector('.cart-subtotal') != null){
      const cartTotal = newContent.querySelector('.cart-subtotal').dataset.amount;
      if(Object.keys(apConfig).length > 0){
        const apConfig = {
          priceSelector: '.cart-policies',
          locale: 'en_US',
          currency: 'USD',
          afterpayLogoColor: 'white',
          showUpperLimit: false,
          amount: cartTotal,
          minMaxThreshold: {
            min: 100,
            max: 100000,
          },
        };
        new presentAfterpay(apConfig).init();
      }
    }

    /*if(getCookie("enable_currency_codes") == "true"){
      const cartItems = newContent.querySelectorAll('.cart-item-list__body > div');
      cartItems.forEach((el, i) => {
        const currencyCode = el.querySelector('.cart-item__total');
        if(currencyCode != null){
          if(currencyCode.dataset.currencyCode == 'CAD' || currencyCode.dataset.currencyCode == 'AUD'){
            el.querySelectorAll('.cart-item__total .theme-money').forEach((e, index) =>{
              // e.innerHTML = e.dataset.currencyCode;
              document.querySelectorAll('.cart-item-list__body > div')[i].querySelectorAll('.cart-item__total .theme-money')[index].innerHTML = e.dataset.currencyCode;
            })
          }
        }
      });
    }*/
    if (newContent.querySelector('#gpp_product_data') &&
        newContent.querySelector('.green-package-slide-cart')
      ) {
    this.adjustGPPWidget(null, true);
    }
    this.dispatchEvent(
      new CustomEvent('on:cart:after-merge', { bubbles: true, cancelable: false })
    );

    if (theme.settings.afterAddToCart === 'drawer' && this.closest('.drawer') && !this.closest('.drawer').hasAttribute('open')) {      
      document.dispatchEvent(
        new CustomEvent('theme:open-cart-drawer', { bubbles: true, cancelable: false })
      );
    }
  }

  getCartData(){
    return fetch('/cart.js')
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then((response) => {
        return response;
      });
  }

  async updateGWP(){
    const cartData = await this.getCartData();
    // console.log(cartData);
    
    let isFreeGiftAdded = false;
    let freeItem = '';
    let isGWPExpire = false;
    let cartCount = 0;
    let cartTotal = 0;
    cartData.items.forEach((item) => {
      if(item.handle != 'green-package-protection'){
        cartCount += item.quantity;
        cartTotal += item.final_line_price;
      }
      if(Object.keys(item.properties).length > 0 && item.properties['_cart-item-type'] != undefined){
        isFreeGiftAdded = true;
        if(this.querySelector('.cart-free-item') != null && this.querySelector('.cart-free-item').classList.contains(`product-${item.handle}`))
        freeItem = this.querySelector('.cart-free-item');
      }
    });
    if(isFreeGiftAdded == true){
      const country = this.querySelector('.gwp-product-container').dataset.country;
      const thresholdAmount = this.querySelector('.gwp-product-container').dataset.cartTotalThreshold;
      const thresholdCount = this.querySelector('.gwp-product-container').dataset.cartCountThreshold;
      
      if(country == 'US'){
        if(cartTotal < thresholdAmount){
          isGWPExpire = true;
        }
      }else{
        if(cartCount < thresholdCount){
          isGWPExpire = true;
        }
      }

      if(isGWPExpire == true){
        const removeBtn = freeItem.querySelector('.quantity-down');
        removeBtn.click();
        removeBtn.dispatchEvent(new Event('click'));
      }
    }
  }
 async adjustGPPWidget(evt = null, status){
    const GPPProduct = this.getGPPProductData();
    const {isAdded, packageProductVariant} = await this.isGPPProductAdded(GPPProduct);
    // console.log(isAdded, packageProductVariant);
    // console.log(isAdded, packageProductVariant);
    if(status == true){
      if(isAdded == null){
        return this.addOrUpdateGPPProduct(packageProductVariant);
      }else if(isAdded != null && isAdded.id != packageProductVariant.id){
        await this.removeGPPProduct(isAdded);
        return await this.addOrUpdateGPPProduct(packageProductVariant);
      }
      return true;
    }
    // console.log(isAdded, status);
    if(isAdded != null && status == false){
      const response = await this.removeGPPProduct(isAdded);
      // console.log(response);
      return response;
    }
    return true;
  }

  async removeGPPProduct(item){
    const updateParam = { updates: {} };
    updateParam.updates[item.key] = 0;
    return await fetch(theme.routes.cartUpdate, {
      method: 'POST',
      body: JSON.stringify(updateParam),
      headers: {
        'Content-Type': 'application/json'
      }
    })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }else{
        return true;
      }
      document.dispatchEvent(
        new CustomEvent('on:cart:change', { bubbles: true, cancelable: false })
      );
    })
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.log(error.message);
      this.dispatchEvent(new CustomEvent('on:cart:error', {
        bubbles: true,
        detail: {
          error: error.message
        }
      }));
      // Uncertainty... Reload page.
      window.location.reload();
    });
  }

  async addOrUpdateGPPProduct(packageProductVariant){
    let formData = {
      'items': [{
        'id': packageProductVariant.id,
        'quantity': 1,
        'properties' : {
          '__package_protection': 'Green Package Protection'
        }
      }]
    };
    
    return await fetch(theme.routes.cartAdd, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    })
    .then(response => {
      this.refresh();
      return response.json();
    })
    .catch((error) => {
      console.error('Error:', error);
    });
  }

  async isGPPProductAdded(packageProduct){
    const cartData = await this.getCartData();
    const isAdded = cartData.items.find((item) => {
      return item.handle == packageProduct.product.handle;
    });
    const packageProductVariant = await this.getPakcageProductVariant(cartData, packageProduct.product);
    if(packageProductVariant != false){
      const formatedPrice = packageProduct.formatted[packageProductVariant.id].price;
      if(formatedPrice != null){
        document.querySelector('.gpp-price').innerHTML = formatedPrice;
      }
    }
    return {
      isAdded, packageProductVariant
    };
  }

  getPakcageProductVariant(cart, product){
    let excludeTotal = 0, excludeItem = 0;
    
    cart.items.forEach((item) => {
      if(!item.requires_shipping || item.handle == product){
        excludeTotal = excludeTotal + item.line_price;
        excludeItem = excludeItem + 1;
      }
    });
    if(cart.items.length != excludeItem){
      const finalTotal = cart.total_price - excludeTotal;
      let variant = product.variants[product.variants.length - 1];
      let packagePrice = parseInt(finalTotal * 2.5) / 100;
      if(packagePrice > 0){
        variant = product.variants.find(variant => variant.price > packagePrice);
      }
      return variant;
    }
    return false;
  }

  getGPPProductData(){
    const dataEl = this.querySelector('#gpp_product_data');
    
    return JSON.parse(dataEl.textContent);
  }
  adjustItemQuantity(item, change) {
    const quantityInput = item.querySelector('.cart-item__quantity-input');

    let newQuantity = parseInt(quantityInput.value, 10);
    if (typeof change.to !== 'undefined') {
      newQuantity = change.to;
      quantityInput.value = newQuantity;
    } else if (change.increase) {
      newQuantity += quantityInput.step || 1;
      quantityInput.value = newQuantity;
    } else if (change.decrease) {
      newQuantity -= quantityInput.step || 1;
      quantityInput.value = newQuantity;
    } else if (change.currentValue) ;

    if (quantityInput.max && parseInt(quantityInput.value, 10) > parseInt(quantityInput.max, 10)) {
      newQuantity = quantityInput.max;
      quantityInput.value = newQuantity;
      theme.showQuickPopup(theme.strings.cartItemsQuantityError.replace('[QUANTITY]', quantityInput.max), quantityInput);
    }
    if(document.querySelector('.green-package-slide-cart') != null){
      document.querySelector('.green-package-slide-cart').classList.remove('open-gpp-content');
    }
    clearTimeout(this.adjustItemQuantityTimeout);
    this.adjustItemQuantityTimeout = setTimeout(() => {
      const updateParam = { updates: {} };
      this.querySelectorAll('.cart-item__quantity-input:not([disabled])').forEach((el) => {
        updateParam.updates[el.dataset.key] = el.value;
        if(el.value == 0){
          if(typeof gtag != "undefined"){
            if(document.querySelector('body').classList.contains('template-cart')){
              gtag('event', 'Cart Page Tracking', {'event_category': 'Cart Page','event_label': 'remove product','value': 'remove product click'});
            }else{
              gtag('event', 'Slideout Cart Tracking', {'event_category': 'Slideout Cart','event_label': 'remove product','value': 'remove product click'});
            }
          }  
        }
        if (el.value !== el.dataset.initialValue) {
          el.closest('[data-merge-list-item]').classList.add('merge-item-refreshing');
        }
      });
      fetch(theme.routes.cartUpdate, {
        method: 'POST',
        body: JSON.stringify(updateParam),
        headers: {
          'Content-Type': 'application/json'
        }
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          
          if(this.querySelector('.gwp-product-container') != null){
            this.updateGWP();  
          }
          document.dispatchEvent(
            new CustomEvent('on:cart:change', { bubbles: true, cancelable: false })
          );
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.log(error.message);
          this.dispatchEvent(new CustomEvent('on:cart:error', {
            bubbles: true,
            detail: {
              error: error.message
            }
          }));
          // Uncertainty... Reload page.
          window.location.reload();
        });
    }, newQuantity === 0 ? 10 : 700);
  }
};

window.customElements.define('cart-form', CartForm);

const CCCartCrossSell = class extends HTMLElement {
  init() {
    this.productList = this.querySelector('.product-grid');

    if (this.dataset.from) {
      fetch(this.dataset.from)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.text();
        })
        .then((response) => {
          const frag = document.createDocumentFragment();
          const newContent = document.createElement('div');
          frag.appendChild(newContent);
          newContent.innerHTML = response;

          const pl = newContent.querySelector('.product-grid');
          if (pl) {
            this.productList.innerHTML = pl.innerHTML;
            this.querySelectorAll('.product-block').forEach((el) => el.classList.add('slider__item'));
            this.querySelectorAll('carousel-slider').forEach((el) => el.refresh());
          } else {
            this.classList.add('hidden');
          }
        });
    }
  }
};

window.customElements.define('cc-cart-cross-sell', CCCartCrossSell);

const CCFetchedContent = class extends HTMLElement {
  connectedCallback() {
    fetch(this.dataset.url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.text();
      })
      .then((response) => {
        const frag = document.createDocumentFragment();
        const fetchedContent = document.createElement('div');
        frag.appendChild(fetchedContent);
        fetchedContent.innerHTML = response;

        const replacementContent = fetchedContent.querySelector(`[data-id="${this.dataset.id}"]`);
        if (replacementContent) {
          this.innerHTML = replacementContent.innerHTML;
        }
        setTimeout(function(){
          fetchedContent.querySelectorAll('.complementary-product-detail').forEach((el, i) => {
            const productId = el.querySelector('.complementary-product').dataset.productId;
            const newApConfig = {
              priceSelector: `.complementary-product[data-product-id="${productId}"] .price-container`,
              locale: 'en_US',
              currency: 'USD',
              afterpayLogoColor: 'white',
              showUpperLimit: false,
              amount: el.querySelector('.complementary-product').dataset.price,
              minMaxThreshold: {
                min: 100,
                max: 100000,
              },
            };
            if(Object.keys(apConfig).length > 0){
              new presentAfterpay(newApConfig).init();  
            }
            /* removing $ FP start 04-04-2025 */
           /* if(getCookie("remove_price_icon") == "true"){
              if(i == length){
                document.querySelectorAll('.complementary-product-details .complementary-product-detail').forEach((el) => {
                  if(el.querySelector('.afterpay-text1') != null){
                    let afterPay = el.querySelector('.afterpay-text1').textContent;
                    if(afterPay.indexOf('$')){
                      afterPay = afterPay.replace('$', '')
                      el.querySelector('.afterpay-text1').textContent = afterPay;
                    }
                  }
                });
              }
            }*/
            /* removing $ FP end 04-04-2025 */
          });
        }, 1000);
      });
  }
};

window.customElements.define('cc-fetched-content', CCFetchedContent);

const ComplementarySlider = class extends HTMLElement{
  constructor() {
    super();
    window.initLazyScript(this, this.init.bind(this));
  }

  init(){
    let mainSlider = $(this).find('.complementary-product-main-slider');
    let thumbSlider = $(this).find('.complementary-product-thumb-slider');
    if(mainSlider != undefined && thumbSlider != undefined){
      mainSlider.slick({
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
        fade: true,
        asNavFor: '.complementary-product-thumb-slider'
      });
      thumbSlider.slick({
        slidesToShow: 3,
        slidesToScroll: 1,
        asNavFor: '.complementary-product-main-slider',
        arrows: true,
        dots: false,
        infinite:false,
        focusOnSelect: true,
        responsive: [ 
          {
            breakpoint: 451,
            settings: {
              slidesToShow: 1
            }
          },
          {
            breakpoint: 600,
            settings: {
              slidesToShow: 2
            }
          },
          {
            breakpoint: 767,
            settings: {
              slidesToShow: 3
            }
          },
           {
            breakpoint: 990,
            settings: {
              slidesToShow: 2
            }
          },
          {
            breakpoint: 1280,
            settings: {
              slidesToShow: 3
            }
          },
          {
            breakpoint: 1370,
            settings: {
              slidesToShow: 2
            }
          }
        ]
      });
    }
  }
  
}

window.customElements.define('complementary-slider', ComplementarySlider);


/**
 * Returns a function, that, when invoked, will only be triggered at most once during
 * a given window of time.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Time window (in milliseconds).
 * @returns {Function}
 */
function throttle(fn, wait = 300) {
  let throttleTimeoutId = -1;
  let tick = false;

  return () => {
    clearTimeout(throttleTimeoutId);
    throttleTimeoutId = setTimeout(fn, wait);

    if (!tick) {
      fn.call();
      tick = true;
      setTimeout(() => {
        tick = false;
      }, wait);
    }
  };
}

const FilterContainer = class extends HTMLElement {
  constructor() {
    super();
    this.section = this.closest('.shopify-section');
    this.filters = this.querySelector('.filters');

    /* commented by s.j for function */
    /*const utilityBar = document.querySelector('.utility-bar');
    if (utilityBar) {
      // duplicate utility bar for mobile
      this.utilBarClone = utilityBar.cloneNode(true);
      this.utilBarClone.classList.add('utility-bar--sticky-mobile-copy');
      this.utilBarClone.removeAttribute('data-ajax-container');
      utilityBar.insertAdjacentElement('afterend', this.utilBarClone);
      // ensure ids are unique
      theme.suffixIds(this.utilBarClone, 'dupe');

      this.previousScrollTop = window.scrollY;
      this.throttledCheckStickyScroll = throttle(this.checkStickyScroll.bind(this), 200);
    }*/
    this.cloneUtilityBarForMobile();
    this.previousScrollTop = window.scrollY;
    this.throttledCheckStickyScroll = throttle(this.checkStickyScroll.bind(this), 200);

    if (this.filters) {
      // auto-select availability
      this.allowAutoApplyHideUnavailable = this.filters.dataset.autoApplyHideUnavailable === 'true';
      if (this.allowAutoApplyHideUnavailable) {
        theme.addDelegateEventListener(this, 'change', '.filter-group__checkbox, .cc-price-range__input', (evt, delEl) => {
          if (this.allowAutoApplyHideUnavailable) {
            if ((delEl.type === 'checkbox' && delEl.checked) || (delEl.type === 'text' && delEl.value)) {
              const toEnable = this.filters.querySelector('[name="filter.v.availability"][value="1"]');
              if (toEnable) {
                toEnable.checked = true;
                toEnable.dispatchEvent(
                  new CustomEvent('change', { bubbles: true, cancelable: false })
                );
              }
            }
          }
        });

        theme.addDelegateEventListener(this, 'change', '.filter-group--availability .filter-toggle__input', (evt, delEl) => {
          if (delEl.checked && delEl.value === '1') {
            this.allowAutoApplyHideUnavailable = false;
          }
        });
      }

      // ajax filter
      if (this.dataset.ajaxFiltering === 'true') {
        // ajax load form submission
        const debouncedAjaxLoadForm = debounce(this.ajaxLoadForm.bind(this), 700);
        // theme.addDelegateEventListener(this, 'change', '#CollectionFilterForm', debouncedAjaxLoadForm);
        theme.addDelegateEventListener(this, 'submit', '#CollectionFilterForm', this.ajaxLoadForm.bind(this));
        // theme.addDelegateEventListener(this, 'change', '#CollectionMobileFilterForm', debouncedAjaxLoadForm);
        theme.addDelegateEventListener(this, 'submit', '#CollectionMobileFilterForm, #CollectionMobileFilterFormdupe', this.ajaxLoadForm.bind(this));

        theme.addDelegateEventListener(this, 'click', '#CollectionMobileFilterForm .filter-dropdown__close-button, #CollectionMobileFilterFormdupe .filter-dropdown__close-button', function(evt){
          const closeButton = evt.target.closest('.filter-dropdown').querySelector('.filter-dropdown__button');
          closeButton.dispatchEvent(new Event('click', { bubbles: true }));
        });

        theme.addDelegateEventListener(this, 'change', '#CollectionMobileFilterForm, #CollectionFilterForm, #CollectionMobileFilterFormdupe', function(evt){
          const target = evt.target;
          
          if(target.closest('form').id == 'CollectionMobileFilterForm' || target.closest('form').id == 'CollectionMobileFilterFormdupe'){
            if(target.closest('.filter-dropdown__options') != null){
              const selectedInputs = target.closest('.filter-dropdown__options').querySelectorAll('input:checked');
              if(selectedInputs.length > 0){
                target.closest('.filter-dropdown__options').querySelector('.mobile-filter__button-group .apply-filter-btn').disabled = false;
              }else{
                target.closest('.filter-dropdown__options').querySelector('.mobile-filter__button-group .apply-filter-btn').disabled = true;
              }
            }
          }
          if(target.closest('form').id == 'CollectionFilterForm'){
            if(target.type == 'text'){
              if(target.value.length > 0){
                target.closest('form').querySelector('.apply-filter-btn').disabled = false;
              }else{
                target.closest('form').querySelector('.apply-filter-btn').disabled = true;  
              }
            }else{
              const selectedInputs = target.closest('form').querySelectorAll('input:checked');
              if(selectedInputs.length > 0){
                target.closest('form').querySelector('.apply-filter-btn').disabled = false;
              }else{
                target.closest('form').querySelector('.apply-filter-btn').disabled = true;
              }
            }            
          }
          const filterValue = evt.target.value;
          gtag('event', 'Collection Page Tracking', {'event_category': 'collection page','event_label': 'filter click','value': filterValue.toLowerCase()});
          
        });
      } else {
        theme.addDelegateEventListener(this, 'change', '#CollectionFilterForm', (_evt, delEl) => delEl.submit());
      }

      // init things that may need re-initialising on ajax load
      this.initFiltersEtc();
       this.cloneUtilityBarForMobile();
    this.previousScrollTop = window.scrollY;
    this.throttledCheckStickyScroll = throttle(this.checkStickyScroll.bind(this), 200);
    }

    // ajax sort, filter, and pagination
    if (this.dataset.ajaxFiltering === 'true') {
      // ajax load on link click
      theme.addDelegateEventListener(this.section, 'click', '.link-dropdown__link, .filter-group__applied-item, .filter-group__clear-link, .pagination a', (evt, delEl) => {
        evt.preventDefault();
        const sortFilterVal = evt.target.innerText;
        if(evt.target.classList.contains('link-dropdown__link') && (typeof gtag != "undefined")){
          gtag('event', 'Collection Page Tracking', {'event_category': 'collection page','event_label': 'sort by click','value': sortFilterVal.toLowerCase()});
        }
        this.ajaxLoadUrl(delEl.href);
      });
    }
  }

  connectedCallback() {
    // scroll
    if (this.throttledCheckStickyScroll) {
      window.addEventListener('scroll', this.throttledCheckStickyScroll);
    }

    // back button
    if (this.dataset.ajaxFiltering === 'true') {
      this.boundAjaxPopState = this.ajaxPopState.bind(this);
      window.addEventListener('popstate', this.boundAjaxPopState);
    }

    // layout switch
    if (this.section.querySelector('.layout-switchers')) {
      this.boundSwitchGridLayout = theme.addDelegateEventListener(this.section, 'click', '.layout-switch', this.switchGridLayout.bind(this));
    }

    // show/hide filters
    this.delegatedToggleFiltersCallback = theme.addDelegateEventListener(this.section, 'click', '[data-toggle-filters]', (evt) => {
      evt.preventDefault();
      this.classList.toggle('filter-container--show-filters-desktop');
      this.classList.toggle('filter-container--show-filters-mobile');
      const isNowVisible = this.classList.contains('filter-container--show-filters-desktop');
      this.section.querySelectorAll('.toggle-btn[data-toggle-filters]').forEach((el) => {
        el.classList.toggle('toggle-btn--revealed-desktop', isNowVisible);
       // document.querySelector('body').classList.toggle('filter-opened', isNowVisible); /* AP commented for filter scrolling issue after apply the filter 28-3-2025 */
      });
    });
  }

  disconnectedCallback() {
    if (this.boundCheckStickyScroll) {
      window.removeEventListener('scroll', this.throttledCheckStickyScroll);
    }

    if (this.boundAjaxPopState) {
      window.removeEventListener('popstate', this.boundAjaxPopState);
    }
  }

  initFiltersEtc() {
    this.classList.add('filter-container--mobile-initialised');

    // append query vars onto sort urls (e.g. filters, vendor collection)
    if (window.location.href.indexOf('?') >= 0) {
      document.querySelectorAll('#sort-dropdown-options .link-dropdown__link').forEach((el) => {
        const queryTerms = window.location.href.split('?')[1].split('&');
        let newHref = el.href;
        queryTerms.forEach((term) => {
          if (term.indexOf('sort_by=') === -1) {
            newHref += `&${term}`;            
          }
        });
        el.href = newHref;        
      });
    }
  }
  
  cloneUtilityBarForMobile() {
    const utilityBar = document.querySelector('.utility-bar');
    if (!utilityBar) return;

    this.utilBarClone = utilityBar.cloneNode(true);
    this.utilBarClone.classList.add('utility-bar--sticky-mobile-copy');
    this.utilBarClone.removeAttribute('data-ajax-container');
    utilityBar.insertAdjacentElement('afterend', this.utilBarClone);

    if (typeof theme !== 'undefined' && typeof theme.suffixIds === 'function') {
      theme.suffixIds(this.utilBarClone, 'dupe');
    }
  }
  
  switchGridLayout(evt) {
    evt.preventDefault();
    if (evt.target.classList.contains('layout-switch--one-column')) {
      this.querySelectorAll('.product-grid').forEach((el) => {
        el.classList.remove('product-grid--per-row-mob-2');
        el.classList.add('product-grid--per-row-mob-1');
      });
    } else {
      this.querySelectorAll('.product-grid').forEach((el) => {
        el.classList.remove('product-grid--per-row-mob-1');
        el.classList.add('product-grid--per-row-mob-2');
      });
    }

    // Desktop events
    if (evt.target.classList.contains('layout-switch--three-columns')) {
      this.querySelectorAll('.product-grid').forEach((el) => {
        el.classList.remove('product-grid--per-row-4');
        el.classList.add('product-grid--per-row-3');
      });
    } else {
      this.querySelectorAll('.product-grid').forEach((el) => {
        el.classList.remove('product-grid--per-row-3');
        el.classList.add('product-grid--per-row-4');
      });
    }

    if(document.querySelector('.product-block.qv-open') != null){
      theme.quickbuy.setBlockHeights(document.querySelector('.product-block.qv-open').nextElementSibling);
    }
    
    evt.target.classList.add('layout-switch--active');
    (evt.target.nextElementSibling || evt.target.previousElementSibling).classList.remove('layout-switch--active');
  }

  checkStickyScroll() {
    this.header = document.querySelector('.section-header').querySelector('#pageheader');
    const headerHeight = this.header.clientHeight || 0;
    const topBar = document.querySelector('#announcement-bar-with-slider');
    let top = 0;
     if((topBar && getComputedStyle(topBar).position != '' && getComputedStyle(topBar).position == 'fixed') || (topBar && getComputedStyle(topBar).position != '' && getComputedStyle(topBar).position == 'sticky')){
      top = top + topBar.clientHeight;
    }
    const totalHeight = headerHeight + top;
    const searchHeight = document.querySelector('.main-search')?.clientHeight || 0;
    document.documentElement.style.setProperty('--theme-sticky-header-height', `${totalHeight}px`);

    const utilityBarOffsetY = theme.getOffsetTopFromDoc(this.section.querySelector('.utility-bar')) - searchHeight;

    if (window.innerWidth < 768 && window.scrollY > utilityBarOffsetY) {
      // Delay adding class
      // clearTimeout(this._revealTimeout); // Prevent duplicate timeouts
      // this._revealTimeout = setTimeout(() => {
        document.body.classList.add('utility-bar-sticky-mobile-copy-reveal');
      // }, 0);
    } else {
      // Remove class immediately
      // clearTimeout(this._revealTimeout);
      document.querySelectorAll('#CollectionMobileFilterFormdupe.collection-mobile-filter-form .filter-dropdown .filter-dropdown__button').forEach((btn) => {
        btn.setAttribute('aria-expanded', false)
      });
      document.body.classList.remove('utility-bar-sticky-mobile-copy-reveal');
    }
  }

  ajaxLoadForm(evt) {
    if (evt.type === 'submit') {
      evt.preventDefault();
    }
    
    if(evt.target.closest('form').classList.contains('collection-mobile-filter-form')){
      // this.filters = document.querySelector('#CollectionMobileFilterForm')
      this.filters = document.querySelector(`#${evt.target.closest('form').id}`);
    }else{
      this.filters = document.querySelector('#CollectionFilterForm');
    }
    
    const queryVals = [];
    this.filters.querySelectorAll('input, select').forEach((input) => {
      if (
        ((input.type !== 'checkbox' && input.type !== 'radio') || input.checked) // is an active input value
        && input.value !== '' // has a value
      ) {
        queryVals.push([input.name, encodeURIComponent(input.value)]);
      }
    });
    // new url
    let newUrl = window.location.pathname;
    queryVals.forEach((value) => {
      newUrl += `&${value[0]}=${value[1]}`;
    });
    newUrl = newUrl.replace('&', '?');
    // load
    this.ajaxLoadUrl.call(this, newUrl);
   
    console.log("URL call");
  }

  ajaxPopState() {
    this.ajaxLoadUrl.call(this, document.location.href, true);
  }

  ajaxLoadUrl(url, noPushState) {
    if (!noPushState) {
      // update url history
      let fullUrl = url;
      if (fullUrl.slice(0, 1) === '/') {
        fullUrl = `${window.location.protocol}//${window.location.host}${fullUrl}`;
      }
      window.history.pushState({ path: fullUrl }, '', fullUrl);
    }

    // limit render to section, if possible (added after pushState)
    let fetchUrl = url;
    if (this.dataset.filterSectionId) {
      fetchUrl = `${url}${url.indexOf('?') >= 0 ? '&' : '?'}section_id=${this.dataset.filterSectionId}`;
    }

    // start fetching URL
    const refreshContainerSelector = '[data-ajax-container]';
    const ajaxContainers = this.section.querySelectorAll(refreshContainerSelector);

    // loading state
    ajaxContainers.forEach((el) => el.classList.add('ajax-loading'));

    // cancel current fetch & fetch next
    if (this.ajaxLoadUrlFetchAbortController) {
      this.ajaxLoadUrlFetchAbortController.abort();
    }
    this.ajaxLoadUrlFetchAbortController = new AbortController();

    fetch(fetchUrl, {
      method: 'get',
      signal: this.ajaxLoadUrlFetchAbortController.signal
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.text();
      })
      .then((response) => {
        // save active element
        if (document.activeElement) {
          this.activeElementId = document.activeElement.id;
        }

        // before replace - check if container scrolls, if so find item at scroll top
        const scrollContainer = this.querySelector('.filters');
        let elAboveScrollTopData = null;
        if (scrollContainer && getComputedStyle(scrollContainer).overflow === 'auto') {
          const allFilterChildren = scrollContainer.querySelectorAll('.filters *');
          let elAboveScrollTop = allFilterChildren[0];
          for (let i = 1; i < allFilterChildren.length; i += 1) {
            if (allFilterChildren[i].offsetTop) {
              // if above fold
              if (allFilterChildren[i].offsetTop < scrollContainer.scrollTop) {
                // if below previously assigned element, assign
                if (allFilterChildren[i].offsetTop > elAboveScrollTop.offsetTop) {
                  elAboveScrollTop = allFilterChildren[i];
                }
              } else {
                break;
              }
            }
          }
          if (elAboveScrollTop.offsetTop === 0) {
            elAboveScrollTop = false;
          } else {
            // determine how to identify this element
            elAboveScrollTopData = {
              selector: '',
              textContent: elAboveScrollTop.textContent,
              extraScrollOffset: scrollContainer.scrollTop - elAboveScrollTop.offsetTop
            };
            const attributeNames = elAboveScrollTop.getAttributeNames();
            for (let i = 0; i < attributeNames.length; i += 1) {
              const attrName = attributeNames[i];
              if (attrName === 'class') {
                Array.from(elAboveScrollTop.classList).filter((a) => a !== 'filter-group__item--disabled').forEach((cl) => {
                  elAboveScrollTopData.selector += `.${cl}`;
                });
              } else {
                elAboveScrollTopData.selector += `[${attrName}="${CSS.escape(elAboveScrollTop.getAttribute(attrName))}"]`;
              }
            }
          }
        }

        // replace contents
        const template = document.createElement('template');
        template.innerHTML = response;
        const newAjaxContainers = template.content.querySelectorAll(refreshContainerSelector);
        newAjaxContainers.forEach((el, index) => {
          ajaxContainers[index].innerHTML = el.innerHTML;
        });

        // init js
        this.initFiltersEtc();
         this.cloneUtilityBarForMobile();
    this.previousScrollTop = window.scrollY;
    this.throttledCheckStickyScroll = throttle(this.checkStickyScroll.bind(this), 200);

        // after replace
        if (elAboveScrollTopData) {
          this.querySelectorAll(elAboveScrollTopData.selector).forEach((el) => {
            if (el.textContent === elAboveScrollTopData.textContent) {
              scrollContainer.scrollTop = el.offsetTop + elAboveScrollTopData.extraScrollOffset;
            }
          });
        }

        // update mobile count
        if (this.utilBarClone) {
          const from = document.querySelector('.utility-bar:not(.utility-bar--sticky-mobile-copy) .utility-bar__centre');
          const to = this.utilBarClone.querySelector('.utility-bar__centre');
          if (from && to) {
            to.innerHTML = from.innerHTML;
          }
        }

        // remove loading state
        ajaxContainers.forEach((el) => el.classList.remove('ajax-loading'));

        /*if(getCookie("enable_currency_codes") == "true"){
          document.querySelectorAll('.price:not(.fully-soldout)').forEach((price) => {
            if(price.dataset.currencyCode == 'CAD' || price.dataset.currencyCode == 'AUD'){
              if(price.querySelector('.price__current') != null){
                mainPrice = price.querySelector('.price__current').dataset.currencyCode;
                price.querySelector('.price__current').innerHTML = mainPrice;
              }
              if(price.querySelector('.price__was') != null){
                compareAtPrice = price.querySelector('.price__was').dataset.currencyCode;
                price.querySelector('.price__was').innerHTML = compareAtPrice;
              }
            }
          });
        }*/
        
        // restore active element
        if (this.activeElementId) {
          const el = document.getElementById(this.activeElementId);
          if (el) {
            el.focus();
          }
        }

        // scroll viewport (must be done after any page size changes)
        const scrollToY = theme.getOffsetTopFromDoc(this.section.querySelector('[data-ajax-scroll-to]')) - document.querySelector('.section-header').clientHeight;
        window.scrollTo({
          top: scrollToY,
          behavior: 'smooth'
        });
      });
  }
};

window.customElements.define('filter-container', FilterContainer);

/* eslint-disable max-len */
const GalleryViewer = class extends HTMLElement {
  connectedCallback() {
    if (!this.initialised) {
      this.initialised = true;

      // ui
      this.classList.add('gallery-viewer--pre-reveal');
      this.zoomContainer = this.querySelector('.gallery-viewer__zoom-container');
      this.thumbContainer = this.querySelector('.gallery-viewer__thumbs');
      this.controlsContainer = this.querySelector('.gallery-viewer__controls');
      this.previousBtn = this.querySelector('.gallery-viewer__prev');
      this.nextBtn = this.querySelector('.gallery-viewer__next');

      // consts
      this.wheelZoomMultiplier = -0.001;
      this.pinchZoomMultiplier = 0.003;
      this.touchPanModifier = 1.0;

      // vars
      this.currentZoomImage = null;
      this.currentTransform = {
        panX: 0,
        panY: 0,
        zoom: 1
      };
      this.pinchTracking = {
        isTracking: false,
        lastPinchDistance: 0
      };
      this.touchTracking = {
        isTracking: false,
        lastTouchX: 0,
        lastTouchY: 0
      };

      // events
      theme.addDelegateEventListener(this, 'click', '.gallery-viewer__thumb', this.onThumbClick.bind(this));
      this.addEventListener('touchend', this.stopTrackingTouch.bind(this));
      this.addEventListener('touchmove', this.trackInputMovement.bind(this));
      this.addEventListener('mousemove', this.trackInputMovement.bind(this));
      this.addEventListener('wheel', this.trackWheel.bind(this));
      // prevent pan while swiping thumbnails
      this.thumbContainer.addEventListener('touchmove', (evt) => evt.stopPropagation());
      this.previousBtn.addEventListener('click', this.selectPreviousThumb.bind(this));
      this.nextBtn.addEventListener('click', this.selectNextThumb.bind(this));
      this.zoomContainer.addEventListener('click', this.onZoomContainerClick.bind(this));
    }

    document.documentElement.classList.add('gallery-viewer-open');
    this.addEventListener('keyup', this.handleKeyup.bind(this));
    setTimeout(() => this.classList.remove('gallery-viewer--pre-reveal'), 10);
  }

  // eslint-disable-next-line class-methods-use-this
  disconnectedCallback() {
    document.documentElement.classList.remove('gallery-viewer-open');
  }

  static createEl(type, className, appendTo, innerHTML) {
    const el = document.createElement(type);
    el.className = className;
    if (type === 'a') {
      el.href = '#';
    }
    if (appendTo) {
      appendTo.insertAdjacentElement('beforeend', el);
    }
    if (innerHTML) {
      el.innerHTML = innerHTML;
    }
    return el;
  }

  init(currentFullUrl) {
    this.selectThumb([...this.thumbContainer.children].find((el) => el.dataset.zoomUrl === currentFullUrl) || this.thumbContainer.firstElementChild);
  }

  panZoomImageFromCoordinate(inputX, inputY) {
    // do nothing if the image fits, pan if not
    const doPanX = this.currentZoomImage.clientWidth > this.clientWidth;
    const doPanY = this.currentZoomImage.clientHeight > this.clientHeight;

    if (doPanX || doPanY) {
      const midX = this.clientWidth / 2;
      const midY = this.clientHeight / 2;

      const offsetFromCentreX = inputX - midX;
      const offsetFromCentreY = inputY - midY;

      // the offsetMultipler ensures it can only pan to the edge of the image, no further
      let finalOffsetX = 0;
      let finalOffsetY = 0;

      if (doPanX) {
        const offsetMultiplierX = ((this.currentZoomImage.clientWidth - this.clientWidth) / 2) / midX;
        finalOffsetX = Math.round(-offsetFromCentreX * offsetMultiplierX);
      }
      if (doPanY) {
        const offsetMultiplierY = ((this.currentZoomImage.clientHeight - this.clientHeight) / 2) / midY;
        finalOffsetY = Math.round(-offsetFromCentreY * offsetMultiplierY);
      }

      this.currentTransform.panX = finalOffsetX;
      this.currentTransform.panY = finalOffsetY;
      this.alterCurrentPanBy(0, 0); // sanitise
      this.updateImagePosition();
    }
  }

  alterCurrentPanBy(x, y) {
    this.currentTransform.panX += x;
    // limit offset to keep most of image on screen
    let panXMax = (this.currentZoomImage.naturalWidth * this.currentTransform.zoom - this.clientWidth) / 2.0;
    panXMax = Math.max(panXMax, 0);
    this.currentTransform.panX = Math.min(this.currentTransform.panX, panXMax);
    this.currentTransform.panX = Math.max(this.currentTransform.panX, -panXMax);

    this.currentTransform.panY += y;
    let panYMax = (this.currentZoomImage.naturalHeight * this.currentTransform.zoom - this.clientHeight) / 2.0;
    panYMax = Math.max(panYMax, 0);
    this.currentTransform.panY = Math.min(this.currentTransform.panY, panYMax);
    this.currentTransform.panY = Math.max(this.currentTransform.panY, -panYMax);
    this.updateImagePosition();
  }

  setCurrentTransform(panX, panY, zoom) {
    this.currentTransform.panX = panX;
    this.currentTransform.panY = panY;
    this.currentTransform.zoom = zoom;
    this.alterCurrentTransformZoomBy(0);
  }

  alterCurrentTransformZoomBy(delta) {
    this.currentTransform.zoom += delta;
    // do not zoom out further than fit
    const maxZoomX = this.clientWidth / this.currentZoomImage.naturalWidth;
    const maxZoomY = this.clientHeight / this.currentZoomImage.naturalHeight;
    this.currentTransform.zoom = Math.max(this.currentTransform.zoom, Math.min(maxZoomX, maxZoomY));

    // do not zoom in further than native size
    this.currentTransform.zoom = Math.min(this.currentTransform.zoom, 1.0);

    // reasses pan bounds
    this.alterCurrentPanBy(0, 0);
    this.updateImagePosition();
  }

  updateImagePosition() {
    this.currentZoomImage.style.transform = `translate3d(${this.currentTransform.panX}px, ${this.currentTransform.panY}px, 0) scale(${this.currentTransform.zoom})`;
  }

  selectThumb(thumb) {
    [...thumb.parentElement.children].forEach((el) => {
      if (el === thumb) {
        el.classList.add('gallery-viewer__thumb--active');
      } else {
        el.classList.remove('gallery-viewer__thumb--active');
      }
    });

    // replace zoom image
    this.zoomContainer.classList.add('gallery-viewer__zoom-container--loading');
    this.currentZoomImage = GalleryViewer.createEl('img', 'gallery-viewer__zoom-image');
    this.currentZoomImage.alt = '';
    this.currentZoomImage.style.visibility = 'hidden';
    this.currentZoomImage.onload = () => {
      this.zoomContainer.classList.remove('gallery-viewer__zoom-container--loading');
      this.currentZoomImage.style.visibility = '';
      this.currentZoomImage.style.top = `${this.clientHeight / 2 - this.currentZoomImage.clientHeight / 2}px`;
      this.currentZoomImage.style.left = `${this.clientWidth / 2 - this.currentZoomImage.clientWidth / 2}px`;
      this.setCurrentTransform(0, 0, 0); // centre, zoomed out
    };
    this.currentZoomImage.src = thumb.dataset.zoomUrl;
    this.zoomContainer.replaceChildren(this.currentZoomImage);
  }

  selectPreviousThumb(evt) {
    if (evt) evt.preventDefault();
    if (this.thumbContainer.childElementCount < 2) return;

    let previous = this.thumbContainer.querySelector('.gallery-viewer__thumb--active').previousElementSibling;
    while (!previous || !previous.offsetParent) {
      if (!previous) {
        previous = this.thumbContainer.lastElementChild;
      } else {
        previous = previous.previousElementSibling;
      }
    }
    this.selectThumb(previous);
  }

  selectNextThumb(evt) {
    if (evt) evt.preventDefault();
    if (this.thumbContainer.childElementCount < 2) return;

    let next = this.thumbContainer.querySelector('.gallery-viewer__thumb--active').nextElementSibling;
    while (!next || !next.offsetParent) {
      if (!next) {
        next = this.thumbContainer.firstElementChild;
      } else {
        next = next.nextElementSibling;
      }
    }
    this.selectThumb(next);
  }

  stopTrackingTouch() {
    this.pinchTracking.isTracking = false;
    this.touchTracking.isTracking = false;
  }

  trackInputMovement(evt) {
    evt.preventDefault();
    if (evt.type === 'touchmove' && evt.touches.length > 0) {
      // pan
      const touch1 = evt.touches[0];
      if (!this.touchTracking.isTracking) {
        this.touchTracking.isTracking = true;
        this.touchTracking.lastTouchX = touch1.clientX;
        this.touchTracking.lastTouchY = touch1.clientY;
      } else {
        this.alterCurrentPanBy(
          (touch1.clientX - this.touchTracking.lastTouchX) * this.touchPanModifier,
          (touch1.clientY - this.touchTracking.lastTouchY) * this.touchPanModifier
        );
        this.touchTracking.lastTouchX = touch1.clientX;
        this.touchTracking.lastTouchY = touch1.clientY;
      }

      if (evt.touches.length === 2) {
        // pinch
        const touch2 = evt.touches[1];
        const pinchDistance = Math.sqrt((touch1.clientX - touch2.clientX) ** 2 + (touch1.clientY - touch2.clientY) ** 2);
        if (!this.pinchTracking.isTracking) {
          this.pinchTracking.lastPinchDistance = pinchDistance;
          this.pinchTracking.isTracking = true;
        } else {
          const pinchDelta = pinchDistance - this.pinchTracking.lastPinchDistance;
          this.alterCurrentTransformZoomBy(pinchDelta * this.pinchZoomMultiplier);
          this.pinchTracking.lastPinchDistance = pinchDistance;
        }
      } else {
        this.pinchTracking.isTracking = false;
      }
    } else {
      // mousemove
      this.panZoomImageFromCoordinate(evt.clientX, evt.clientY);
    }
  }

  trackWheel(evt) {
    evt.preventDefault();
    if (evt.deltaY !== 0) {
      this.alterCurrentTransformZoomBy(evt.deltaY * this.wheelZoomMultiplier);
    }
  }

  onThumbClick(evt, thumb) {
    evt.preventDefault();
    this.selectThumb(thumb);
  }

  onZoomContainerClick(evt) {
    evt.preventDefault();

    if (this.currentTransform.zoom === 1.0) {
      this.currentTransform.zoom = 0;
      this.alterCurrentTransformZoomBy(0);
    } else {
      this.currentTransform.zoom = 1;
      this.alterCurrentTransformZoomBy(0);
      this.panZoomImageFromCoordinate(evt.clientX, evt.clientY);
    }
  }

  handleKeyup(evt) {
    switch (evt.key) {
      case 'ArrowLeft':
        evt.preventDefault();
        this.selectPreviousThumb();
        break;
      case 'ArrowRight':
        evt.preventDefault();
        this.selectNextThumb();
        break;
    }
  }
};

window.customElements.define('gallery-viewer', GalleryViewer);

const LinkDropdown = class extends HTMLElement {
  constructor() {
    super();

    this.open = false;
    this.button = this.querySelector('.link-dropdown__button');
    this.button.addEventListener('click', this.toggle.bind(this));
  }

  connectedCallback() {
    if (this.open) {
      this.addDismissListener();
    }
  }

  disconnectedCallback() {
    if (this.open) {
      this.removeDismissListener();
    }
  }

  toggle(evt, isDismiss) {
    if (!isDismiss) {
      evt.preventDefault();
      evt.stopPropagation();
    }

    const doExpand = this.button.getAttribute('aria-expanded') === 'false';
    this.button.setAttribute('aria-expanded', doExpand);
    this.button.style.width = `${this.button.clientWidth}px`;

    let newWidth = null;
    const optsBox = this.button.nextElementSibling;
    const isLeftAligned = this.button.closest('.link-dropdown').classList.contains('link-dropdown--left-aligned');
    if (!isLeftAligned) {
      if (doExpand) {
        newWidth = optsBox.clientWidth;
        // rtl - could be either side
        if (document.querySelector('html[dir=rtl]')) {
          newWidth += parseInt(getComputedStyle(optsBox).left, 10);
        } else {
          newWidth += parseInt(getComputedStyle(optsBox).right, 10);
        }
        newWidth -= parseInt(getComputedStyle(optsBox.querySelector('.link-dropdown__link')).paddingInlineStart, 10);
      } else {
        newWidth = parseInt(getComputedStyle(this.button).paddingInlineEnd, 10) + Math.ceil(this.button.querySelector('.link-dropdown__button-text').getBoundingClientRect().width);
      }
      setTimeout(() => {
        this.button.style.width = `${newWidth}px`;
      }, 10);
    }

    if (doExpand) {
      this.open = true;
      this.addDismissListener();
    } else {
      this.open = false;
      this.removeDismissListener();
    }
  }

  addDismissListener() {
    this.dismissCallback = this.toggle.bind(this, true);
    document.addEventListener('click', this.dismissCallback);
  }

  removeDismissListener() {
    document.removeEventListener('click', this.dismissCallback);
    this.dismissCallback = null;
  }
};

window.customElements.define('link-dropdown', LinkDropdown);

const FilterDropdown = class extends HTMLElement {
  constructor() {
    super();

    this.open = false;
    this.button = this.querySelector('.filter-dropdown__button');
    this.button.addEventListener('click', this.toggle.bind(this));
    this.closeButton = this.querySelector('.filter-dropdown__close-button');
    this.closeButton.addEventListener('click', this.close.bind(this));
  }

  connectedCallback() {
    if (this.open) {
      this.addDismissListener();
    }
  }

  disconnectedCallback() {
    if (this.open) {
      this.removeDismissListener();
    }
  }

  close(evt){
    
  }

  toggle(evt, isDismiss) {
    if (!isDismiss) {
      evt.preventDefault();
      evt.stopPropagation();
    }
    
    if(evt != true && evt.target.closest('.filter-dropdown__button') != null){
      const otherDropdown = document.querySelectorAll('filter-dropdown');
      otherDropdown.forEach((el) => {
        const currentBtn = evt.target.classList.contains('filter-dropdown__button') ? evt.target.getAttribute('aria-describedby') : evt.target.closest('.filter-dropdown__button').getAttribute('aria-describedby');
        const elementBtn = el.querySelector('.filter-dropdown__button').getAttribute('aria-describedby');
        if(currentBtn != elementBtn){
          el.querySelector('.filter-dropdown__button').setAttribute('aria-expanded', false);
        }
      });
    }

    const doExpand = this.button.getAttribute('aria-expanded') === 'false';
    this.button.setAttribute('aria-expanded', doExpand);
    this.button.style.width = `${this.button.clientWidth}px`;

    let newWidth = null;
    const optsBox = this.button.nextElementSibling;
    const isLeftAligned = this.button.closest('.filter-dropdown').classList.contains('filter-dropdown--left-aligned');
    if (!isLeftAligned) {
      if (doExpand) {
        newWidth = optsBox.clientWidth;
        // rtl - could be either side
        if (document.querySelector('html[dir=rtl]')) {
          newWidth += parseInt(getComputedStyle(optsBox).left, 10);
        } else {
          newWidth += parseInt(getComputedStyle(optsBox).right, 10);
        }
        // newWidth -= parseInt(getComputedStyle(optsBox.querySelector('.filter-dropdown__link')).paddingInlineStart, 10);
      } else {
        newWidth = parseInt(getComputedStyle(this.button).paddingInlineEnd, 10) + Math.ceil(this.button.querySelector('.filter-dropdown__button-text').getBoundingClientRect().width);
      }
      setTimeout(() => {
        this.button.style.width = `${newWidth}px`;
      }, 10);
    }

    if (doExpand) {
      this.open = true;
      // this.addDismissListener();
    } else {
      this.open = false;
      // this.removeDismissListener();
    }
  }

  addDismissListener() {
    this.dismissCallback = this.toggle.bind(this, true);
    document.addEventListener('click', this.dismissCallback);
  }

  removeDismissListener() {
    document.removeEventListener('click', this.dismissCallback);
    this.dismissCallback = null;
  }
}

window.customElements.define('filter-dropdown', FilterDropdown);

const MainNavigation = class extends HTMLElement {
  constructor() {
    super();

    this.navHoverDelay = 250;
    this.navLastOpenDropdown = null;
    this.navOpenTimeoutId = -1;

    // hover
    this.querySelectorAll('.navigation__tier-1 > .navigation__item--with-children').forEach((el) => {
      el.addEventListener('mouseenter', this.onNavParentHoverIn.bind(this));
    });
    this.querySelectorAll('.navigation__tier-1 > .navigation__item--with-children').forEach((el) => {
      el.addEventListener('mouseleave', this.onNavParentHoverOut.bind(this));
    });

    // touch
    theme.addDelegateEventListener(this, 'touchstart', '.navigation__tier-1 > .navigation__item--with-children > .navigation__link', (evt, el) => { this.handleTouch(evt, el); }, { passive: true });
    theme.addDelegateEventListener(this, 'touchend', '.navigation__tier-1 > .navigation__item--with-children > .navigation__link', (evt, el) => { this.handleTouch(evt, el); });
    theme.addDelegateEventListener(this, 'click', '.navigation__tier-1 > .navigation__item--with-children > .navigation__link', this.onNavParentHoverIn.bind(this));

    // keypress
    theme.addDelegateEventListener(this, 'keydown', '.navigation__tier-1 > .navigation__item--with-children > .navigation__link', this.onNavKeydown.bind(this));

    // click #-link - open child nav
    this.querySelectorAll('.navigation__link[href="#"][aria-haspopup="true"]').forEach((el) => {
      el.addEventListener('click', (evt) => {
        evt.preventDefault();
        evt.currentTarget.nextElementSibling.dispatchEvent(new Event('click', { bubbles: true }));
      });
    });

    // transparent header hover
    this.addEventListener('mouseenter', this.handleNavHover.bind(this));
    this.addEventListener('mouseleave', this.handleNavHover.bind(this));
  }

  ensureDropdownsInPageBounds() {
    this.querySelectorAll('.navigation__item--with-small-menu > .navigation__child-tier').forEach((el) => {
      const parentBcr = el.parentElement.getBoundingClientRect();
      const childBcr = el.getBoundingClientRect();
      const diff = window.innerWidth - (parentBcr.left + childBcr.width);
      if (diff < 25) {
        el.style.setProperty('--nav-side-offset', `${diff - 25}px`);
      }
    });
  }

  connectedCallback() {
    // shuffle dropdowns off RHS of viewport
    const debouncedEnsureDropdownsInPageBounds = debounce(
      this.ensureDropdownsInPageBounds.bind(this),
      300
    );
    debouncedEnsureDropdownsInPageBounds();
    this.resizeObserver = new ResizeObserver((entries) => {
      for (let i = 0; i < entries.length; i += 1) {
        const entry = entries[i];
        if (entry.contentBoxSize || entry.contentRect) {
          debouncedEnsureDropdownsInPageBounds();
        }
      }
    });
    this.resizeObserver.observe(this);

    // proxy for desktop nav interaction events
    this.proxyTier1Nav = document.getElementById(this.dataset.proxyNav);

    if (this.proxyTier1Nav) {
      // reposition small menu
      this.proxyTier1NavBoundEvents = [];

      const boundOnProxyNavEnterSmallMenu = this.onProxyNavEnterSmallMenu.bind(this);
      this.proxyTier1Nav.querySelectorAll('.navigation__item--with-small-menu').forEach((el) => {
        el.addEventListener('mouseenter', boundOnProxyNavEnterSmallMenu);
        this.proxyTier1NavBoundEvents.push({ element: el, name: 'mouseenter', fn: boundOnProxyNavEnterSmallMenu });
      });

      this.proxyTier1NavBoundEvents.push({
        element: this.proxyTier1Nav,
        name: 'touchstart',
        fn: theme.addDelegateEventListener(this.proxyTier1Nav, 'touchstart', '.navigation__item--with-small-menu', this.onProxyNavEnterSmallMenu.bind(this), { passive: true })
      });

      // transfer all events to main menu
      const onProxyNavEnterLeaveLargeMenu = (evt) => {
        const elIndex = [...evt.currentTarget.parentNode.children].indexOf(evt.currentTarget);
        this.querySelectorAll('.navigation__tier-1 > .navigation__item')[elIndex].dispatchEvent(new Event(evt.type));
      };
      this.proxyTier1Nav.querySelectorAll('.navigation__tier-1 > .navigation__item--with-children').forEach((el) => {
        el.addEventListener('mouseenter', onProxyNavEnterLeaveLargeMenu);
        this.proxyTier1NavBoundEvents.push({ element: el, name: 'mouseenter', fn: onProxyNavEnterLeaveLargeMenu });
        el.addEventListener('mouseleave', onProxyNavEnterLeaveLargeMenu);
        this.proxyTier1NavBoundEvents.push({ element: el, name: 'mouseleave', fn: onProxyNavEnterLeaveLargeMenu });
      });

      const eventNames = ['touchstart', 'touchend', 'click'];
      for (let i = 0; i < eventNames.length; i += 1) {
        const eventName = eventNames[i];
        this.proxyTier1NavBoundEvents.push({
          element: this.proxyTier1Nav,
          name: eventName,
          fn: theme.addDelegateEventListener(this.proxyTier1Nav, eventName, '.navigation__tier-1 > .navigation__item--with-children > .navigation__link', (evt, el) => {
            const elIndex = [...el.parentNode.parentNode.children].indexOf(el.parentNode);
            const proxiedLink = this.querySelectorAll('.navigation__tier-1 > .navigation__item')[elIndex].firstElementChild;
            this.handleTouch(evt, proxiedLink);
          }, { passive: eventName === 'touchstart' })
        });
      }

      this.proxyTier1NavBoundEvents.push({
        element: this.proxyTier1Nav,
        name: 'keydown',
        fn: theme.addDelegateEventListener(this.proxyTier1Nav, 'keydown', '.navigation__tier-1 > .navigation__item--with-children > .navigation__link', (evt, el) => {
          if (evt.key === 'Enter') {
            const elIndex = [...el.parentNode.parentNode.children].indexOf(el.parentNode);
            const proxiedLink = this.querySelectorAll('.navigation__tier-1 > .navigation__item')[elIndex].firstElementChild;
            el.setAttribute('aria-expanded', !proxiedLink.parentElement.classList.contains('navigation__item--show-children'));
            this.onNavKeydown(evt, proxiedLink);
          }
        })
      });

      // transparent header hover
      const boundHandleNavHover = this.handleNavHover.bind(this);
      this.proxyTier1Nav.addEventListener('mouseenter', boundHandleNavHover);
      this.proxyTier1Nav.addEventListener('mouseleave', boundHandleNavHover);
      this.proxyTier1NavBoundEvents.push({ element: this.proxyTier1Nav, name: 'mouseenter', fn: boundHandleNavHover });
      this.proxyTier1NavBoundEvents.push({ element: this.proxyTier1Nav, name: 'mouseleave', fn: boundHandleNavHover });
    }

    // create mobile nav
    const tDiv = document.createElement('div');
    tDiv.innerHTML = this.querySelector('.mobile-navigation-drawer-template').innerHTML;
    this.mobileDrawer = tDiv.firstElementChild;

    // add announcement bar items
    const mobileDrawerFooter = this.mobileDrawer.querySelector('.mobile-navigation-drawer__footer');
    const annBarMenu = document.querySelector('.announcement-bar .inline-menu');
    if (annBarMenu) {
      const clone = annBarMenu.cloneNode(true);
      clone.classList.remove('desktop-only');
      mobileDrawerFooter.appendChild(clone);
    }

    const annBarLocalizations = document.querySelector('.announcement-bar .header-localization');
    if (annBarLocalizations) {
      const clone = annBarLocalizations.cloneNode(true);
      clone.classList.remove('desktop-only');
      mobileDrawerFooter.appendChild(clone);

      clone.querySelector('form').addEventListener('change', (evt) => {
        const input = evt.target.previousElementSibling;
        if (input && input.tagName === 'INPUT') {
          input.value = evt.detail.selectedValue;
          evt.currentTarget.submit();
        }
      });
    }

    const annBarSocial = document.querySelector('.announcement-bar .social');
    if (annBarSocial) {
      const clone = annBarSocial.cloneNode(true);
      clone.classList.remove('desktop-only');
      mobileDrawerFooter.appendChild(clone);
    }

    // ensure ids are unique
    theme.suffixIds(this.mobileDrawer, 'MobileNav');

    // insert into page
    document.querySelector('.section-header').insertAdjacentElement('afterend', this.mobileDrawer);

    // event: open second tier
    theme.addDelegateEventListener(this.mobileDrawer, 'click', '.navigation__tier-1 > .navigation__item > .navigation__children-toggle', (evt, delEl) => {
      evt.preventDefault();

      // set text in header
      delEl.parentElement.classList.add('navigation__item--open');
      this.mobileDrawer.classList.add('mobile-navigation-drawer--child-open');
      this.mobileDrawer.querySelector('.mobile-nav-title').innerText = delEl.previousElementSibling.innerText;

      // position under header
      delEl.nextElementSibling.style.top = `${Math.ceil(this.mobileDrawer.querySelector('.navigation__mobile-header').clientHeight + 1)}px`;

      // scroll container
      this.mobileDrawer.closest('.mobile-navigation-drawer').scrollTo({ top: 0, left: 0, behavior: 'instant' }); // 'smooth' not working in iOS 15
    });

    if (this.mobileDrawer.dataset.mobileExpandWithEntireLink === 'true') {
      theme.addDelegateEventListener(this.mobileDrawer, 'click', '.navigation__item--with-children > .navigation__link', (evt, delEl) => {
        evt.preventDefault();
        delEl.nextElementSibling.dispatchEvent(new Event('click', { bubbles: true, cancelable: true }));
      });
    } else {
      theme.addDelegateEventListener(this.mobileDrawer, 'click', '.navigation__item--with-children > .navigation__link[href="#"]', (evt, delEl) => {
        evt.preventDefault();
        delEl.nextElementSibling.dispatchEvent(new Event('click', { bubbles: true, cancelable: true }));
      });
    }

    // event: close second tier
    theme.addDelegateEventListener(this.mobileDrawer, 'click', '.mobile-nav-back', (evt) => {
      evt.preventDefault();
      this.mobileDrawer.classList.remove('mobile-navigation-drawer--child-open');
      this.mobileDrawer.querySelectorAll('.navigation__tier-1 > .navigation__item--open').forEach((el) => {
        el.classList.remove('navigation__item--open');
      });
    });

    // event: toggle third tier
    theme.addDelegateEventListener(this.mobileDrawer, 'click', '.navigation__tier-2 > .navigation__item > .navigation__children-toggle', (evt, delEl) => {
      evt.preventDefault();
      const doOpen = !delEl.parentElement.classList.contains('navigation__item--open');

      if (doOpen) {
        delEl.parentElement.classList.add('navigation__item--open');
        delEl.nextElementSibling.style.height = `${delEl.nextElementSibling.firstElementChild.clientHeight}px`;
      } else {
        delEl.parentElement.classList.remove('navigation__item--open');
        delEl.nextElementSibling.style.height = '';
      }
    });
  }

  handleNavHover(evt) {
    this.closest('.section-header').classList.toggle('section-header--nav-hover', evt.type === 'mouseenter');
  }

  onNavParentHoverIn(evt) {
    const dropdownContainer = evt.currentTarget;
    clearTimeout(this.navOpenTimeoutId);
    clearTimeout(dropdownContainer.dataset.navCloseTimeoutId);
    const openSiblings = [...dropdownContainer.parentNode.children].filter((child) => child !== dropdownContainer && child.classList.contains('navigation__item--show-children'));

    // close all menus but last opened
    openSiblings.filter((el) => el !== this.navLastOpenDropdown).forEach((el) => el.classList.remove('navigation__item--show-children'));
    this.navLastOpenDropdown = dropdownContainer;

    // show after a delay, based on first-open or not
    const timeoutDelay = openSiblings.length === 0 ? 0 : this.navHoverDelay;

    // open it
    const newNavOpenTimeoutId = setTimeout(() => {
      [...dropdownContainer.parentNode.children].forEach((el) => {
        if (el === dropdownContainer) {
          el.classList.add('navigation__item--show-children');
        } else {
          el.classList.remove('navigation__item--show-children');
        }
      });
      dropdownContainer.closest('.section-header').classList.add('section-header--nav-open');
    }, timeoutDelay);

    this.navOpenTimeoutId = newNavOpenTimeoutId;
    dropdownContainer.dataset.navOpenTimeoutId = newNavOpenTimeoutId;

    dropdownContainer.firstElementChild.setAttribute('aria-expanded', true);
  }

  onNavParentHoverOut(evt) {
    // cancel opening, close after delay, and clear transforms
    const dropdownContainer = evt.currentTarget;
    clearTimeout(dropdownContainer.dataset.navOpenTimeoutId);
    dropdownContainer.dataset.navCloseTimeoutId = setTimeout(() => {
      dropdownContainer.classList.remove('navigation__item--show-children');
      dropdownContainer.closest('.section-header').classList.remove('section-header--nav-open');
    }, this.navHoverDelay);

    dropdownContainer.firstElementChild.setAttribute('aria-expanded', false);
  }

  handleTouch(evt, link) {
    if (window.innerWidth > 767) {
      if (evt.type === 'touchstart') {
        link.dataset.touchstartedAt = evt.timeStamp.toString();
      } else if (evt.type === 'touchend') {
        // down & up in under a second - presume tap
        if (evt.timeStamp - parseInt(link.dataset.touchstartedAt, 10) < 1000) {
          link.dataset.touchOpenTriggeredAt = evt.timeStamp.toString();
          if (link.parentElement.classList.contains('navigation__item--show-children')) {
            // trigger close
            link.parentElement.dispatchEvent(new Event('mouseleave'));
          } else {
            // trigger close on any open items
            this.querySelectorAll('.navigation__item--show-children').forEach((el) => el.dispatchEvent(new Event('mouseleave')));
            // trigger open
            link.parentElement.dispatchEvent(new Event('mouseenter'));
          }
          // prevent fake click
          evt.preventDefault();
          evt.stopPropagation();
        }
      } else if (evt.type === 'click') {
        // if touch open was triggered very recently, prevent click event
        if (link.dataset.touchOpenTriggeredAt
            && evt.timeStamp - parseInt(link.dataset.touchOpenTriggeredAt, 10) < 1000) {
          evt.preventDefault();
          evt.stopPropagation();
        }
      }
    }
  }

  // eslint-disable-next-line class-methods-use-this
  onNavKeydown(evt, el) {
    if (evt.key === 'Enter') {
      if (el.parentElement.classList.contains('navigation__item--show-children')) {
        el.parentElement.dispatchEvent(new Event('mouseleave'));
      } else {
        el.parentElement.dispatchEvent(new Event('mouseenter'));
      }
      evt.preventDefault();
    }
  }

  onProxyNavEnterSmallMenu(evt, delEl) {
    const el = delEl || evt.currentTarget;
    const elIndex = [...el.parentNode.children].indexOf(el);
    const dropdown = this.querySelectorAll('.navigation__tier-1 > .navigation__item')[elIndex].querySelector('.navigation__tier-2-container');
    if (document.querySelector('html[dir="rtl"]')) {
      dropdown.style.right = `${dropdown.offsetParent.clientWidth - (theme.getOffsetLeftFromDoc(el) + el.clientWidth)}px`;
    } else {
      dropdown.style.left = `${theme.getOffsetLeftFromDoc(el)}px`;
    }
  }

  disconnectedCallback() {
    // remove externalities
    this.mobileDrawer.remove();

    if (this.proxyTier1NavBoundEvents) {
      for (let i = 0; i < this.proxyTier1NavBoundEvents.length; i += 1) {
        this.proxyTier1NavBoundEvents[i].element.removeEventListener(
          this.proxyTier1NavBoundEvents[i].name,
          this.proxyTier1NavBoundEvents[i].fn
        );
      }
    }
  }
};

window.customElements.define('main-navigation', MainNavigation);

const ProductBlock = class extends HTMLElement {
  constructor() {
    super();
    window.initLazyScript(this, this.init.bind(this));
  }

  init() {
    this.images = Array.from(this.querySelectorAll('.product-block__image'));
    if (this.images.length > 1) {
      this.initImagePagination();
      this.monitorSwatchSelection();
      this.initCustomLazyLoading();
    }
  }

  initCustomLazyLoading() {
    // preload hover images after a delay
    setTimeout(() => {
      this.querySelector('.product-block__image--show-on-hover').classList.remove('product-block__image--inactivated');
    }, 1000);
    // activate all images after first interaction
    this.boundLazyLoadAll = this.lazyLoadAll.bind(this);
    this.addEventListener('mouseenter', this.boundLazyLoadAll);
    this.addEventListener('touchstart', this.boundLazyLoadAll, { passive: true });
  }

  lazyLoadAll() {
    this.querySelectorAll('.product-block__image--inactivated').forEach((el) => el.classList.remove('product-block__image--inactivated'));
    this.removeEventListener('mouseenter', this.boundLazyLoadAll);
    this.removeEventListener('touchstart', this.boundLazyLoadAll);
  }

  // get image at index, safely wrapping index
  getImageAt(index) {
    // positive modulo, like it should be, JS does not do *maths* correctly
    return this.images[((index % this.images.length) + this.images.length) % this.images.length];
  }

  incrementActiveImage(increment) {
    let index = 0;
    for (let i = 0; i < this.images.length; i += 1) {
      if (this.images[i].classList.contains('product-block__image--active')) {
        index = i;
        break;
      }
    }
    this.setActiveImage(index + increment);
  }

  setActiveImage(index) {
    index = parseInt(index);
    const newActiveImage = this.getImageAt(index);
    const hoverImage = this.getImageAt(index + 1);

    // set new active image visibility
    [...newActiveImage.parentElement.children].forEach((el) => {
      el.classList.toggle('product-block__image--active', el === newActiveImage);
      el.classList.toggle('product-block__image--show-on-hover', el === hoverImage);
    });

    // dots
    this.querySelectorAll('.product-block__image-dot').forEach((el, iter) => {
      el.classList.toggle('product-block__image-dot--active', iter === index);
    });
  }

  initImagePagination() {
    // next button
    this.querySelector('.image-page-button--next').addEventListener('click', (evt) => {
      evt.preventDefault();
      if(typeof gtag != "undefined" && document.querySelector('body').classList.contains('template-collection')){
        gtag('event', 'Collection Page Tracking', {'event_category': 'collection page','event_label': 'image carousel click','value': 'image carousel click'});
      }
      this.incrementActiveImage(1);
    });

    // previous button
    this.querySelector('.image-page-button--previous').addEventListener('click', (evt) => {
      evt.preventDefault();
      if(typeof gtag != "undefined" && document.querySelector('body').classList.contains('template-collection')){
        gtag('event', 'Collection Page Tracking', {'event_category': 'collection page','event_label': 'image carousel click','value': 'image carousel click'});
      }
      this.incrementActiveImage(-1);
    });

    // swipe (when not in a carousel)
    if (!this.closest('.carousel, .product-grid--scrollarea')) {
      const touchContainer = this.querySelector('.image-cont--with-secondary-image');
      touchContainer.addEventListener('touchstart', (evt) => {
        // swipe may end in another block
        theme.productBlockTouchTracking = true;
        theme.productBlockTouchStartX = evt.touches[0].clientX;
        theme.productBlockTouchStartY = evt.touches[0].clientY;
      }, { passive: true });

      touchContainer.addEventListener('touchmove', (evt) => {
        if (theme.productBlockTouchTracking) {
          if (Math.abs(evt.touches[0].clientY - theme.productBlockTouchStartY) < 30) {
            const deltaX = evt.touches[0].clientX - theme.productBlockTouchStartX;
            if (deltaX > 25) {
              this.incrementActiveImage(-1);
              theme.productBlockTouchTracking = false;
            } else if (deltaX < -25) {
              this.incrementActiveImage(1);
              theme.productBlockTouchTracking = false;
            }
          }
        }
      }, { passive: true });

      touchContainer.addEventListener('touchend', () => {
        theme.productBlockTouchTracking = false;
      });
    }
  }

  onSelectSwatch(evt) {
    evt.preventDefault();
    let index = -1;

    for (let i = 0; i < this.images.length; i += 1) {
      if (this.images[i].dataset.mediaId === evt.currentTarget.dataset.media) {
        index = i;
        break;
      }
    }

    if (index < 0) return;

    this.setActiveImage(index);

    // include in URL
    const { optionName } = evt.currentTarget.closest('.product-block-options').dataset;
    const optionValue = evt.currentTarget.dataset.optionItem;
    this.querySelectorAll('.product-link, .quickbuy-toggle').forEach((el) => {
      const url = new URL(el.href);
      const params = new URLSearchParams(url.search);
      params.set(optionName, optionValue);
      el.href = `${url.pathname}?${params}`;
      el.rel = 'nofollow';
    });
  }

  monitorSwatchSelection() {
    this.querySelectorAll('[data-media].product-block-options__item').forEach((el) => {
      el.addEventListener('mouseenter', this.onSelectSwatch.bind(this));
      el.addEventListener('click', this.onSelectSwatch.bind(this));
    });
  }
};

window.customElements.define('product-block', ProductBlock);

const MediaGallery = class extends HTMLElement {
  constructor() {
    super();
    this.section = this.closest('.js-product');
    this.mainImageContainer = this.querySelector('.main-image');
    this.slideshow = this.mainImageContainer.querySelector('.main-image carousel-slider');
    this.collage = this.querySelector('.product-media-collage');
    this.xrButton = this.querySelector('[data-shopify-xr]');
    this.variantPicker = this.section.querySelector('variant-picker');
    this.mediaGroupingEnabled = this.variantPicker && this.hasAttribute('data-media-grouping-enabled') && this.getMediaGroupData();

    if(this.mediaGroupingEnabled == false && this.querySelector('.product-thumb-slider') != null && this.querySelector('.product-thumb-slider').innerHTML != ''){
      
      $('.product-thumb-slider').slick({
        vertical: true,
        verticalSwiping: true,
        arrows:true,
        infinite: false,
        slidesToShow:6,
        slidesToScroll:2,
        responsive: [
          {
            breakpoint: 1701,
            settings: {
              slidesToShow: 4.4                        
            }
          },
          {
            breakpoint: 1501,
            settings: {
              slidesToShow: 4                        
            }
          },
          {
            breakpoint: 1401,
            settings: {
              slidesToShow: 3.5                        
            }
          },
          {
            breakpoint: 1351,
            settings: {
              slidesToShow: 3.3                        
            }
          },
          {
            breakpoint: 1301,
            settings: {
              slidesToShow: 3                        
            }
          }
        ]
      });
      
    }
    this.mediaLists = [
      this.querySelectorAll('.main-image .slider__item'),
      this.querySelectorAll('.product-media-collage__item'),
      this.querySelectorAll('.thumbnails .slider__item')
    ];

    if (this.variantPicker) {
      const toLoad = [this.variantPicker];
      if (this.slideshow.offsetParent) {
        toLoad.push(this.slideshow);
      }
      const thumbnailSlider = this.querySelector('carousel-slider.thumbnails');
      if (thumbnailSlider && thumbnailSlider.offsetParent) {
        toLoad.push(thumbnailSlider);
      }
      theme.whenComponentLoaded(toLoad, this.setFromVariantPicker.bind(this));
    }

    if (this.hasAttribute('data-zoom-enabled')) {
      this.galleryModal = this.querySelector('.js-media-zoom-template').content.firstElementChild.cloneNode(true);
      this.mediaLists.push(this.galleryModal.querySelectorAll('.gallery-viewer__thumb'));
      theme.addDelegateEventListener(this, 'click', '.show-gallery', this.openGalleryViewer.bind(this));

      if (this.hasAttribute('data-zoom-preload')) {
        this.mainImageContainer.addEventListener('mouseover', MediaGallery.hoverMainImage.bind(this));
        this.mainImageContainer.addEventListener('touchstart', MediaGallery.hoverMainImage.bind(this));
      }
    }

    if(!this.closest('.product-info').classList.contains('complementary-product') && !this.closest('.product-info').classList.contains('quickview-product')){
      if(window.innerWidth > 767 && document.querySelector('.main-image .product-media')?.classList.contains('product-media--image')){
        $('.main-image .product-media.product-media--image img').wrap('<span style="display:inline-block"></span>')
        .css('display', 'block')
        .parent()
        .zoom({ on:'click' });
      }
    }

    this.section.addEventListener('on:carousel-slider:select', this.selectMainMedia.bind(this));
    theme.addDelegateEventListener(this, 'click', '.thumbnail', this.selectThumbnail.bind(this));
    this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));

    // preload slideshow images after a short delay, if slideshow is visible
    setTimeout(() => {
      if (this.slideshow.offsetParent) {
        this.slideshow.querySelectorAll('.theme-img').forEach((el) => { el.loading = 'eager'; });
      }
    }, 3000);
  }

  setFromVariantPicker() {
    if (this.mediaGroupingEnabled) {
      this.setMediaGroupOnFirstLoad();
    } else {
      // Find which variant is selected and set active media.
      const variant = this.variantPicker.getSelectedVariant();
      if (variant && variant.featured_media) {
        this.setActiveMedia(variant.featured_media.id, true, true);
      }
    }
  }

  /**
   * Run on first load to set initial media group and focussed image
   */
  setMediaGroupOnFirstLoad() {
    this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
    
    // Find which variant is selected and set active media.
    const variant = this.variantPicker.getSelectedVariant();
    if (variant && variant.featured_media) {
      this.setActiveMedia(variant.featured_media.id, true, true);
      const slideIndex = this.querySelector(`.main-carousel [data-media-id="${variant.featured_media.id}"]`).dataset.slideIndex;
      this.runThumbSlider(slideIndex);
      if(slideIndex > 0){
        this.goToSlide(variant.featured_media.id);
      }
      return;
    }

    const mainSlider = this.querySelector('.main-carousel').dataset.initialId;
    if(mainSlider != undefined){
      const slideIndex = this.querySelector(`.main-carousel [data-media-id="${mainSlider}"]`).dataset.slideIndex;
      this.setActiveMedia(mainSlider, true, true);
      this.runThumbSlider(slideIndex);
      // console.log(slideIndex);
      // console.log('ddd');
      if(slideIndex > 0){
        this.goToSlide(mainSlider);
      }
    }else{
      // Default to first visible image.
      this.setActiveMedia(this.querySelector('.thumbnails .slider__item:not([hidden])').dataset.mediaId, false, true);
      // console.log('dddsz');
      if (!variant) {
        this.runThumbSlider();
      }
    }
  }

  runThumbSlider(initialSlide = 0){
     //console.log('hd');
     $('.product-thumb-slider').not('.slick-initialized')
    .on('init', function () {
      //console.log('awef');
      document.querySelectorAll('.product-thumb-slider .thumbnail img.theme-img').forEach(function (img) {
        const src = img.getAttribute('src');
        if (isGifFile(src)) { 
          const [baseUrl, queryString] = src.split('?');
          const jpgBaseUrl = baseUrl.replace(/\.gif$/i, '.jpg');
          const placeholderSrc = queryString ? `${jpgBaseUrl}?${queryString}` : jpgBaseUrl;

          img.setAttribute('data-gif-src', src); // keep original for potential use
          img.setAttribute('src', placeholderSrc);

          // If there's a srcset, also adjust it
          if (img.hasAttribute('srcset')) {
            const originalSrcset = img.getAttribute('srcset');
            const jpgSrcset = originalSrcset
              .split(',')
              .map(item => {
                const [urlPart, size] = item.trim().split(' ');
                const [urlBase, urlQuery] = urlPart.split('?');
                const jpgUrlBase = urlBase.replace(/\.gif$/i, '.jpg');
                const newUrl = urlQuery ? `${jpgUrlBase}?${urlQuery}` : jpgUrlBase;
                return `${newUrl} ${size}`;
              })
              .join(', ');

            img.setAttribute('data-gif-srcset', originalSrcset);
            img.setAttribute('srcset', jpgSrcset);
          }
        }
      }); 
    })
  .slick({
      vertical: true,
      verticalSwiping: true,
      arrows:true,
      infinite: false,
      // initialSlide: initialSlide,
      slidesToShow:6,
      slidesToScroll:2,
      responsive: [
        {
          breakpoint: 1701,
          settings: {
            slidesToShow: 4.4                        
          }
        },
        {
          breakpoint: 1501,
          settings: {
            slidesToShow: 4                        
          }
        },
        {
          breakpoint: 1401,
          settings: {
            slidesToShow: 3.5                        
          }
        },
        {
          breakpoint: 1351,
          settings: {
            slidesToShow: 3.3                        
          }
        },
        {
          breakpoint: 1301,
          settings: {
            slidesToShow: 3                        
          }
        }
      ]
    });
  }

  goToSlide(mediaId){
    const index = $('.product-thumb-slider [data-media-id="'+mediaId+'"]').data('slick-index');
    $('.product-thumb-slider').slick('slickGoTo', index);
  }

  destroySlickSlider(){
    if($('.product-thumb-slider').hasClass('slick-initialized')){
      $('.product-thumb-slider').slick('unslick');
      $('.product-thumb-slider').html('');
    }
  }

  /**
   * Hover main image.
   * @param {Event} evt - hover event for image
   */
  static hoverMainImage(evt) {
    MediaGallery.loadImage(evt.currentTarget.querySelector('.slider__item.is-active a.show-gallery'));
  }

  /**
   * Open large image viewer.
   * @param {Event} evt - click event for image
   * @param {Element} imageLink - link clicked on
   */
  openGalleryViewer(evt, imageLink) {
    evt.preventDefault();

    // add to document on first open
    if (!this.galleryModal.parentElement) {
      document.body.appendChild(this.galleryModal);
    }

    this.galleryModal.open(imageLink);
    const viewer = this.galleryModal.querySelector('gallery-viewer');
    viewer.init(imageLink.getAttribute('href'));
    viewer.focus();

    if (this.hasAttribute('data-zoom-preload')) {
      this.mediaLists[0].forEach((sliderItem) => {
        MediaGallery.loadImage(sliderItem.querySelector('a.show-gallery'));
      });
    }
  }

  /**
   * Load an image.
   * @param {Element} anchor - anchor element
   */
  static loadImage(anchor) {
    if (!anchor || anchor.hasAttribute('data-loading') || anchor.hasAttribute('data-loaded')) return;

    anchor.setAttribute('data-loading', '');

    const image = new Image(5000);
    image.addEventListener('load', () => {
      anchor.removeAttribute('data-loading');
      anchor.setAttribute('data-loaded', '');
    }, {
      once: true
    });
    image.src = anchor.href;
  }

  /**
   * Handle a change in variant on the page.
   * @param {Event} evt - variant change event dispatched by variant-picker
   */
  onVariantChange(evt) {
    if(evt.detail.form.classList.contains('complementary-product-form')){
      const mediaId = evt.detail.variant.featured_media.id;
      const productId = evt.target.closest('.js-product').dataset.productId;
      if(document.querySelector('.complementary-product-images .product-block[data-product-id="'+productId+'"]') != null){
        const productBlock = document.querySelector('.complementary-product-images .product-block[data-product-id="'+productId+'"]');
        const mediaIndex = productBlock.querySelector('.product-block__image[data-media-id="'+mediaId+'"]').dataset.imageIndex;
        productBlock.setActiveImage(mediaIndex);
      }
      return;
    }
    
    if (this.mediaGroupingEnabled) {
      this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors(evt));
      this.runThumbSlider();
    }

    if (evt.detail.variant && evt.detail.variant.featured_media) {
      this.setActiveMedia(evt.detail.variant.featured_media.id, true);
      this.goToSlide(evt.detail.variant.featured_media.id);
    } else if (this.mediaGroupChanged) {
      // default to first visible image, if group changed and current variant has no related image
      this.setActiveMedia(this.querySelector('.thumbnails .slider__item:not([hidden])').dataset.mediaId, true);
    }
  }

  /**
   * Gets the media group from currently selected variant options.
   * @param {Event} evt - variant change event dispatched by variant-picker
   * @returns {?object}
   */
  getMediaGroupFromOptionSelectors(evt) {
    if (evt) {
      return evt.detail.selectedOptions[this.getMediaGroupData().groupOptionIndex];
    }
    
    return this.variantPicker.getSelectedOptions()[this.getMediaGroupData().groupOptionIndex];
  }

  /**
   * Gets the variant media associations for a product.
   * @returns {?object}
   */
  getMediaGroupData() {
    if (typeof this.variantMediaData === 'undefined') {
      const dataEl = this.querySelector('.js-data-variant-media');
      if (dataEl) {
        this.variantMediaData = JSON.parse(dataEl.textContent);
      } else {
        this.variantMediaData = false;
      }
    }

    return this.variantMediaData;
  }

  /**
   * Show only images associated to the current variant.
   * @param {string} groupName - optional - Group to show (uses this.currentItem if empty)
   */
  setActiveMediaGroup(groupName) {
    this.mediaGroupChanged = this.currentMediaGroup !== groupName;
    this.currentMediaGroup = groupName;
    this.currentSelectedGroup = null;
    if(document.querySelector('.main-option-container [data-option="Color"]') != null){
      this.currentSelectedGroup = document.querySelector('.main-option-container [data-option="Color"] input:checked').value;
    }

    this.dekstopThumbnails = document.querySelector('.product-thumb-slider');
    
    if(this.mediaGroupChanged) {
      this.destroySlickSlider();
    }
    
    if (!this.mediaGroupChanged) return;

    if (!groupName) {
      this.mediaLists.forEach((list) => {
        list.forEach((mediaItem) => { mediaItem.hidden = false; });
      });
      return;
    }

    const mediaGroupData = this.getMediaGroupData();
    
    this.mediaLists.forEach((list) => {
      list.forEach((el) => {
        const mediaItemGroup = mediaGroupData.media[el.dataset.mediaId].group;
        const showThis = mediaItemGroup === groupName || mediaItemGroup === true;
        el.hidden = !showThis;

        if(el.closest('carousel-slider') != null && el.closest('carousel-slider').classList.contains('thumbnails') && showThis && !$('.product-thumb-slider').hasClass('slick-initialized')){
          const cloneNode = el.cloneNode(true);
          if(document.querySelector('.media-gallery').classList.contains('mens-only-img')){
            if(cloneNode.querySelector('img').alt.indexOf('-Men') != -1 || cloneNode.querySelector('img').alt.indexOf('-Man') != -1){
              $('.product-thumb-slider').append(cloneNode);  
            }
          }else{
            $('.product-thumb-slider').append(cloneNode);
          }
        }
      });

      // Move all-groups media to start/end
      [...list].forEach((el) => {
        if (mediaGroupData.media[el.dataset.mediaId].group === true) {
          if (mediaGroupData.media[el.dataset.mediaId].position === 'start') {
            el.parentElement.prepend(el);
          } else {
            el.parentElement.append(el);
          }
        }
      });

      // Move hidden elements to the end (to allow nth-child CSS in layouts)
      [...list].filter((el) => el.hidden).forEach((el) => el.parentElement.append(el));

      // Refresh slider
      if (list.length > 0) {
        const slider = list[0].closest('carousel-slider');
        if (slider && slider.offsetParent) {
          slider.refresh();
        }
      }
    });
  }

  /**
   * Sets the active media item.
   * @param {number} mediaId - Media ID to set as active.
   * @param {boolean} [scrollToItem=true] - Scroll to the active media item.
   * @param {boolean} [firstLoad=false] - Is gallery initialising.
   */
  setActiveMedia(mediaId, scrollToItem = true, firstLoad = false) {
    const strMediaId = mediaId.toString();
    const mediaEl = this.mainImageContainer.querySelector(`[data-media-id="${strMediaId}"]`);

    if (scrollToItem) {
      // slideshow (if visible)
      if (this.slideshow.hasAttribute('loaded') && this.slideshow.offsetParent) {
        this.slideshow.scrollToElement(mediaEl, 'instant');
        // console.log($('.product-thumb-slider [data-media-id="'+mediaId+'"]').data('slick-index'));
        if($('.product-thumb-slider [data-media-id="'+mediaId+'"]').data('slick-index') != undefined){
          $('.product-thumb-slider').slick('slickGoTo', $('.product-thumb-slider [data-media-id="'+mediaId+'"]').data('slick-index'));
          $('.product-thumb-slider .slider__item').removeClass('is-active');
          $('.product-thumb-slider [data-media-id="'+mediaId+'"]').addClass('is-active');
        }
      }

      // collage (if visible and not first load)
      if (this.collage && this.collage.offsetParent && !firstLoad) {
        const collageMediaEl = this.collage.querySelector(`[data-media-id="${strMediaId}"]`);
        if (collageMediaEl) {
          theme.scrollToRevealElement(collageMediaEl);

          clearTimeout(this.collage.dataset.highlightTimeoutId);
          // When media group changes, assume first media has attention
          if (this.mediaGroupChanged) {
            this.collage.querySelectorAll('.product-media-collage__item--highlight-off').forEach((el) => {
              el.classList.remove('product-media-collage__item--highlight-off');
            });
          } else {
            this.collage.querySelectorAll('.product-media-collage__item').forEach((el) => {
              if (el === collageMediaEl) {
                el.classList.remove('product-media-collage__item--highlight-off');
              } else {
                el.classList.add('product-media-collage__item--highlight-off');
              }
            });
            this.collage.dataset.highlightTimeoutId = setTimeout(() => {
              this.collage.querySelectorAll('.product-media-collage__item--highlight-off').forEach((el) => {
                el.classList.remove('product-media-collage__item--highlight-off');
              });
            }, 1200);
          }
        }
      }
    }

    this.mediaLists.forEach((list) => {
      list.forEach((mediaItem) => {
        if (mediaItem.dataset.mediaId === strMediaId) {
          mediaItem.classList.add('is-active');
          const carousel = mediaItem.closest('carousel-slider');
          if (carousel && carousel.offsetParent) {
            if(this.slideshow.querySelectorAll('.slider__grid').length > 1 && !mediaItem.classList.contains('is-active')){
              carousel.scrollToElement(mediaItem);
            }
          }
        } else {
          mediaItem.classList.remove('is-active');
        }
      });
    });

    // play media
    window.pauseAllMedia();
    if (mediaEl) {
      // deferred
      const deferredMedia = mediaEl.querySelector('deferred-media, product-model');
      if (deferredMedia) deferredMedia.loadContent(true);

      // video tag
      const playableMedia = mediaEl.querySelector('video');
      if (playableMedia) {
        playableMedia.play();
      }
    }

    // set model ID
    if (this.xrButton && this.mediaIsModel(mediaId)) {
      this.xrButton.dataset.shopifyModel3dId = mediaId;
    }

    document.dispatchEvent(
      new CustomEvent('on:media-gallery:change', { bubbles: true, cancelable: false })
    );
  }

  /**
   * Check if media is a 3D model
   * @param {number} mediaId - Media ID to check.
   * @returns {boolean} - If it is a 3D model.
   */
  mediaIsModel(mediaId) {
    return !!this.querySelector(`.product-media--model[data-model-id="${mediaId}"]`);
  }

  selectThumbnail(evt, thumbnail) {
    evt.preventDefault();
    if(typeof gtag != "undefined"){
      if(thumbnail.classList.contains('thumbnail--media-video')){
        const url = thumbnail.dataset.videoUrl;
        gtag('event', 'Product Page Tracking', {'event_category': 'product page','event_label': 'video carousel play click','value': 'https:'+url});
      }else{
        if(thumbnail.closest('.js-product').dataset.title != ""){
          const handle = thumbnail.closest('.js-product').dataset.title ;
          gtag('event', 'Product Page Tracking', {'event_category': 'product page','event_label': 'image carousel click','value': handle});
        }
      }
    }
    this.setActiveMedia(thumbnail.parentNode.dataset.mediaId, true);
  }

  selectMainMedia(evt) {
    if (evt.detail.slide.dataset.mediaId) {
      this.setActiveMedia(evt.detail.slide.dataset.mediaId, false);
    }
  }
};

window.customElements.define('media-gallery', MediaGallery);

/* eslint-disable max-len */
/* eslint-disable no-multiple-empty-lines */
const ProductForm = class extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('.js-product-form');
    if (this.form) {
      const idInput = this.form.querySelector('[name="id"]');
      idInput.disabled = false;
      idInput.required = true;

      if (theme.settings.afterAddToCart !== 'no-js') {
        this.submitBtn = this.querySelector('[name="add"]');
        this.form.addEventListener('submit', this.handleSubmit.bind(this));
      }
    }
  }

  /**
   * Handles submission of the product form.
   * @param {object} evt - Event object.
   */
  async handleSubmit(evt) {
    evt.preventDefault();
    
    if(evt.target.classList.contains('cart-product-option-edit-form')){
      const form = evt.target;
      const originalId = form.querySelector('.original-id').value;
      const newId = form.querySelector('[name="id"]').value;
      if(originalId != newId){
        const updateParam = { updates: {} };
        updateParam.updates[originalId] = 0;
        await fetch(theme.routes.cartUpdate, {
          method: 'POST',
          body: JSON.stringify(updateParam),
          headers: {
            'Content-Type': 'application/json'
          }
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP error! Status: ${response.status}`);
            }
            if(this.querySelector('.gwp-product-container') != null){
              this.updateGWP();  
            }
            document.dispatchEvent(
              new CustomEvent('on:cart:change', { bubbles: true, cancelable: false })
            );
          })
          .catch((error) => {
            // eslint-disable-next-line no-console
            console.log(error.message);
            this.dispatchEvent(new CustomEvent('on:cart:error', {
              bubbles: true,
              detail: {
                error: error.message
              }
            }));
            // Uncertainty... Reload page.
            window.location.reload();
          });
      }else{
        return;
      }
    }
    
    if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

    this.setErrorMsgState();

    const formValid = this.validate();
    if (!formValid) return;

    // Disable "Add to Cart" button until submission is complete.
    this.submitBtn.setAttribute('aria-disabled', 'true');
    this.submitBtn.classList.add('is-loading');

    if(this.classList.contains('main-product')){
      const productTitle = this.form.querySelector('[name="product_title"]').value;
      if(typeof gtag != "undefined"){
        gtag('event', 'Product Page Tracking', {'event_category': 'product page','event_label': 'add to cart inline','value': productTitle.toLowerCase()});
      }
    }

    if(this.classList.contains('complementary-product-block')){
      if(typeof gtag != "undefined"){
        const productTitle = this.form.querySelector('[name="product_title"]').value;
        gtag('event', 'Product Page Tracking', {'event_category': 'product page','event_label': 'complete the look add to cart','value': productTitle.toLowerCase()});
      }
    }

    if(this.classList.contains('gwp-product-block')){
      if(typeof gtag != "undefined"){
        gtag('event', 'Slideout Cart Tracking', {'event_category': 'Slideout Cart','event_label': 'Gift Product Added','value': 'Add Gift with Purchase'});
      }
    }

    if(this.classList.contains('cart-upsell-product-block')){
      if(typeof gtag != "undefined"){
        const productTitle = this.form.querySelector('[name="product_title"]').value;
        if(this.form.closest('.cart-main-blocks') != null && this.form.closest('.cart-main-blocks').classList.contains('cart-drawer__content')){
           gtag('event', 'Slideout Cart Tracking', {'event_category': 'slideout cart','event_label': 'add product recommendation','value': productTitle.toLowerCase()});
        }else{
           gtag('event', 'Slideout Cart Tracking', {'event_category': 'slideout cart','event_label': 'empty cart add product recommendation','value': productTitle.toLowerCase()});
        }
      }
    }

    if(this.classList.contains('quickview-product')){
      if(typeof gtag != "undefined"){
        if(document.querySelector('body').classList.contains('template-index')){
          gtag('event', 'Homepage Tracking', {'event_category': 'homepage','event_label': 'quickview click','value': 'quickview add to cart'});
        }
        if(document.querySelector('body').classList.contains('template-collection')){
         gtag('event', 'Collection Page Tracking', {'event_category': 'collection page','event_label': 'quickview click','value': 'quickview add to cart'});
        }
        if(document.querySelector('body').classList.contains('template-product')){
         gtag('event', 'Product Page Tracking', {'event_category': 'product page','event_label': 'quickview click','value': 'quickview add to cart'});
        }
      }
    }

    const formData = new FormData(this.form);
    const sectionsToUpdate = ['page-header', 'cart-drawer'].map((sel) => document.querySelector(sel)).filter((el) => el);
    const sectionIds = sectionsToUpdate.map((el) => el.dataset.sectionId);

    formData.append('sections_url', window.location.pathname);
    formData.append('sections', sectionIds.join(','));

    const fetchRequestOpts = {
      method: 'POST',
      headers: {
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    };

    try {
      const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
      const data = await response.json();
      let error = typeof data.description === 'string' ? data.description : data.message;
      if (data.errors && typeof data.errors === 'object') {
        error = Object.entries(data.errors).map((item) => item[1].join(', '));
      }
      if (data.status) this.setErrorMsgState(error);
      if (!response.ok) throw new Error(response.status);

      if(evt.target.classList.contains('cart-product-option-edit-form')){
        const modalDialog = document.querySelector('modal-dialog.modal-cart-product-edit-popup');
        if(modalDialog){
          modalDialog.close();
        }
      }

      if (document.querySelector('.template-cart')) {
        // Cart page
        const cartForm = document.querySelector('cart-form');
        if (cartForm && cartForm.enableAjaxUpdate) {
          cartForm.refresh();
        }
      } else if (theme.settings.afterAddToCart === 'page') {
        // Allow the tick animation to complete
        setTimeout(() => {
          window.location.href = theme.routes.cart;
        }, 300);
      } else {
        if(data.sections != null && Object.keys(data.sections).length > 0){
          // console.log('original');
          sectionsToUpdate.forEach((el) => {
            el.updateFromCartChange(data.sections[el.dataset.sectionId]);
          });
        }else{
          // console.log('custom coded');
          const sectionResponse = await this.handleSectionsUpdateRequest(sectionIds);
          // console.log(sectionResponse);
          if(Object.keys(sectionResponse).length > 0){
            sectionsToUpdate.forEach((el) => {
              el.updateFromCartChange(sectionResponse[el.dataset.sectionId]);
            });
          }else{
            console.log('noresponse', sectionResponse);
          }
        }

        if (theme.settings.afterAddToCart === 'notification') {
          const notification = document.getElementById('AddedNotification').content.firstElementChild.cloneNode(true);
          notification.dataset.productTitle = data.product_title;
          let notificationContainer = document.querySelector('.pageheader--sticky');
          if (!notificationContainer) {
            notificationContainer = document.querySelector('body');
          }
          notificationContainer.appendChild(notification);
        } else if (theme.settings.afterAddToCart === 'drawer') {
          document.querySelector('.js-cart-drawer').open();
          /*if(getCookie("enable_currency_codes") == "true"){
            const cartItems = document.querySelectorAll('.cart-item-list__body > div');
            cartItems.forEach((el, i) => {
              const currencyCode = el.querySelector('.cart-item__total');
              if(currencyCode != null){
                if(currencyCode.dataset.currencyCode == 'CAD' || currencyCode.dataset.currencyCode == 'AUD'){
                  el.querySelectorAll('.cart-item__total .theme-money').forEach((e, index) =>{
                    // e.innerHTML = e.dataset.currencyCode;
                    document.querySelectorAll('.cart-item-list__body > div')[i].querySelectorAll('.cart-item__total .theme-money')[index].innerHTML = e.dataset.currencyCode;
                  })
                }
              }
            });
          }*/
        }
      }

      this.dispatchEvent(new CustomEvent('on:cart:add', {
        bubbles: true,
        detail: {
          variantId: data.variant_id
        }
      }));

      // Re-enable 'Add to Cart' button.
      this.submitBtn.classList.add('is-success');
      this.submitBtn.removeAttribute('aria-disabled');
      setTimeout(() => {
        this.submitBtn.classList.remove('is-loading');
        this.submitBtn.classList.remove('is-success');
      }, 2000);
    } catch (error) {
      console.log(error); // eslint-disable-line
      this.dispatchEvent(new CustomEvent('on:cart:error', {
        bubbles: true,
        detail: {
          error: this.errorMsg.textContent
        }
      }));

      // Re-enable 'Add to Cart' button.
      this.submitBtn.classList.remove('is-loading');
      this.submitBtn.classList.remove('is-success');
      this.submitBtn.removeAttribute('aria-disabled');
    }
  }

  async handleSectionsUpdateRequest(sectionIds){
    const sectionIdsStr = sectionIds.join(',')
    const response = await fetch(window.Shopify.routes.root + "?sections="+sectionIdsStr);
    const data = await response.json();
    return data;
  }

  /**
   * Shows/hides an error message.
   * @param {string} [error=false] - Error to show a message for.
   */
  setErrorMsgState(error = false) {
    this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
    if (!this.errorMsg) return;

    this.errorMsg.hidden = !error;
    if (error) {
      this.errorMsg.innerHTML = '';
      const errorArray = Array.isArray(error) ? error : [error];
      errorArray.forEach((err, index) => {
        if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
        this.errorMsg.insertAdjacentText('beforeend', err);
      });
    }
  }

  /**
   * Validate form & show errors.
   * @returns {boolean} - Is form valid.
   */
  validate() {
    let isValid = true;
    
    let className = 'main-option-container';
    if(this.classList.contains('complementary-product')){
      className = 'complementary-option-container';
    }

    let formContainer = this;
    if(this.classList.contains('sticky-atc')){
      formContainer = this.mainProductForm;
    }
    
    formContainer.querySelector(`.${className}`).querySelectorAll('variant-picker .option-selector').forEach((el) => {
      if (el.dataset.selectorType === 'listed') {
        const input = el.querySelector('input');
        // const inputIsValid = input.reportValidity();
        if (!input.checkValidity()) {
          isValid = false;
          this.submitBtn.classList.add('is-error');
          this.submitBtn.innerText = 'Please select a '+el.dataset.option;
          // el.querySelector('.error-wrapper').classList.remove('hidden');
          // el.querySelector('.error-msg').innerText = `Please select the ${el.dataset.option} option`;
          console.log(formContainer);
          if(className == 'main-option-container' && document.querySelector('.sticky-atc__buttons') != null && !formContainer.classList.contains('quickview-product')){
            document.querySelector('.sticky-atc__buttons .add-to-cart').classList.add('is-error');
            document.querySelector('.sticky-atc__buttons .add-to-cart').innerText = 'Please select a '+el.dataset.option;
          }
        }
      } else {
        // Clear prior errors
        el.querySelectorAll('.label__prefix').forEach((label) => label.remove());
        el.querySelectorAll('.label--contains-error').forEach((label) => label.classList.remove('label--contains-error'));

        const selectedOption = el.querySelector('.custom-select .js-option[aria-selected="true"]');
        if (!selectedOption) {
          const label = el.querySelector('.label');
          // label.setAttribute('aria-live', 'polite');
          // label.classList.add('label--contains-error');
          const labelPrefix = document.createElement('span');
          labelPrefix.innerText = `${theme.strings.productsProductChooseA} `;
          labelPrefix.className = 'label__prefix';
          // label.prepend(labelPrefix);
          // theme.scrollToRevealElement(el);
          el.querySelector('.error-wrapper').classList.remove('hidden');
          el.querySelector('.error-msg').innerText = `Please select the ${el.dataset.option} option`;
          isValid = false;
        }
      }
    });

    return isValid;
  }
};

window.customElements.define('product-form', ProductForm);

class ProductInventory extends HTMLElement {
  constructor() {
    super();
    window.initLazyScript(this, this.initLazySection.bind(this));
    this.init.bind(this);
  }

  init(){
    this.threshold = parseInt(this.dataset.threshold, 10);
    this.productInventory = this.querySelector('.product-inventory');
    this.inventoryNotice = this.querySelector('.product-inventory__status');
    this.variantInventory = this.getVariantInventory();

    this.closest('.js-product').addEventListener('on:variant:change', this.handleVariantChange.bind(this));
  }

  initLazySection() {
    this.threshold = parseInt(this.dataset.threshold, 10);
    this.productInventory = this.querySelector('.product-inventory');
    this.inventoryNotice = this.querySelector('.product-inventory__status');
    this.variantInventory = this.getVariantInventory();

    this.closest('.js-product').addEventListener('on:variant:change', this.handleVariantChange.bind(this));
  }

  /**
   * Gets the inventory data for all product variants
   * @returns {?object}
   */
  getVariantInventory() {
    const dataEl = this.querySelector('[type="application/json"]');
    return this.variantInventory || JSON.parse(dataEl.textContent);
  }

  /**
   * Handles a 'change' event on the variant picker element
   * @param {object} evt - Event object
   */
  handleVariantChange(evt) {
    this.updateInventory(
      evt.detail.variant
        ? this.variantInventory.find((v) => v.id === evt.detail.variant.id)
        : null
    );
  }

  /**
   *
   * Updates the inventory notice
   * @param {?object} inventory - the inventory data
   */
  updateInventory(inventory) {
    if (!inventory) {
      this.productInventory.hidden = true;
      return;
    }

    const count = inventory.inventory_quantity;
    const showCount = this.dataset.showInventoryCount === 'always'
      || (
        this.dataset.showInventoryCount === 'low'
        && count <= this.threshold
      );

    let notice = null;
    if (showCount) {
      if (count <= this.threshold) {
        notice = this.dataset.textXLeftLow.replace('[QTY]', count);
      } else {
        notice = this.dataset.textXLeftOk.replace('[QTY]', count);
      }
    } else {
      // eslint-disable-next-line no-lonely-if
      if (count <= this.threshold) {
        notice = this.dataset.textLow;
      } else {
        notice = this.dataset.textOk;
      }
    }

    if(this.closest('.js-product').classList.contains('cart-edit-product')){
      const currentQty = this.closest('.js-product').querySelector('.original-quantity').value;
      // console.log(currentQty, count);
      this.closest('.js-product').querySelector('.js-form-error').innerHTML = '';
      this.closest('.js-product').querySelector('.js-form-error').hidden = true;
      this.closest('.js-product').querySelector('.quantity').value = currentQty;
      if(currentQty > count){
        this.closest('.js-product').querySelector('.js-form-error').innerHTML = `<p>You can add a maximum of ${count} qty of this variant. Only ${count} qty are available.</p>`;
        this.closest('.js-product').querySelector('.js-form-error').hidden = false;
        this.closest('.js-product').querySelector('.quantity').value = count;
        // console.log('bhai vadhare che');
      }
    }
    
    if(this.closest('.js-product').classList.contains('outfit-product-form')){
      
      this.productInventory.classList.toggle('product-inventory--low', count <= this.threshold);
      // this.productInventory.classList.toggle('product-inventory--ok', count > this.threshold);
      this.classList.remove('hidden');
      if(count <= 0){
        this.inventoryNotice.innerHTML = `<span class="out-of-stock">This product is out of stock.</span>`;
      }else{
        if(notice.indexOf('||') != -1){
          this.inventoryNotice.innerHTML = `<span class="stock-left">${notice.split('||')[0]}</span> <span class="ship-info">${notice.split('||')[1]}</span>`;
        }else{
          this.classList.add('hidden');
          this.inventoryNotice.innerHTML = `<span class="ship-info">${notice}</span>`;
        }
      }
    }else{
      this.productInventory.classList.toggle('product-inventory--low', count <= this.threshold);
      this.productInventory.classList.toggle('product-inventory--ok', count > this.threshold);
      if(notice.indexOf('||') != -1){
        this.inventoryNotice.innerHTML = `<span class="stock-left">${notice.split('||')[0]}</span> <span class="ship-info">${notice.split('||')[1]}</span>`;
      }else{
        this.inventoryNotice.innerHTML = `<span class="ship-info">${notice}</span>`;
      }
    }
    
    
    this.productInventory.hidden = false;
  }
}

customElements.define('product-inventory', ProductInventory);

if (!customElements.get('quantity-wrapper')) {
  class QuantityWrapper extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.handleClick.bind(this));
    }

    handleClick(evt) {
      const btn = evt.target.closest('[data-quantity]');
      if (btn) {
        evt.preventDefault();
        const input = this.querySelector('[name="quantity"]');
        let change = btn.dataset.quantity === 'up' ? 1 : -1;
        if (input.step) {
          change *= parseInt(input.step, 10);
        }
        input.value = Math.max(1, parseInt(input.value, 10) + change);
        input.dispatchEvent(
          new CustomEvent('change', { bubbles: true, cancelable: false })
        );
      }
    }
  }

  customElements.define('quantity-wrapper', QuantityWrapper);
}

 /* A\B test bag icon d.dabhi start 04-02-2025 */
  if (window.innerWidth < 768) {
    const quickBuyBag = getCookie("quickbuy_icon");    
    const quickBuyBlock = document.querySelectorAll('.quickbuy-toggle');
    //console.log("quickBuyBlock", quickBuyBlock);
    if (quickBuyBag == "true") {
      
      quickBuyBlock.forEach((el) => {
        el.classList.add('quickbuy-icon'); // Add multiple classes                
      });
    }
  }
  /* A\B test bag icon d.dabhi end 04-02-2025 */

/* A/B Test expose header search bar 06-02-2025 d.dabhi start */
  // if (window.innerWidth < 768) {
  //   // const mobile_search_cookie = getCookie("mobile_search_bar");   
  //   if(document.querySelector('.pageheader') != undefined){

  //   } 
  //   const mobile_searchbar = document.querySelector('.pageheader');
  //   //const mobile_headersearch_body = document.querySelector('body');
  
  //   // if(mobile_search_cookie == "true"){
  //     mobile_searchbar.classList.add('expose-header-search');    
  //   // }

  //   window.addEventListener("scroll", function () {
  //     let scroll_down = document.querySelector(".expose-header-search");
  //     if (scroll_down) {
  //       let mobile_header_annoucement = document.querySelector("#announcement-bar-with-slider");
  //       let mobile_header = document.querySelector(".expose-header-search");
  //       let header_annoucement_height = (mobile_header_annoucement?.offsetHeight || 0) + (mobile_header?.offsetHeight || 0);
  //     // console.log("header_annoucement_height",header_annoucement_height);
  //       if (window.scrollY > header_annoucement_height) {
  //           scroll_down.classList.add("scrolled");          
  //       } else {
  //           scroll_down.classList.remove("scrolled");
  //       } 
  //     }
  //   });
  // }
  /* A/B Test expose header search bar 06-02-2025 d.dabhi end */

if (theme.settings.quickbuyStyle !== 'off') {
  theme.quickbuy = {
    quickbuyResizeObserver: null,
    init: () => {
      theme.quickbuy.quickbuyResizeObserver = new ResizeObserver((entries) => {
        for (let i = 0; i < entries.length; i += 1) {
          const entry = entries[i];
          if (entry.contentBoxSize || entry.contentRect) {
            theme.quickbuy.debouncedQuickbuyResize(entry.target);
          }
        }
      });

      theme.addDelegateEventListener(document, 'click', '.quickbuy-toggle', (evt, delEl) => {
        const productUrl = delEl.href;

        if(evt.target.classList.contains('cart-item__edit-option-btn')){
          evt.preventDefault();

          if(document.querySelector('body').classList.contains('template-cart')){
            if(typeof gtag != "undefined"){gtag('event', 'Cart Page Tracking', {'event_category': 'Cart Page','event_label': 'edit item details','value': 'updated bag'});}
          }else{
            if(typeof gtag != "undefined"){gtag('event', 'Slideout Cart Tracking', {'event_category': 'Slideout Cart','event_label': 'edit item details','value': 'updated bag'});}
          }
          
          const originalVariantId = evt.target.dataset.variantId;
          const originalVariantQty = evt.target.dataset.quantity;
          
          const popupButton = document.querySelector('#modal-cart-edit-popup-btn .btn');
          const popupContainer = document.querySelector('.modal-cart-edit-popup-container');
          if(popupContainer != null && popupButton != null){
            let detailCont = null;
            detailCont = popupContainer.querySelector('.inner');
            detailCont.classList.add('loading');
            
            /* Closing the cart drawer */
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer) {
              cartDrawer.close();
            }
            
            fetch(productUrl, {
                method: 'get',
                // signal: theme.quickbuy.currentRequestAbortController.signal
              })
              .then((response) => {
                if (!response.ok) {
                  throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.text();
              })
              .then((response) => {
                const tmpl = document.createElement('template');
                tmpl.innerHTML = response;
                const newDetail = tmpl.content.querySelector('.quickbuy-content').cloneNode(true);
  
                // convert to quickbuy content
                newDetail.querySelectorAll('.more').forEach((el) => { el.href = productUrl; });
                newDetail.querySelectorAll('.product-title').forEach((el) => {
                  el.innerHTML = `<a href="">${el.innerHTML}</a>`;
                  el.firstElementChild.href = productUrl;
                });
                newDetail.querySelectorAll('.show-gallery').forEach((el) => {
                  el.classList.remove('show-gallery');
                  el.href = productUrl;
                });
                newDetail.querySelectorAll('.not-in-quickbuy').forEach((el) => { el.remove(); });
                newDetail.querySelectorAll('.only-in-quickbuy').forEach((el) => { el.classList.remove('only-in-quickbuy'); });
                newDetail.querySelectorAll('.sticky-content-container').forEach((el) => { el.classList.remove('sticky-content-container'); });
                newDetail.querySelectorAll('pickup-availability').forEach((el) => { el.remove(); });
                newDetail.querySelectorAll('variant-picker[data-update-url="true"]').forEach((el) => { el.dataset.updateUrl = false; });
                newDetail.querySelectorAll('noscript').forEach((el) => el.remove());
  
                // enforce thumbnails-under gallery layout
                newDetail.querySelectorAll('.media-gallery .main-image carousel-slider').forEach((el) => { el.classList.remove('mobile-only'); });
                newDetail.querySelectorAll('.product-media-collage').forEach((el) => { el.remove(); });
                newDetail.querySelectorAll('.media-gallery .thumbnails').forEach((el) => { el.classList.remove('mobile-only'); });
                ['media-gallery--layout-carousel-beside', 'media-gallery--layout-columns-1', 'media-gallery--layout-columns-2', 'media-gallery--layout-collage-1', 'media-gallery--layout-collage-2'].forEach((cl) => {
                  newDetail.querySelectorAll(`.${cl}`).forEach((el) => {
                    el.classList.remove(cl);
                    el.classList.add('media-gallery--layout-carousel-under');
                  });
                });

                /* custom js code for open product edit */
                newDetail.classList.add('complementary-product');
                newDetail.classList.add('cart-edit-product');
                newDetail.querySelector('.js-product-form').classList.add('cart-product-option-edit-form');
                newDetail.querySelector('.js-product-form').classList.remove('main-product');
                const orignalIdInput = `<input type="hidden" name="original-id" class="original-id" value="${originalVariantId}">`;
                const newQtyInput = `<input type="hidden" name="quantity" class="quantity" value="${originalVariantQty}">`;
                const orignalQtyInput = `<input type="hidden" class="original-quantity" value="${originalVariantQty}">`;
                newDetail.querySelector('.js-product-form').insertAdjacentHTML('beforeend', orignalIdInput);
                newDetail.querySelector('.js-product-form').insertAdjacentHTML('beforeend', newQtyInput);
                newDetail.querySelector('.js-product-form').insertAdjacentHTML('beforeend', orignalQtyInput);
                newDetail.querySelector('.input-row').classList.add('complementary-option-container');
                newDetail.querySelectorAll('.option-selector').forEach((el) => {
                  el.querySelectorAll('.opt-label').forEach((label) => {
                    // console.log(label);
                    const id = `edit-${label.getAttribute('for')}`;
                    label.setAttribute('for', id);
                    label.previousElementSibling.id = id;
                    // console.log(label.previousElementSibling);
                    label.previousElementSibling.setAttribute('name', `edit-${label.previousElementSibling.getAttribute('name')}`);
                  });
                });
                if(newDetail.querySelector('.thumbnails') != null ){
                  newDetail.querySelector('.thumbnails').classList.add('hidden');
                }
                // newDetail.querySelector('.desktop-thumbnail-container').remove();

                newDetail.querySelector('.add-to-cart').setAttribute('data-add-to-cart-text', 'Update Bag');
                newDetail.querySelector('.add-to-cart').innerText = 'Update Bag';

                /*if(getCookie("enable_currency_codes") == "true"){
                  const price = newDetail.querySelector('.price:not(.fully-soldout)');
                  if(price != null){
                    if(price.dataset.currencyCode == 'CAD' || price.dataset.currencyCode == 'AUD'){
                      if(price.querySelector('.price__current') != undefined){
                        mainPrice = price.querySelector('.price__current').dataset.currencyCode;
                        newDetail.querySelector('.price:not(.fully-soldout)').querySelector('.price__current').innerHTML = mainPrice;
                      }
                      if(price.querySelector('.price__was') != null){
                        compareAtPrice = price.querySelector('.price__was').dataset.currencyCode;
                        newDetail.querySelector('.price:not(.fully-soldout)').querySelector('.price__was').innerHTML = compareAtPrice;
                      }
                    }
                  }
                }*/

                // add any external resources
                tmpl.content.querySelectorAll('.section-main-product link[rel="stylesheet"]').forEach((el) => {
                  if (!document.querySelector(`link[rel="stylesheet"][href="${el.getAttribute('href')}"]`)) {
                    document.head.insertAdjacentHTML('beforeend', el.outerHTML);
                  }
                });
                tmpl.content.querySelectorAll('.section-main-product script[src]').forEach((el) => {
                  if (!document.querySelector(`script[src="${el.getAttribute('src')}"]`)) {
                    const scr = document.createElement('script');
                    scr.src = el.getAttribute('src');
                    scr.defer = 'defer';
                    document.head.appendChild(scr);
                  }
                });
  
                detailCont.innerHTML = '';
                detailCont.appendChild(newDetail);
                // setTimeout(function(){
                  document.querySelector('.modal-cart-product-edit-popup .modal__heading-wrapper').classList.remove('hidden');
                  detailCont.classList.remove('loading');
                // }, 1500);
              });

              const modalDialog = document.querySelector('modal-dialog.modal-cart-product-edit-popup');
              if(modalDialog){
                modalDialog.open(popupButton);
              }
              // popupButton.click();
              // popupButton.dispatchEvent(new Event('click'));
            
          }
          return;
        }
        // Only show dropdown if screen is large enough for it to be useful
        
        if(window.innerWidth > 768){
           evt.preventDefault();
         // console.log('loading');
            const popupButton = document.querySelector('#modal-quickview-popup-btn .btn');
            const popupContainer = document.querySelector('.modal-product-quick-popup');
           // console.log(popupContainer);
          if(popupContainer != null && popupButton != null){
            let detailCont = null;
            detailCont = popupContainer.querySelector('.inner');
           // console.log(detailCont);
            detailCont.classList.add('loading');
          

         // load in content
            fetch(productUrl, {
              method: 'get',
             // signal: theme.quickbuy.currentRequestAbortController.signal
            })
              .then((response) => {
                if (!response.ok) {
                  throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.text();
              })
              .then((response) => {
               
                const tmpl = document.createElement('template');
                tmpl.innerHTML = response;
                const newDetail = tmpl.content.querySelector('.quickbuy-content').cloneNode(true);
 
                newDetail.classList.add('quickview-product');
                newDetail.classList.remove('main-product');
                // convert to quickbuy content
                
                newDetail.querySelectorAll('.more').forEach((el) => { el.href = productUrl; });
                newDetail.querySelectorAll('.product-title').forEach((el) => {
                  el.innerHTML = `<a href="">${el.innerHTML}</a>`;
                  el.firstElementChild.href = productUrl;
                });
                newDetail.querySelectorAll('.show-gallery').forEach((el) => {
                  el.classList.remove('show-gallery');
                  el.href = productUrl;
                });
                newDetail.querySelectorAll('.not-in-quickbuy').forEach((el) => { el.remove(); });
                newDetail.querySelectorAll('.only-in-quickbuy').forEach((el) => { el.classList.remove('only-in-quickbuy'); });
                newDetail.querySelectorAll('.sticky-content-container').forEach((el) => { el.classList.remove('sticky-content-container'); });
                newDetail.querySelectorAll('pickup-availability').forEach((el) => { el.remove(); });
                newDetail.querySelectorAll('variant-picker[data-update-url="true"]').forEach((el) => { el.dataset.updateUrl = false; });
                newDetail.querySelectorAll('noscript').forEach((el) => el.remove());

                // enforce thumbnails-under gallery layout
                newDetail.querySelectorAll('.media-gallery .main-image carousel-slider').forEach((el) => { el.classList.remove('mobile-only'); });
                newDetail.querySelectorAll('.product-media-collage').forEach((el) => { el.remove(); });
                newDetail.querySelectorAll('.media-gallery .thumbnails').forEach((el) => { el.classList.remove('mobile-only'); });
                ['media-gallery--layout-carousel-beside', 'media-gallery--layout-columns-1', 'media-gallery--layout-columns-2', 'media-gallery--layout-collage-1', 'media-gallery--layout-collage-2'].forEach((cl) => {
                  newDetail.querySelectorAll(`.${cl}`).forEach((el) => {
                    el.classList.remove(cl);
                    el.classList.add('media-gallery--layout-carousel-under');
                  });
                });

                // add any external resources
                tmpl.content.querySelectorAll('.section-main-product link[rel="stylesheet"]').forEach((el) => {
                  if (!document.querySelector(`link[rel="stylesheet"][href="${el.getAttribute('href')}"]`)) {
                    document.head.insertAdjacentHTML('beforeend', el.outerHTML);
                  }
                });
                tmpl.content.querySelectorAll('.section-main-product script[src]').forEach((el) => {
                  if (!document.querySelector(`script[src="${el.getAttribute('src')}"]`)) {
                    const scr = document.createElement('script');
                    scr.src = el.getAttribute('src');
                    scr.defer = 'defer';
                    document.head.appendChild(scr);
                  }
                });

                // if(getCookie("enable_currency_codes") == "true"){
                //   const price = newDetail.querySelector('.price:not(.fully-soldout)');
                  
                //   if(price.dataset.currencyCode == 'CAD' || price.dataset.currencyCode == 'AUD'){
                //     if(price.querySelector('.price__current') != undefined){
                //       mainPrice = price.querySelector('.price__current').dataset.currencyCode;
                //       newDetail.querySelector('.price:not(.fully-soldout)').querySelector('.price__current').innerHTML = mainPrice;
                //     }
                //     if(price.querySelector('.price__was') != null){
                //       compareAtPrice = price.querySelector('.price__was').dataset.currencyCode;
                //       newDetail.querySelector('.price:not(.fully-soldout)').querySelector('.price__was').innerHTML = compareAtPrice;
                //     }
                //   }
                // }
               
               // console.log(newDetail);
                detailCont.innerHTML = '';
               // console.log(detailCont);
               const viewMoreLinkPlace =  newDetail.querySelector('.product-form');
                const viewmore = '<div class="afterpay-content"></div><div class="view-more-detail"><a class="btn btn-large view-more-link" href='+productUrl+'>View more detail</a></div>';
                viewMoreLinkPlace.insertAdjacentHTML('beforeend', viewmore);
                detailCont.appendChild(newDetail);
                const price = newDetail.querySelector('.price .price__current').dataset.price;
                console.log(price);
                 const apConfig = {
                  priceSelector: '.afterpay-content',
                  locale: 'en_US',
                  currency: 'USD',
                  afterpayLogoColor: 'white',
                  showUpperLimit: false,
                  amount: price,
                  minMaxThreshold: {
                    min: 100,
                    max: 100000,
                  },
                };
                new presentAfterpay(apConfig).init();
                detailCont.classList.remove('loading');
                const $mainCaresoul = $(detailCont).find('.main-carousel .slider__grid');
                if ($mainCaresoul.length && $mainCaresoul.children().length > 6) {
                  $mainCaresoul.slick({
                    slidesToScroll: 1,
                    slidesToShow: 1,
                    infinite: false,
                    nextArrow: $('.slider-nav__btn[name="next"]'),
                    prevArrow: $('.slider-nav__btn[name="prev"]')
                  });
                }
                
// Initialize Slick only if there are enough thumbnails
// const $thumbSlider = $(detailCont).find('.carousel.thumbnails .slider__grid');

// if ($thumbSlider.length && $thumbSlider.children().length > 6) {
//   $thumbSlider.slick({
//     slidesToScroll: 3,
//     slidesToShow: 5,
//     infinite: false
//   });
// }
                
                // if($('.carousel.thumbnails .slider__grid').length > 6){
                //   $('.carousel.thumbnails .slider__grid').slick({
                //     slidesToScroll : 3,
                //     slidesToShow: 6.5,
                //     infinite:false
                //   });
                // }

               // quickbuyCont.dispatchEvent(new CustomEvent('on:quickbuy:after-open', { bubbles: true }));
              });

           const closeButton = popupContainer.querySelector('.modal__close-btn');
            closeButton.addEventListener('click', function () {
              detailCont.innerHTML = '';
            });

            // enable close button
             const modalDialog = document.querySelector('modal-dialog.modal-product-quick-popup');
              if(modalDialog){
                modalDialog.open(popupButton);
              }
          }
          // disable close button
            
           // closeButton.setAttribute('tabindex', '-1');
           // closeButton.removeEventListener('click', theme.quickbuy.onCloseClick);
          
        }else{
        // Only show dropdown if screen is large enough for it to be useful
        
          evt.preventDefault();

          // cancel current request if one exists
          if (theme.quickbuy.currentRequestAbortController) {
            theme.quickbuy.currentRequestAbortController.abort();
          }
          theme.quickbuy.currentRequestAbortController = new AbortController();

          const block = delEl.closest('.product-block');
          const slider = delEl.closest('.collection-slider');
          let detailCont = null;
          let quickbuyCont = null;
          let sliderRow = null;

          // do different things if it's inside a slideshow
          if (slider) {
            sliderRow = slider.closest('.collection-slider-row');
            // slider without detail
            if (!sliderRow.querySelector('.quickbuy-container')) {
              return;
            }
            quickbuyCont = sliderRow.querySelector('.quickbuy-container');
          } else {
            theme.quickbuy.setBlockHeights(block);
            quickbuyCont = block.querySelector('.quickbuy-container');
          }
          detailCont = quickbuyCont.querySelector('.inner');

          // toggle active class on block
          block.classList.toggle('expanded');
          if (block.classList.contains('expanded')) {
            quickbuyCont.dispatchEvent(new CustomEvent('on:quickbuy:before-open', { bubbles: true }));

            // expanding
            if (slider) {
              // if another block is expanded, remove its expanded class
              const otherExpanded = Array.from(slider.querySelectorAll('.product-block.expanded')).filter((el) => el !== block);
              otherExpanded.forEach((el) => el.classList.remove('expanded'));

              // if expanding from empty, set initial detail container height to 0
              if (otherExpanded.length === 0) {
                quickbuyCont.style.height = 0;
              }
            } else {
              // close expanded siblings
              [...block.parentElement.children].forEach((el) => {
                if (el !== block && el.classList.contains('expanded')) {
                  theme.quickbuy.contractDetail(el, true);
                }
              });
            }

            // monitor for contents changing size
            theme.quickbuy.quickbuyResizeObserver.disconnect();
            theme.quickbuy.quickbuyResizeObserver.observe(detailCont);

            // add spinner
            detailCont.innerHTML = '<div class="loading-spinner"></div>';

            // load in content
            fetch(productUrl, {
              method: 'get',
              signal: theme.quickbuy.currentRequestAbortController.signal
            })
              .then((response) => {
                if (!response.ok) {
                  throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.text();
              })
              .then((response) => {
                const tmpl = document.createElement('template');
                tmpl.innerHTML = response;
                const newDetail = tmpl.content.querySelector('.quickbuy-content').cloneNode(true);

                newDetail.classList.add('quickview-product');
                newDetail.classList.remove('main-product');
                // convert to quickbuy content
                newDetail.querySelectorAll('.more').forEach((el) => { el.href = productUrl; });
                newDetail.querySelectorAll('.product-title').forEach((el) => {
                  el.innerHTML = `<a href="">${el.innerHTML}</a>`;
                  el.firstElementChild.href = productUrl;
                });
                newDetail.querySelectorAll('.show-gallery').forEach((el) => {
                  el.classList.remove('show-gallery');
                  el.href = productUrl;
                });
                newDetail.querySelectorAll('.not-in-quickbuy').forEach((el) => { el.remove(); });
                newDetail.querySelectorAll('.only-in-quickbuy').forEach((el) => { el.classList.remove('only-in-quickbuy'); });
                newDetail.querySelectorAll('.sticky-content-container').forEach((el) => { el.classList.remove('sticky-content-container'); });
                newDetail.querySelectorAll('pickup-availability').forEach((el) => { el.remove(); });
                newDetail.querySelectorAll('variant-picker[data-update-url="true"]').forEach((el) => { el.dataset.updateUrl = false; });
                newDetail.querySelectorAll('noscript').forEach((el) => el.remove());

                // enforce thumbnails-under gallery layout
                newDetail.querySelectorAll('.media-gallery .main-image carousel-slider').forEach((el) => { el.classList.remove('mobile-only'); });
                newDetail.querySelectorAll('.product-media-collage').forEach((el) => { el.remove(); });
                newDetail.querySelectorAll('.media-gallery .thumbnails').forEach((el) => { el.classList.remove('mobile-only'); });
                ['media-gallery--layout-carousel-beside', 'media-gallery--layout-columns-1', 'media-gallery--layout-columns-2', 'media-gallery--layout-collage-1', 'media-gallery--layout-collage-2'].forEach((cl) => {
                  newDetail.querySelectorAll(`.${cl}`).forEach((el) => {
                    el.classList.remove(cl);
                    el.classList.add('media-gallery--layout-carousel-under');
                  });
                });

                // add any external resources
                tmpl.content.querySelectorAll('.section-main-product link[rel="stylesheet"]').forEach((el) => {
                  if (!document.querySelector(`link[rel="stylesheet"][href="${el.getAttribute('href')}"]`)) {
                    document.head.insertAdjacentHTML('beforeend', el.outerHTML);
                  }
                });
                tmpl.content.querySelectorAll('.section-main-product script[src]').forEach((el) => {
                  if (!document.querySelector(`script[src="${el.getAttribute('src')}"]`)) {
                    const scr = document.createElement('script');
                    scr.src = el.getAttribute('src');
                    scr.defer = 'defer';
                    document.head.appendChild(scr);
                  }
                });

                /*if(getCookie("enable_currency_codes") == "true"){
                  const price = newDetail.querySelector('.price:not(.fully-soldout)');
                  if(price != null){
                    if(price.dataset.currencyCode == 'CAD' || price.dataset.currencyCode == 'AUD'){
                      if(price.querySelector('.price__current') != undefined){
                        mainPrice = price.querySelector('.price__current').dataset.currencyCode;
                        newDetail.querySelector('.price:not(.fully-soldout)').querySelector('.price__current').innerHTML = mainPrice;
                      }
                      if(price.querySelector('.price__was') != null){
                        compareAtPrice = price.querySelector('.price__was').dataset.currencyCode;
                        newDetail.querySelector('.price:not(.fully-soldout)').querySelector('.price__was').innerHTML = compareAtPrice;
                      }
                    }
                  }
                }*/

                detailCont.innerHTML = '';
                detailCont.appendChild(newDetail);

                if($('.carousel.thumbnails .slider__grid').length > 6){
                  $('.carousel.thumbnails .slider__grid').slick({
                    slidesToScroll : 3,
                    slidesToShow: 6.5,
                    infinite:false
                  });
                }

                quickbuyCont.dispatchEvent(new CustomEvent('on:quickbuy:after-open', { bubbles: true }));
              });

            // enable close button
            const closeButton = quickbuyCont.querySelector('.close-detail');
            closeButton.removeAttribute('tabindex');
            closeButton.addEventListener('click', theme.quickbuy.onCloseClick);

            // StampedFn.loadBadges();

            // Scroll to an appropriate position
            const scrollOffset = -120 - theme.stickyHeaderHeight();
            if (slider) {
              // Simple for a carousel
              window.scrollTo({ top: theme.getOffsetTopFromDoc(quickbuyCont) + scrollOffset, behavior: 'smooth' });
            } else {
              // Grid - calculate where to scroll if quickbuys above this block will collapse
              theme.quickbuy.saveProductGridSpacingData();
              window.scrollTo({ top: parseFloat((block.dataset.qbScrollTo || theme.getOffsetTopFromDoc(quickbuyCont))) + scrollOffset, behavior: 'smooth' });
            }
          } else {
            // Close quick buy
            if (slider) {
              // collapse detail container & empty
              quickbuyCont.style.height = '0px';
              setTimeout(() => {
                if (quickbuyCont.style.height === '0px') {
                  detailCont.innerHTML = '';
                }
              }, parseFloat(
                getComputedStyle(quickbuyCont).transitionDuration
              ) * 1000);
            } else {
              theme.quickbuy.contractDetail(block);
            }

            // scroll to top of closing block, after reflow
            setTimeout(() => {
              window.scrollTo({ top: theme.getOffsetTopFromDoc(block) - theme.stickyHeaderHeight() - 20, behavior: 'smooth' });
            }, 100);

            // disable close button
            const closeButton = quickbuyCont.querySelector('.close-detail');
            closeButton.setAttribute('tabindex', '-1');
            closeButton.removeEventListener('click', theme.quickbuy.onCloseClick);
          
        }
        }
      });
    },

    debouncedQuickbuyResizeTimeoutID: -1,
    debouncedQuickbuyResize: (qbInner) => {
      clearTimeout(theme.quickbuy.debouncedQuickbuyResizeTimeoutID);
      theme.quickbuy.debouncedQuickbuyResizeTimeoutID = setTimeout(() => {
        const qbc = qbInner.closest('.quickbuy-container');
        const block = qbc.closest('.product-block');
        if (block) {
          // use padding in grid
          // also check expanded class in case it's mid close transition
          if (block.classList.contains('expanded')) {
            const targetHeight = qbInner.clientHeight;
            if(window.innerWidth > 767){
              block.style.paddingBottom = `${targetHeight + 20}px`;
            }
            qbc.style.height = `${targetHeight}px`;
          }
        } if (qbInner.childElementCount > 0) {
          // use height in carousel
          // - if not empty (e.g. post-close tidy-up)
          qbc.style.height = `${qbInner.clientHeight}px`;
        }
        
      }, 100);
    },

    contractDetail: (block, instant) => {
      // Cancel everything
      theme.quickbuy.quickbuyResizeObserver.disconnect();
      clearTimeout(theme.quickbuy.debouncedQuickbuyResizeTimeoutID);

      block.classList.remove('expanded');
      const quickbuyCont = block.querySelector('.quickbuy-container');
      if (instant) {
        quickbuyCont.style.transitionDuration = '0ms';
        block.style.transitionDuration = '0ms';
      }
      quickbuyCont.style.height = 0;
      block.style.paddingBottom = 0;
      // empty .inner after transition
      const msToClose = parseFloat(getComputedStyle(quickbuyCont).transitionDuration) * 1000;
      setTimeout(() => {
        if (quickbuyCont.style.height === '0px') {
          quickbuyCont.querySelector('.inner').innerHTML = '';
        }
        if (instant) {
          quickbuyCont.style.transitionDuration = '';
          block.style.transitionDuration = '';
        }
      }, msToClose);

      quickbuyCont.dispatchEvent(new CustomEvent('on:quickbuy:after-close', { bubbles: true }));
    },

    saveProductGridSpacingData: () => {
      document.querySelectorAll('.product-grid').forEach((el) => {
        const blocks = el.querySelectorAll('.product-block .block-inner');
        if (blocks.length <= 1) return; // Skip for empty colls
        let row = 0;
        let currTop = 0;
        let runningHeight = 0;
        const gutter = parseFloat(getComputedStyle(el).rowGap);
        const listingOffset = theme.getOffsetTopFromDoc(blocks[0]);
        const rowOffsets = [listingOffset];
        blocks.forEach((block, index) => {
          if (block.clientHeight > runningHeight) {
            runningHeight = block.clientHeight;
          }

          const currOffsetTop = theme.getOffsetTopFromDoc(block);
          if (index === 0) {
            currTop = currOffsetTop;
          } else if (currOffsetTop > currTop) {
            row += 1;
            currTop = currOffsetTop;
            rowOffsets.push(gutter + runningHeight + rowOffsets[row - 1]);
            runningHeight = 0;
          }

          block.dataset.gridRow = row;
        });

        blocks.forEach((block) => {
          block.dataset.qbScrollTo = rowOffsets[block.dataset.gridRow] + block.clientHeight;
        });
      });
    },

    setBlockHeights: (block) => {
      // Align product block heights in this row
      const list = block.closest('.product-grid');
      if (!list) return;

      let tallest = 0;
      const blockTop = block.offsetTop;
      const blocksInThisRow = [...list.querySelectorAll('.product-block')].filter((el) => el.offsetTop === blockTop);
      blocksInThisRow.forEach((el) => {
        if(el.querySelector('.block-inner-inner') != null){
          const elInnerHeight = el.querySelector('.block-inner .block-inner-inner').clientHeight;
          if (elInnerHeight > tallest) tallest = elInnerHeight;
        }
      });
      blocksInThisRow.forEach((el) => {
        // console.log(el, el.classList);
        el.querySelector('.block-inner').style.setProperty('--qb-block-height', `${tallest}px`);
        // if(el.classList.contains('inline-content-slot')){
        //   el.classList.add('qv-open');
        // }
      });

      // list.querySelectorAll('.product-block').forEach((el) => {
      //   if(el.classList.contains('inline-content-slot') && el.classList.contains('qv-open')){
      //     el.querySelector('.block-inner').style.setProperty('--qb-block-height', `${tallest}px`);
      //   }
      // });
    },

    setBlockHeightZero: () => {
      const list = document.querySelector('.product-grid');
      if (!list) return;

      const allBlock = list.querySelectorAll('.product-block');

      allBlock.forEach((el) => {
        el.querySelector('.block-inner').style.setProperty('--qb-block-height', `0px`);
      });
    },

    onCloseClick: (evt) => {
      evt.preventDefault();
      evt.stopPropagation();
      const slider = evt.target.closest('.collection-slider-row');
      if (slider) {
        slider.querySelector('.product-block.expanded .quickbuy-toggle').dispatchEvent(new CustomEvent('click', { bubbles: true, cancelable: true }));
      } else {
        evt.target.closest('.product-block').querySelector('.quickbuy-toggle').dispatchEvent(new CustomEvent('click', { bubbles: true, cancelable: true }));
      }
      
      if(window.innerWidth < 768){
        document.querySelector('body').classList.remove('quick-view-overlay');
      }

      if(typeof gtag != "undefined"){
        if(document.querySelector('body').classList.contains('template-index')){
          gtag('event', 'Homepage Tracking', {'event_category': 'homepage','event_label': 'quickview click','value': 'quickview close'});
        }
        if(document.querySelector('body').classList.contains('template-collection')){
          gtag('event', 'Collection Page Tracking', {'event_category': 'collection page','event_label': 'quickview click','value': 'quickview close'});
        }
        if(document.querySelector('body').classList.contains('template-product')){
          gtag('event', 'Product Page Tracking', {'event_category': 'product page','event_label': 'quickview click','value': 'quickview close'});
        }
      }    
      
      setTimeout(function() {
        // document.querySelectorAll('.inline-content-slot').forEach((el) => {
        //   el.classList.remove('qv-open');
        // });
        theme.quickbuy.setBlockHeightZero();
      }, 500);
    }
  };

  theme.quickbuy.init();
}

class CarouselSlider extends HTMLElement {
  constructor() {
    super();
    this.slides = [...this.querySelectorAll('.slider__item:not([hidden])')];
    if (this.slides.length < 2) {
      this.setCarouselState(false);
      return;
    }

    window.initLazyScript(this, this.init.bind(this));
  }

  init() {
    this.slider = this.querySelector('.slider');
    this.grid = this.querySelector('.slider__grid');
    this.nav = this.querySelector('.slider-nav');
    this.rtl = document.dir === 'rtl';

    if (this.nav) {
      this.prevBtn = this.querySelector('button[name="prev"]');
      this.nextBtn = this.querySelector('button[name="next"]');
    }

    this.initSlider();
    
    window.addEventListener('on:debounced-resize', this.handleResize.bind(this));

    this.setAttribute('loaded', '');
  }

  initSlider() {
    this.gridWidth = this.grid.clientWidth;

    // Distance between leading edges of adjacent slides (i.e. width of card + gap).
    this.slideSpan = this.getWindowOffset(this.slides[1]) - this.getWindowOffset(this.slides[0]);

    // Note: Slide can re-initialise after previous use
    this.currentIndex = Math.round(this.slider.scrollLeft / this.slideSpan) || 0;

    // Width of gap between slides.
    this.slideGap = this.slideSpan - this.slides[0].clientWidth;

    this.slidesPerPage = Math.round((this.gridWidth + this.slideGap) / this.slideSpan);
    this.slidesToScroll = theme.settings.sliderItemsPerNav === 'page' ? this.slidesPerPage : 1;
    this.totalPages = this.slides.length - this.slidesPerPage + 1;

    this.setCarouselState(this.totalPages > 1);
    if (this.dataset.dynamicHeight === 'true') {
      this.updateDynamicHeight();
    }
    this.addListeners();

    if(this.classList.contains('thumbnails') && window.innerWidth > 1040){
      // this.initSlickSlider();
    }
    
    if (this.totalPages < 2 || !this.nav) return;

    this.sliderStart = this.getWindowOffset(this.slider);
    if (!this.sliderStart) this.sliderStart = (this.slider.clientWidth - this.gridWidth) / 2;
    this.sliderEnd = this.sliderStart + this.gridWidth;

    if (window.matchMedia('(pointer: fine)').matches) {
      this.slider.classList.add('is-grabbable');
    }

    this.setButtonStates();
  }

  initSlickSlider(){
    if($('.product-thumb-slider').hasClass('slick-initialized')){
      console.log('dd');
    }
    $('.product-thumb-slider').not('.slick-initialized').slick({
      vertical: true,
      verticalSwiping: true,
      arrows:true,
      infinite: false,
      slidesToShow:6,
      slidesToScroll:2
    });
  }

  destroySlick() {
    $('.product-thumb-slider').slick('unslick');
  }

  /**
   * Re-initialise state. Call if slides have changed.
   */
  refresh() {
    if (this.hasAttribute('loaded')) {
      this.removeListeners();
      this.style.removeProperty('--current-slide-height');
    }
    this.slides = [...this.querySelectorAll('.slider__item:not([hidden])')];
    if (this.slides.length < 2) {
      this.setCarouselState(false);
      return;
    }
    this.init();
  }

  addListeners() {
    this.scrollHandler = debounce(this.handleScroll.bind(this), 100);
    this.slider.addEventListener('scroll', this.scrollHandler);

    if (this.nav) {
      this.navClickHandler = this.handleNavClick.bind(this);
      this.nav.addEventListener('click', this.navClickHandler);
    }

    if (window.matchMedia('(pointer: fine)').matches) {
      this.mousedownHandler = this.handleMousedown.bind(this);
      this.mouseupHandler = this.handleMouseup.bind(this);
      this.mousemoveHandler = this.handleMousemove.bind(this);

      this.slider.addEventListener('mousedown', this.mousedownHandler);
      this.slider.addEventListener('mouseup', this.mouseupHandler);
      this.slider.addEventListener('mouseleave', this.mouseupHandler);
      this.slider.addEventListener('mousemove', this.mousemoveHandler);
    }
  }

  removeListeners() {
    this.slider.removeEventListener('scroll', this.scrollHandler);

    if (this.nav) {
      this.nav.removeEventListener('click', this.navClickHandler);
    }

    if (window.matchMedia('(pointer: fine)').matches) {
      this.slider.removeEventListener('mousedown', this.mousedownHandler);
      this.slider.removeEventListener('mouseup', this.mouseupHandler);
      this.slider.removeEventListener('mouseleave', this.mouseupHandler);
      this.slider.removeEventListener('mousemove', this.mousemoveHandler);
    }
  }

  /**
   * Handles 'scroll' events on the slider element.
   */
  handleScroll() {
    const previousIndex = this.currentIndex;
    
    this.currentIndex = Math.round(Math.abs(this.slider.scrollLeft) / this.slideSpan);

    if(this.closest('.media-gallery') != null && this.closest('.product-info') != null && this.closest('.quickbuy-container') == null){
      if(typeof gtag != "undefined"){
        gtag('event', 'Product Page Tracking', {'event_category': 'product page','event_label': 'image carousel click','value': 'image carousel click'});
      }
    }else{
      // gtag('event', 'Product Page Tracking', {'event_category': 'product page','event_label': 'quickview click','value': 'quickview image carousel click'});
      if(typeof gtag != "undefined"){
        if(this.closest('.quickbuy-container') != null && document.querySelector('body').classList.contains('template-product')){
          gtag('event', 'Product Page Tracking', {'event_category': 'product page','event_label': 'quickview click','value': 'quickview image carousel click'});
        }
        if(this.closest('.quickbuy-container') != null && document.querySelector('body').classList.contains('template-collection')){
          gtag('event', 'Collection Page Tracking', {'event_category': 'collection page','event_label': 'quickview click','value': 'quickview image carousel click'});
        }
      }
    }
    
    if(this.classList.contains('complementary-products-slider')){
      this.slides.forEach((slide, k) => {
        if(k == this.currentIndex){
          slide.classList.add('is-active');
          let productId = slide.querySelector('.product-block').dataset.productId;
          let details = document.querySelectorAll('.complementary-product-detail');
          details.forEach((detail) => {
            if(detail.dataset.productId == productId){
              detail.classList.remove('hidden');
            }else{
              detail.classList.add('hidden');
            }
          })
        }else{
          slide.classList.remove('is-active');
        }
      });
    }
    
    if (this.nav) {
      this.setButtonStates();      
    }

    if (this.dataset.dynamicHeight === 'true') {
      this.updateDynamicHeight();
    }

    if (this.dataset.dispatchEvents === 'true' && previousIndex !== this.currentIndex) {
      this.dispatchEvent(
        new CustomEvent('on:carousel-slider:select', {
          bubbles: true,
          detail: {
            index: this.currentIndex,
            slide: this.slides[this.currentIndex]
          }
        })
      );
    }
  }

  /**
   * Handles 'mousedown' events on the slider element.
   * @param {object} evt - Event object.
   */
  handleMousedown(evt) {
    this.mousedown = true;
    this.startX = evt.pageX - this.sliderStart;
    this.scrollPos = this.slider.scrollLeft;
    this.slider.classList.add('is-grabbing');
  }

  /**
   * Handles 'mouseup' events on the slider element.
   */
  handleMouseup() {
    this.mousedown = false;
    this.slider.classList.remove('is-grabbing');
  }

  /**
   * Handles 'mousemove' events on the slider element.
   * @param {object} evt - Event object.
   */
  handleMousemove(evt) {
    if (!this.mousedown) return;
    evt.preventDefault();

    const x = evt.pageX - this.sliderStart;
    this.slider.scrollLeft = this.scrollPos - (x - this.startX) * 2;
  }

  /**
   * Handles 'click' events on the nav buttons container.
   * @param {object} evt - Event object.
   */
  handleNavClick(evt) {
    if (!evt.target.matches('.slider-nav__btn')) return;

    if ((evt.target.name === 'next' && !this.rtl) || (evt.target.name === 'prev' && this.rtl)) {
      this.scrollPos = this.slider.scrollLeft + (this.slidesToScroll * this.slideSpan);
    } else {
      this.scrollPos = this.slider.scrollLeft - (this.slidesToScroll * this.slideSpan);
    }
    // if(typeof gtag != "undefined"){
    //   if(evt.target.closest('.quickbuy-container') != null && document.querySelector('body').classList.contains('template-product')){
    //     gtag('event', 'Product Page Tracking', {'event_category': 'product page','event_label': 'quickview click','value': 'quickview image carousel click'});
    //   }
    //   if(evt.target.closest('.quickbuy-container') != null && document.querySelector('body').classList.contains('template-collection')){
    //     gtag('event', 'Collection Page Tracking', {'event_category': 'collection page','event_label': 'quickview click','value': 'quickview image carousel click'});
    //   }
    // }
    
    this.slider.scrollTo({ left: this.scrollPos, behavior: 'smooth' });
  }

  /**
   * Handles 'on:debounced-resize' events on the window.
   */
  handleResize() {
    if (this.nav) this.removeListeners();
    this.initSlider();
  }

  /**
   * Show a specific item inside this slider.
   * @param {Element} el - Slide element
   * @param {string} transition - Transition to pass to behavior parameter of scrollTo (optional)
   */
  scrollToElement(el, transition) {
    if (!this.getSlideVisibility(el)) {
      // this.scrollPos = this.rtl ? (el.offsetLeft + this.slideSpan) : el.offsetLeft;
      this.scrollPos = el.offsetLeft;
      this.slider.scrollTo({ left: this.scrollPos, behavior: transition || 'smooth' });
    }
  }

  /**
   * Sets a height property based on the current slide.
   */
  updateDynamicHeight() {
    this.style.setProperty('--current-slide-height', `${this.slides[this.currentIndex].firstElementChild.clientHeight}px`);
  }

  /**
   * Gets the offset of an element from the edge of the viewport (left for ltr, right for rtl).
   * @param {number} el - Element.
   * @returns {number}
   */
  getWindowOffset(el) {
    if(el != undefined){
      return this.rtl
        ? window.innerWidth - el.getBoundingClientRect().right
        : el.getBoundingClientRect().left;
    }
    return 0;
  }

  /**
   * Gets the visible state of a slide.
   * @param {Element} el - Slide element.
   * @returns {boolean}
   */
  getSlideVisibility(el) {
    const slideStart = this.getWindowOffset(el);
    const slideEnd = Math.floor(slideStart + this.slides[0].clientWidth);
    return slideStart >= this.sliderStart && slideEnd <= this.sliderEnd;
  }

  /**
   * Sets the active state of the carousel.
   * @param {boolean} active - Set carousel as active.
   */
  setCarouselState(active) {
    if (active) {
      this.removeAttribute('inactive');

      // If slider width changed when activated, reinitialise it.
      if (this.gridWidth !== this.grid.clientWidth) {
        this.handleBreakpointChange();
      }
    } else {
      this.setAttribute('inactive', '');
    }
  }

  /**
   * Sets the disabled state of the nav buttons.
   */
  setButtonStates() {
    this.prevBtn.disabled = this.getSlideVisibility(this.slides[0]) && this.slider.scrollLeft === 0;
    this.nextBtn.disabled = this.getSlideVisibility(this.slides[this.slides.length - 1]);    
  }
}

customElements.define('carousel-slider', CarouselSlider);

const TermsAgreement = class extends HTMLElement {
  connectedCallback() {
    this.delegatedEvent = theme.addDelegateEventListener(document, 'click', '#cartform [name="checkout"], .additional-checkout-buttons input, a[href*="/checkout"]', this.handleFormSubmittingEvent.bind(this));
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.delegatedEvent);
  }

  handleFormSubmittingEvent(evt) {
    if (!this.querySelector('input:checked')) {
      evt.preventDefault();
      theme.showQuickPopup(theme.strings.cartTermsConfirmation, this.querySelector('[type="checkbox"]'));
    }
  }
};

window.customElements.define('terms-agreement', TermsAgreement);

const ToggleTarget = class extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.toggleOpen.bind(this));
    this.addEventListener('keyup', this.handleKeyUp.bind(this));
    if (this.dataset.toggleCloseLabel) {
      this.dataset.toggleOpenLabel = this.innerHTML;
    }
  }

  /**
   * Handles 'keyup' events on the document.
   * @param {object} evt - Event object.
   */
  handleKeyUp(evt) {
    if (evt.key === 'Enter') this.toggleOpen(evt);
  }

  /**
   * Opens or closes the target element.
   * @param {object} evt - Event object.
   */
  toggleOpen(evt) {
    evt.preventDefault();

    const target = document.querySelector(this.dataset.toggleTarget);
    const transCont = target.querySelector('.toggle-target-container');
    const doCollapse = !target.classList.contains('toggle-target--hidden');
    const transitionDuration = parseFloat(getComputedStyle(target).transitionDuration) * 1000;

    if (!target.classList.contains('toggle-target--in-transition')) {
      if (doCollapse) {
        this.classList.add('toggle-target-toggler--is-hidden');
        target.classList.add('toggle-target--in-transition', 'toggle-target--hiding');
        target.style.height = `${transCont.clientHeight}px`;
        target.style.opacity = 1;

        if (this.dataset.toggleCloseLabel) {
          this.innerHTML = this.dataset.toggleOpenLabel;
        }

        setTimeout(() => {
          target.style.height = 0;
          target.style.opacity = 0;

          setTimeout(() => {
            target.classList.add('toggle-target--hidden');
            target.classList.remove('toggle-target--in-transition', 'toggle-target--hiding');
            target.style.height = '';
            target.style.opacity = '';
          }, transitionDuration);
        }, 10);
      } else {
        this.classList.remove('toggle-target-toggler--is-hidden');
        target.classList.add('toggle-target--in-transition', 'toggle-target--revealing');
        target.style.height = 0;
        target.style.opacity = 0;
        target.style.display = 'block';

        if (this.dataset.toggleCloseLabel) {
          this.innerHTML = this.dataset.toggleCloseLabel;
        }

        setTimeout(() => {
          target.style.height = `${transCont.clientHeight}px`;
          target.style.opacity = 1;

          setTimeout(() => {
            target.classList.remove('toggle-target--hidden', 'toggle-target--in-transition', 'toggle-target--revealing');
            target.style.height = '';
            target.style.opacity = '';
            target.style.display = '';
          }, transitionDuration);
        }, 10);
      }
    }
  }
};

window.customElements.define('toggle-target', ToggleTarget);

class VariantContent extends HTMLElement {
  constructor() {
    super();
    if (this.childElementCount > 0) {
      this.closest('.js-product').addEventListener('on:variant:change', this.handleVariantChange.bind(this));
    }
  }

  /**
   * Handles a 'change' event on the variant picker element
   * @param {object} evt - Event object
   */
  handleVariantChange(evt) {
    [...this.childNodes].filter((el) => el.tagName !== 'SCRIPT').forEach((el) => el.remove());
    this.querySelectorAll(':not(script)').forEach((el) => el.remove());
    const contentToShow = this.querySelector(`[data-variant="${evt.detail.variant ? evt.detail.variant.id : ''}"]`);
    if (contentToShow) {
      this.insertAdjacentHTML('beforeend', contentToShow.innerHTML);
    }
  }
}

customElements.define('variant-content', VariantContent);

const AnnouncementBar = class extends HTMLElement {
  constructor() {
    super();

    this.announcements = this.querySelectorAll('.announcement');
    if (this.announcements.length > 1) {
      this.backgrounds = Array.from(this.querySelectorAll('.announcement-bg'));
      this.changeDelay = 5000;
      this.currentAnnouncement = 0;
      this.querySelector('.announcement-bar__middle').addEventListener('focusin', this.pauseAnnouncements.bind(this));
      this.querySelector('.announcement-bar__middle').addEventListener('focusout', this.playAnnouncements.bind(this));
      this.querySelector('.announcement-button--previous').addEventListener('click', this.previousAnnouncement.bind(this));
      this.querySelector('.announcement-button--next').addEventListener('click', this.nextAnnouncement.bind(this));
      this.observer = new IntersectionObserver(this.handleIntersect.bind(this));
      this.observer.observe(this);
    }
  }
    updateSlides(slides){
    this.announcements = slides;
  }
  

  handleIntersect(entries) {
    entries.forEach((entry) => {
      if (entry.target === this) {
        if (entry.isIntersecting) {
          this.playAnnouncements(this);
        } else {
          this.pauseAnnouncements(this);
        }
      }
    });
  }

  playAnnouncements() {
    if (this.announcements.length > 1) {
      this.announcementInterval = setInterval(() => {
        this.setCurrentAnnouncement(this.currentAnnouncement + 1);
      }, this.changeDelay);
    }
  }

  pauseAnnouncements() {
    if (this.announcementInterval) {
      clearInterval(this.announcementInterval);
    }
  }

  setCurrentAnnouncement(newIndex) {
    this.currentAnnouncement = newIndex % this.announcements.length;
    this.announcements.forEach((announcement, index) => {
      const background = this.backgrounds.find((bg) => bg.dataset.index === index.toString());
      if (index !== this.currentAnnouncement) {
        announcement.classList.add('announcement--inactive');
        if (background) {
          background.classList.remove('is-active');
        }
      } else {
        announcement.classList.remove('announcement--inactive');
        if (background) {
          background.classList.add('is-active');
        }
      }
    });

    const headingColor = this.announcements[this.currentAnnouncement].style.getPropertyValue('--heading-color');
    const textColor = this.announcements[this.currentAnnouncement].style.getPropertyValue('--text-color');
    const linkColor = this.announcements[this.currentAnnouncement].style.getPropertyValue('--link-color');
    if (headingColor) {
      this.style.setProperty('--heading-color', headingColor);
    }
    if (textColor) {
      this.style.setProperty('--text-color', textColor);
    }
    if (linkColor) {
      this.style.setProperty('--link-color', linkColor);
    }
  }

  previousAnnouncement() {
    this.setCurrentAnnouncement(this.currentAnnouncement - 1);
  }

  nextAnnouncement() {
    this.setCurrentAnnouncement(this.currentAnnouncement + 1);
  }
};

window.customElements.define('announcement-bar', AnnouncementBar);

/*
  If the following conditions are met:
  1. This section directly follows the header
  2. This section is set to be full page height

  Then:
  If the header is transparent, subtract the height of messages and announcement bar.
  If the header if not transparent, subtract the height of the header.
*/

const ImageWithTextOverlay = class extends HTMLElement {
  constructor() {
    super();

    this.fullHeightContainer = this.classList.contains('height--full') ? this : this.querySelector('.height--full');

    if (this.fullHeightContainer) {
      if (this.closest('.shopify-section').previousElementSibling) return;
      const ph = document.querySelector('.pageheader');
      if (!ph) {
        this.fullHeightContainer.classList.add('height--full-ignore-header-height');
        return;
      }

      const thisOffsetTop = theme.getOffsetTopFromDoc(this);
      const phOffsetTop = theme.getOffsetTopFromDoc(ph);
      if (phOffsetTop - 5 > thisOffsetTop) { // Must be lower. For trans header, it'll roughly match
        this.fullHeightContainer.classList.add('height--full-ignore-header-height');
        return;
      }

      this.fullHeightContainer.classList.add('height--full-minus-header-height');
      this.checkForHeaderHeightSubtraction();

      const handleResize = debounce(this.checkForHeaderHeightSubtraction.bind(this), 300);
      this.resizeObserver = new ResizeObserver((entries) => {
        for (let i = 0; i < entries.length; i += 1) {
          const entry = entries[i];
          if (entry.contentBoxSize || entry.contentRect) {
            handleResize();
          }
        }
      });
      this.resizeObserver.observe(this);
    }
  }

  checkForHeaderHeightSubtraction() {
    let height = window.innerHeight;
    // transparent header & top section? slightly different calculation
    if (document.querySelector('.pageheader--transparent-permitted') && !this.closest('.shopify-section').previousElementSibling) {
      document.querySelectorAll('.section-store-messages, .section-announcement-bar').forEach((el) => {
        height -= el.clientHeight;
      });
    } else {
      height -= document.querySelector('.section-header').clientHeight;
    }
    this.fullHeightContainer.style.setProperty('--image-height', `${height}px`);
  }
};

window.customElements.define('image-with-text-overlay', ImageWithTextOverlay);

const PageHeader = class extends HTMLElement {
  constructor() {
    super();

    // Search (delegate event as contents may update with cart changes)
    if (this.querySelector('.main-search')) {
      theme.addDelegateEventListener(this, 'click', '.show-search-link', (evt) => {
        evt.preventDefault();
        document.body.classList.add('show-search');
        setTimeout(() => {
          this.querySelector('.main-search__input').focus();
        }, 500);
      });
    }

    // Navigation
    theme.inlineNavigationCheck();

    // Reveal cart drawer
    if (theme.settings.cartType === 'drawer' && document.querySelector('.js-cart-drawer') && this.querySelector('.cart-link')) {
      theme.addDelegateEventListener(this, 'click', '.cart-link', (evt) => {
        evt.preventDefault();

        if(Object.keys(apConfig).length > 0 && document.querySelector('.cart-drawer__footer .afterpay-paragraph') == null){
          if(!document.querySelector('.cart-drawer__content').classList.contains('cart-drawer__content--hidden')){
            const cartTotal = document.querySelector('.cart-subtotal').dataset.amount;
            const apConfig = {
              priceSelector: '.cart-policies',
              locale: 'en_US',
              currency: 'USD',
              afterpayLogoColor: 'white',
              showUpperLimit: false,
              amount: cartTotal,
              minMaxThreshold: {
                min: 100,
                max: 100000,
              },
            };
            new presentAfterpay(apConfig).init();
            /* removing $ FP start 04-04-2025 */
           /* if(getCookie("remove_price_icon") == "true"){
              let afterPay = document.querySelector('.cart-drawer__footer').querySelector('.afterpay-text1').textContent;
              if(afterPay.indexOf('$')){
                afterPay = afterPay.replace('$', '')
                document.querySelector('.cart-drawer__footer').querySelector('.afterpay-text1').textContent = afterPay;
              }
            }*/
            /* removing $ FP end 04-04-2025 */
          }
        }

        if(typeof gtag != "undefined"){
          gtag('event', 'Navigation Tracking', {'event_category': 'navigation','event_label': 'bag click','value': 'cart open'});
        }
        
        document.querySelector('.js-cart-drawer').open();
      });
    }

    // init mobile nav again if it's open and this is a reload
    if (window.Shopify.designMode && document.body.classList.contains('reveal-mobile-nav')) {
      theme.openMobileNav();
    }

    // restore all disabled images
    setTimeout(() => theme.manuallyLoadImages(this), 250);
     this.throttledCheckStickyScroll = throttle(this.checkStickyScroll.bind(this), 10);
  }

  connectedCallback() {
    this.section = document.querySelector('.section-header');
    this.transparentPageheader = document.querySelector('.pageheader--transparent-permitted');

    this.setTransparency();
    this.setSticky();
    this.setHeaderHeightProperty();

    window.addEventListener('scroll', this.afterScroll.bind(this));
    this.searchBar = this.section.querySelector('.main-search');
    this.searchUtilityBarOffsetY = theme.getOffsetTopFromDoc(this.searchBar);

    if (this.throttledCheckStickyScroll) {
      window.addEventListener('scroll', this.throttledCheckStickyScroll);
    }

    this.refreshHeader = () => {
      fetch(`${window.location.origin}?sections=header`)
        .then((response) => response.json())
        .then((data) => {
          const template = document.createElement('template');
          template.innerHTML = data.header;
          const selectorsForRefresh = ['#pageheader .logo-area__right__inner .cart-link .cart-link__icon .cart-link__count'];
          for (let i = 0; i < selectorsForRefresh.length; i += 1) {
            const newEl = template.content.querySelector(selectorsForRefresh[i]);
            const currentEl = this.querySelector(selectorsForRefresh[i]);
            currentEl.innerHTML = newEl.innerHTML;
          }
        });
    };
    document.addEventListener('on:cart:change', this.refreshHeader);

    const debouncedAfterResize = debounce(this.afterResize.bind(this), 300);
    this.resizeObserver = new ResizeObserver((entries) => {
      for (let i = 0; i < entries.length; i += 1) {
        const entry = entries[i];
        if (entry.contentBoxSize || entry.contentRect) {
          debouncedAfterResize();
        }
      }
    });
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.resizeObserver.disconnect();
    document.removeEventListener('on:cart:change', this.refreshHeader);
  }

  afterScroll() {
    this.setTransparency();
  }

  checkStickyScroll() {
    if(window.innerWidth < 768){
      if ( window.scrollY < this.offsetHeight) {
        document.body.classList.remove('top-pad');
        this.section.querySelector('#pageheader').classList.remove('scrolled');
      } else {
        document.body.classList.add('top-pad');
        this.section.querySelector('#pageheader').classList.add('scrolled');
      }
    }
  }

  afterResize() {
    if (theme.inlineNavigationCheck) {
      theme.inlineNavigationCheck();
    }
    this.setSticky();
    this.setHeaderHeightProperty();
  }

  setTransparency() {
    if (this.transparentPageheader) {
      const bar = document.querySelector('.section-announcement-bar');
      const scrollThreshold = bar ? (bar.offsetTop + bar.clientHeight) : 0;
      if (!this.isSticky || window.scrollY <= scrollThreshold) {
        this.transparentPageheader.classList.add('pageheader--transparent');
      } else {
        this.transparentPageheader.classList.remove('pageheader--transparent');
      }
    }
  }

  setSticky() {
    this.isSticky = getComputedStyle(this.section).position === 'sticky' || getComputedStyle(this.section).position === '-webkit-sticky';
  }

   setHeaderHeightProperty() {
    let headerHeight = 0;
    let stickyHeaderHeight = 0;
    const topBar = document.querySelector('#announcement-bar-with-slider');
    let top = 0;
    if((topBar && getComputedStyle(topBar).position != '' && getComputedStyle(topBar).position == 'fixed') || (topBar && getComputedStyle(topBar).position != '' && getComputedStyle(topBar).position == 'sticky')){
      top = top + topBar.clientHeight;
    }
    
    if (this.section) {
      this.header = this.section.querySelector('#pageheader');
      headerHeight = Math.ceil(this.header.clientHeight) + top;
      if (this.isSticky) {
        stickyHeaderHeight = Math.ceil(this.header.clientHeight) + top;
      }
    }
/*if(getCookie("sticky_announcementbar") == 'true'){*/
      
   
   /*  } */
   document.querySelector('.section-header').style.top= `${top}px`;
    document.documentElement.style.setProperty('--theme-header-height', `${headerHeight}px`);
    document.documentElement.style.setProperty('--theme-sticky-header-height', `${stickyHeaderHeight}px`);
  }

  updateFromCartChange(html) {
    const selectorForUpdate = '.logo-area__right';
    const template = document.createElement('template');
    template.innerHTML = html;
    
    const elToUpdate = this.querySelector(selectorForUpdate);
    if (elToUpdate) {
      elToUpdate.innerHTML = template.content.querySelector(selectorForUpdate).innerHTML;
    }
  }
};

window.customElements.define('page-header', PageHeader);

const RelatedCollectionLinkButtons = class extends HTMLElement {
  constructor() {
    super();

    const handleResize = debounce(this.truncateButtons.bind(this), 300);
    this.resizeObserver = new ResizeObserver((entries) => {
      for (let i = 0; i < entries.length; i += 1) {
        const entry = entries[i];
        if (entry.contentBoxSize || entry.contentRect) {
          handleResize();
        }
      }
    });
    this.resizeObserver.observe(this);

    this.truncateButtons();
  }

  truncateButtons() {
    if (getComputedStyle(this).display === 'flex') {
      const limit = 4;
      const btns = this.querySelectorAll('.btn');
      if (btns.length > limit && !this.querySelector('.related-collection-links__expander')) {
        Array.from(btns).slice(limit - 1).forEach((el) => el.classList.add('hidden'));
        const expander = document.createElement('a');
        expander.className = 'btn btn--tertiary related-collection-links__expander';
        expander.href = '#';
        expander.innerText = this.dataset.expanderBtnText;
        expander.addEventListener('click', (evt) => {
          evt.preventDefault();
          this.resizeObserver.disconnect();
          [...evt.currentTarget.parentElement.children].forEach((el) => el.classList.remove('hidden'));
          evt.currentTarget.remove();
        });
        this.insertAdjacentElement('beforeend', expander);
      }
    } else {
      const expander = this.querySelector('.related-collection-links__expander');
      if (expander) {
        [...expander.parentElement.children].forEach((el) => el.classList.remove('hidden'));
        expander.remove();
      }
    }
  }
};

window.customElements.define('related-collection-link-buttons', RelatedCollectionLinkButtons);

/* Custom code added S.J. */
const ReadMoreLessButtons = class extends HTMLElement {
  constructor() {
    super();

    const handleResize = debounce(this.truncateContents.bind(this), 300);
    if(this.querySelector('.rte')){
    }
    this.resizeObserver = new ResizeObserver((entries) => {
      for (let i = 0; i < entries.length; i += 1) {
        const entry = entries[i];
        if (entry.contentBoxSize || entry.contentRect) {
          handleResize();
        }
      }
    });
    this.resizeObserver.observe(this);

    // this.truncateContents();
  }

  truncateContents() {
    this.querySelector('.rte').style.maxHeight = '55px';

    const expander = document.createElement('a');
    expander.className = 'btn btn-link read-more-content__expander read-more';
   
    expander.href = '#';
    expander.innerText = 'read more';
    if (document.body.classList.contains('template-collection')) {
      const heading = document.querySelector('h1.pagetitle');
      if (heading) {
        expander.setAttribute('aria-label', `Read more about ${heading.textContent.trim()}`);
      } else {
        expander.setAttribute('aria-label', 'Read more');
      }
    } else {
      expander.setAttribute('aria-label', 'Read more');
    }
    expander.addEventListener('click', (evt) => {
      evt.preventDefault();
      if(evt.currentTarget.classList.contains('read-more')){
        evt.currentTarget.classList.add('read-less');
        evt.currentTarget.classList.remove('read-more');
        expander.innerText = 'read less';
        this.querySelector('.rte').style.maxHeight = this.querySelector('.rte').scrollHeight+'px';
        this.querySelector('.rte').classList.add('expanded-content');
        // this.handleResize();
      }else{
        evt.currentTarget.classList.add('read-more');
        evt.currentTarget.classList.remove('read-less');
        expander.innerText = 'read more';
        this.querySelector('.rte').style.maxHeight = '55px';
        this.querySelector('.rte').classList.remove('expanded-content');
        // this.handleResize();
      }
    });
    this.insertAdjacentElement('beforeend', expander);
  }
};

window.customElements.define('read-more-less-buttons', ReadMoreLessButtons);

const AjaxForm = class extends HTMLElement {
  constructor() {
    super();

    this.submitButton = this.querySelector('.btn[type="submit"]');
    this.form = this.querySelector('form');
    this.focusField = this.querySelector('.focused-field');
    this.termsContainer = this.querySelector('.terms-checkbox-container');

    if(this.focusField != null){
      if(this.termsContainer != null){
        this.focusField.addEventListener('focus', this.showTermsCheckbox.bind(this));
      }
      this.fieldType = this.focusField.classList.contains('email-field') ? 'email' : 'mobile';
      this.focusField.addEventListener('click', this.hideErrorFromField.bind(this));

      if(this.focusField.classList.contains('signup-form__mobile') && this.fieldType == 'mobile'){
        this.focusField.addEventListener('keypress', this.addCountryNumber.bind(this));
        this.focusField.addEventListener('paste', this.addCountryNumber.bind(this));
        this.focusField.addEventListener('keyup', this.handleNumberFormat.bind(this));
      }
    }
    this.form.addEventListener('submit', this.handleSubmitForm.bind(this));
  }
  
  async handleSubmitForm(evt){
    evt.preventDefault();
    evt.stopImmediatePropagation();
    
    if(this.termsContainer != null){
      this.termsContainer.classList.remove('hidden');
      if(!this.termsContainer.querySelector('input[type="checkbox"]').checked){
        this.form.querySelector('.error').innerText = 'Please select terms conditions';
        this.form.querySelector('.error').classList.remove('hidden');
        return false;
      }else{
        this.form.querySelector('.error').innerText = '';
        this.form.querySelector('.error').classList.add('hidden');
      }
    }
    
    if(this.focusField.value == ''){
      this.focusField.placeholder = this.focusField.dataset.errorText;
      this.focusField.classList.add('error');
      return false;
    }else{
      this.focusField.placeholder = this.focusField.dataset.text;
      this.focusField.classList.remove('error');
    }

    if(this.closest('.signup-form').classList.contains('phone-signup-form')){
      const mobileNumber = this.form.querySelector('.signup-form__mobile').value;
      const localization = this.form.querySelector('.localization')?.value || 'US';
      // const regexp =  /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/;
      let regexp;
      switch (localization) {
        case 'GB':
          // UK: +44 7123 123456 or 07123 123456
          regexp = /^(\+44\s?7\d{3}|\(?07\d{3}\)?)\s?\d{3}\s?\d{3}$/;
          break;
        case 'AU':
          // AU: +61 412 345 678 or 0412 345 678
          regexp = /^(\+61\s?4\d{2}\s?\d{3}\s?\d{3}|04\d{2}\s?\d{3}\s?\d{3})$/;
          break;
        case 'CA':
        case 'US':
        default:
          // US/CA: +1 (123) 456-7890 or (123) 456-7890 or 123-456-7890
          regexp = /^(\+1\s?)?(\(?\d{3}\)?[\s.-]?)?\d{3}[\s.-]?\d{4}$/;
          break;
      }
      
      if (!regexp.test(mobileNumber)){
        this.focusField.value = '';
        this.focusField.placeholder = this.focusField.dataset.errorText;
        this.focusField.classList.add('error');
        return false;
      }

      const formData = new FormData();
      formData.append("signUpSourceId", this.querySelector('.attv_id-mobile').value);
      formData.append("smsPhoneNumber", this.focusField.value);
      formData.append("checkLocalization", this.querySelector('.localization').value);
      
      // console.log(formData);
      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript'
        },
        body: formData
      };
      
      try {
        const response = await fetch('https://payments.intotheam.com/private-shopify-app/ajax-allow-origin.php?action=smsSignupSubscribers&shopDomain=iheartraves.myshopify.com', fetchRequestOpts);
        if(response.ok == true){
          const data = await response.json();
          this.termsContainer.querySelector('input[type="checkbox"]').checked = false;
          this.termsContainer.classList.add('hidden');
          this.form.reset();
          if(data.error == 0){
            this.querySelector('.success-msg').classList.remove('hidden');
            setInterval(function() {
              this.querySelector('.success-msg').classList.add('hidden')
            }, 5000);
          }else{
            this.querySelector('.error-container').classList.remove('hidden');
            this.querySelector('.error-container').innerHTML = `<p>${data.message}</p>`;
            setInterval(function() {
              this.querySelector('.error-container').innerHTML = '';
              this.querySelector('.error-container').classList.add('hidden')
            }, 4000);
          }
        }else{
          console.log(response);
        }
      } catch (error) {
        console.log(error);
      }
    }

    if(this.closest('.signup-form').classList.contains('email-signup-form')){
      const pattern = new RegExp(/^(("[\w-\s]+")|([\w-]+(?:\.[\w-]+)*)|("[\w-\s]+")([\w-]+(?:\.[\w-]+)*))(@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$)|(@\[?((25[0-5]\.|2[0-4][0-9]\.|1[0-9]{2}\.|[0-9]{1,2}\.))((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\.){2}(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\]?$)/i);
      const inputValue = this.form.querySelector('.signup-form__email').value;
      
      if (!pattern.test(inputValue)){
        this.focusField.value = '';
        this.focusField.placeholder = this.focusField.dataset.errorText;
        this.focusField.classList.add('error');
        return false;
      }
      
      const formData = new FormData(this.form);
      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };
      
      try {
        const response = await fetch(this.form.action, fetchRequestOpts);
        if(response.status != 429){
          this.termsContainer.querySelector('input[type="checkbox"]').checked = false;
          this.termsContainer.classList.add('hidden');
          this.form.reset();
          this.querySelector('.success-msg').classList.remove('hidden');
          setInterval(function() {
            document.querySelector('.email-signup-form .success-msg').classList.add('hidden')
          }, 5000);
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        // this.dispatchEvent(new CustomEvent('on:cart:error', {
        //   bubbles: true,
        //   detail: {
        //     error: this.errorMsg.textContent
        //   }
        // }));
      }
    }
  }

  handleNumberFormat(evt){
    const val = evt.target.value;
    // evt.target.value = val.replace(/(\d{3})\-?(\d{3})\-?(\d{4})/,'($1) $2-$3');
    // US: (123) 456-7890
    // UK: +44 7123 123456 or 07123 123456
    // AU: +61 412 345 678 or 0412 345 678
    // CA: (123) 456-7890 (same as US)
    let formatted = val;
    const localization = this.form.querySelector('.localization')?.value || 'US';

    // Remove all non-digit except leading +
    let digits = val.replace(/(?!^\+)\D/g, '');
    if (localization === 'GB') {
      if (digits.startsWith('+44')) {
        // UK international
        formatted = digits.replace(/^(\+44)(\d{4})(\d{6})$/, '$1 $2 $3');
      } else if (digits.startsWith('07')) {
        // UK local
        formatted = digits.replace(/^(07\d{3})(\d{6})$/, '$1 $2');
      }
    } else if (localization === 'AU') {
      if (digits.startsWith('+61')) {
        // AU international
        formatted = digits.replace(/^(\+61)(\d{1})(\d{3})(\d{3})(\d{3})$/, '$1 $2 $3 $4 $5').replace(/\s+$/, '');
      } else if (digits.startsWith('04')) {
        // AU local
        formatted = digits.replace(/^(04\d{2})(\d{3})(\d{3})$/, '$1 $2 $3');
      }
    } else if (localization === 'CA' || localization === 'US') {
      if (digits.startsWith('+1')) {
        // US/CA international
        formatted = digits.replace(/^(\+1)(\d{3})(\d{3})(\d{4})$/, '$1 ($2) $3-$4');
      } else if (digits.length === 10) {
        // US/CA local
        formatted = digits.replace(/^(\d{3})(\d{3})(\d{4})$/, '($1) $2-$3');
      }
    }
    evt.target.value = formatted;
  }

  addCountryNumber(evt){
    const val = evt.target.value;
    if(val.length < 4){
      const localization = this.form.querySelector('.localization')?.value || 'US';
      if (localization === 'GB') {
        evt.target.value = '+44 ';
      } else if (localization === 'AU') {
        evt.target.value = '+61 ';
      } else if (localization === 'CA' || localization === 'US') {
        evt.target.value = '+1 ';
      }
    }
  }

  hideErrorFromField(){
    this.focusField.classList.remove('error');
    this.focusField.placeholder = this.focusField.dataset.text;
  }

  showTermsCheckbox(){
    this.termsContainer.classList.remove('hidden');
  }
}

window.customElements.define('ajax-form', AjaxForm);


class CustomSlider extends HTMLElement {
  constructor() {
    super();
    this.slider = null;
    this.slides = [...this.querySelectorAll('.slider__item:not([hidden])')];
    this.sliderType = this.dataset.sliderType;
    if (this.slides.length < 2) {
      // this.setCarouselState(false);
      return;
    }

    window.initLazyScript(this, this.init.bind(this));
  }

  init() {
    this.sliderElement = this.querySelector('.product-slick--slider') || this.querySelector('.custom--slider');
    this.sliderConfiguration = this.querySelector('[type="application/json"]').textContent;
    this.nav = this.querySelector('.slider-nav');

    if (this.nav) {
      this.prevBtn = this.querySelector('button[name="prev"]');
      this.nextBtn = this.querySelector('button[name="next"]');
    }

    this.addListeners();
    
    this.initSlider();
  }

  initSlider() {
    const configuration = JSON.parse(this.sliderConfiguration);

    if(this.sliderType == 'owl'){
      this.sliderElement.classList.add('owl-carousel');
      this.slider = $(this.sliderElement).owlCarousel(configuration);
    }else{
      this.slider = $(this.sliderElement).slick(configuration);
    }

    this.setButtonStates();
  }

  addListeners() {
    if (this.nav) {
      this.navClickHandler = this.handleNavClick.bind(this);
      this.nav.addEventListener('click', this.navClickHandler);
    }    
  }

  removeListeners() {
    if (this.nav) {
      this.nav.removeEventListener('click', this.navClickHandler);
    }
  }

  /**
   * Handles 'scroll' events on the slider element.
   */
  handleScroll() {
    const previousIndex = this.currentIndex;
    this.currentIndex = Math.round(Math.abs(this.slider.scrollLeft) / this.slideSpan);

    if (this.nav) {
      this.setButtonStates();
    }

    if (this.dataset.dynamicHeight === 'true') {
      this.updateDynamicHeight();
    }

    if (this.dataset.dispatchEvents === 'true' && previousIndex !== this.currentIndex) {
      this.dispatchEvent(
        new CustomEvent('on:carousel-slider:select', {
          bubbles: true,
          detail: {
            index: this.currentIndex,
            slide: this.slides[this.currentIndex]
          }
        })
      );
    }
  }

  /**
   * Handles 'click' events on the nav buttons container.
   * @param {object} evt - Event object.
   */
  handleNavClick(evt) {
    if (!evt.target.matches('.slider-nav__btn')) return;

    if(this.sliderType == 'owl'){
      if (evt.target.name === 'next' ) {
        this.slider.trigger('next.owl.carousel');
      } else {
        this.slider.trigger('prev.owl.carousel');
      }
    }else{
      if (evt.target.name === 'next' ) {
        this.slider.slick('slickNext');
      } else {
        this.slider.slick('slickPrev');
      }
    }
  }

  /**
   * Handles 'on:debounced-resize' events on the window.
   */
  handleResize() {
    if (this.nav) this.removeListeners();
    this.initSlider();
  }

  /**
   * Show a specific item inside this slider.
   * @param {Element} el - Slide element
   * @param {string} transition - Transition to pass to behavior parameter of scrollTo (optional)
   */
  scrollToElement(el, transition) {
    
  }

  /**
   * Sets the disabled state of the nav buttons.
   */
  setButtonStates() {
    // console.log('dddd');
    // this.prevBtn.disabled = this.getSlideVisibility(this.slides[0]) && this.slider.scrollLeft === 0;
    // this.nextBtn.disabled = this.getSlideVisibility(this.slides[this.slides.length - 1]);
  }
}

customElements.define('custom-slider', CustomSlider);


/* End */

window.initLazyScript = initLazyScript;

if (!theme.Shopify) theme.Shopify = {};
try {
  theme.Shopify.features = JSON.parse(document.documentElement.querySelector('#shopify-features').textContent);
} catch (e) {
  theme.Shopify.features = {};
}

document.addEventListener('DOMContentLoaded', () => {
  /* removing $ FP start 04-04-2025 */
  /*if(getCookie("remove_price_icon") == "true"){
    document.querySelector('body').classList.add('hide-price-sign');
  }*/
  /* removing $ FP end 04-04-2025 */
  document.addEventListener('click', function(evt){

    // if(evt.target.classList.contains('wk-button') || (evt.target.closest('a') != null && evt.target.closest('a').classList.contains('wk-button'))){
    //   if(typeof gtag != "undefined"){
    //     gtag('event', 'Wishlist Page Tracking', {'event_category': 'wishlist page','event_label': 'wishlist product click','value': 'Product click' });
    //   }
    // }
    // console.log(evt, evt.target);

    if(typeof gtag != "undefined"){
      /* home page */
      if(document.querySelector('body').classList.contains('template-index')){
        if(evt.target.closest('.banner-link') != null || evt.target.classList.contains('banner-link')){
          const alt = evt.target.closest('.banner-link').dataset.alt;
          gtag('event', 'Homepage Tracking', {'event_category': 'homepage','event_label': 'hp hero click','value': alt.toLowerCase()});
        }
        if(evt.target.classList.contains('mobile-scroll--link')){
          const t = evt.target.innerText;
          gtag('event', 'Homepage Tracking', {'event_category': 'homepage','event_label': 'hp mobile collections carousel','value': t.toLowerCase()});
        }
      }
      
      /* product page page */
      if(document.querySelector('body').classList.contains('template-product')){
        if(evt.target.closest('.related-products') != null && !evt.target.classList.contains('quickbuy-toggle')){
          const title = evt.target.closest('.product-block').dataset.productName;
          gtag('event', 'Product Page Tracking', {'event_category': 'product page','event_label': 'product recommendations click','value': title.toLowerCase()});
        }
        if(evt.target.closest('.recently-viewed-wrapper') != null && !evt.target.classList.contains('quickbuy-toggle')){
          const title = evt.target.closest('.product-block').dataset.productName;
          gtag('event', 'Product Page Tracking', {'event_category': 'product page','event_label': 'recently viewed click','value': title.toLowerCase()});
        }
        if(evt.target.classList.contains('stamped-summary-actions-newreview')){
          gtag('event', 'Product page Tracking', {'event_category': 'product page','event_label': 'reviews engagement','value': 'write a review click'});
        }
        if(evt.target.closest('.stamped-instagram-nav') != null){
          gtag('event', 'Product Page Tracking', {'event_category': 'product page','event_label': 'ugc gallery click','value': 'ugc gallery'});
        }
        if(evt.target.closest('.main-carousel') != null && evt.target.closest('.main-product') != null){
          const title = evt.target.closest('.main-product').dataset.title;
          gtag('event', 'Product Page Tracking', {'event_category': 'product page','event_label': 'image carousel click','value': title});
        }
      }
  
      /* collection page */
      if(document.querySelector('body').classList.contains('template-collection')){
        if(evt.target.closest('.product-block') != null && !evt.target.classList.contains('quickbuy-toggle')){
          const title = evt.target.closest('.product-block').dataset.productName;
          gtag('event', 'Collection Page Tracking', {'event_category': 'collection page','event_label': 'product click','value': title.toLowerCase()});
        }
        if(evt.target.classList.contains('pagination__prev')){
          gtag('event', 'Collection Page Tracking', {'event_category': 'collection page','event_label': 'page click','value': 'back page click'});
        }
        if(evt.target.classList.contains('pagination__next')){
          gtag('event', 'Collection Page Tracking', {'event_category': 'collection page','event_label': 'page click','value': 'next page click'});
        }
        if(evt.target.classList.contains('read-more-content__expander')){
          gtag('event', 'Collection Page Tracking', {'event_category': 'collection page','event_label': 'read more click','value': 'read more click'});
        }
        if(evt.target.classList.contains('mobile-scroll--link')){
          const t = evt.target.innerText;
          gtag('event', 'Collection Page Tracking', {'event_category': 'collection page','event_label': 'mobile collections carousel','value': t.toLowerCase()});
        }
      }
  
      /* footer page */
      if(evt.target.closest('.section-footer__menu-block__menu') != null){
        const t = evt.target.innerText;
        gtag('event', 'Footer Tracking', {'event_category': 'footer','event_label': 'footer menu click','value': t.toLowerCase()+' click'});
      }
      if(evt.target.classList.contains('social__link') && evt.target.closest('.social') != null){
        const label = evt.target.title;
        gtag('event', 'Footer Tracking', {'event_category': 'footer','event_label': 'social icons click','value': label.toLowerCase()+' click'});
      }
  
      /* sidecart event */
      if(evt.target.classList.contains('cart-drawer-checkout') && evt.target.closest('.checkout-buttons') != null){
        gtag('event', 'Slideout Cart Tracking', {'event_category': 'Slideout Cart','event_label': 'initiate checkout','value': 'slidecart checkout click'});
      }
  
      if(evt.target.classList.contains('empty-cart-drawer-link')){
        const label = evt.target.innerText;
        gtag('event', 'Slideout Cart Tracking', {'event_category': 'Slideout Cart','event_label': 'empty cart category click','value': label +' click'});
      }
  
      if(evt.target.classList.contains('empty-cart-drawer-login-link')){
        const label = evt.target.innerText;
        gtag('event', 'Slideout Cart Tracking', {'event_category': 'Slideout Cart','event_label': 'empty cart login','value': 'empty cart login click'});
      }
  
      /* account pages */    
      if(evt.target.classList.contains('btn') && evt.target.closest('form') != null && evt.target.closest('form').id == 'customer_login'){
        gtag('event', 'Account Tracking', {'event_category': 'account','event_label': 'sign in page','value': 'sign in click'});
      }
  
      if(evt.target.classList.contains('create-account-link')){
        gtag('event', 'Account Tracking', {'event_category': 'account','event_label': 'sign in page','value': 'create an account click'});
      }
  
      if(evt.target.classList.contains('btn') && evt.target.closest('form') != null && evt.target.closest('form').id == 'create_customer'){
        gtag('event', 'Account Tracking', {'event_category': 'account','event_label': 'create account page','value': 'create an account click'});
      }
  
      if(evt.target.classList.contains('view-address-link')){
        gtag('event', 'Account Tracking', {'event_category': 'account','event_label': 'view addresses','value': 'view addresses'});
      }
  
      if(evt.target.classList.contains('logout-link')){
        gtag('event', 'Account Tracking', {'event_category': 'account','event_label': 'sign out','value': 'account sign out'});
      }
  
      if(evt.target.classList.contains('view-order-link')){
        gtag('event', 'Account Tracking', {'event_category': 'account','event_label': 'view order ','value': 'view order'});
      }
  
      /* blog article */
      if(evt.target.classList.contains('blog-cat-logo') || evt.target.classList.contains('blog-cat-nav-link')){
        gtag('event', 'Blog Tracking', {'event_category': 'blog','event_label': 'blog page view','value': 'blog page view'});
      }
  
      if(evt.target.classList.contains('read-article') || evt.target.closest('.article-link') != null){
        const title = evt.target.closest('.article-sort-content').querySelector('.article-link').innerText;
        gtag('event', 'Blog Tracking', {'event_category': 'blog','event_label': 'blog post view','value': title});
      }
  
      /* cart page */
      if(evt.target.id == 'update-cart' && evt.target.closest('.cart-form') != null){
        gtag('event', 'Cart Page Tracking', {'event_category': 'Cart Page','event_label': 'initiate checkout','value': 'cart page checkout click'});
      }
  
      /* wishlist */   
      if(evt.target.classList.contains('.wk-icon') && evt.target.classList.contains('wk-button') || (evt.target.closest('a') != null && evt.target.closest('a').classList.contains('wk-button'))){
        gtag('event', 'Navigation Tracking', {'event_category': 'navigation','event_label': 'account click','value': 'wishlist'});
      }
      if(evt.target.classList.contains('wk-cta-button') || (evt.target.closest('button') != null && evt.target.closest('button').classList.contains('wk-cta-button'))){
        const productTitle = evt.target.closest('.wk-product-card').querySelector('.wk-product-title').innerText;
        gtag('event', 'Wishlist Page Tracking', {'event_category': 'wishlist page','event_label': 'wishlist add to cart','value': productTitle.toLowerCase()});
      }
      if(evt.target.classList.contains('wk-button') || (evt.target.closest('button') != null && evt.target.closest('button').classList.contains('wk-button'))){
        const productTitle = evt.target.closest('.product-block').dataset.productName;
        if(document.querySelector('body').classList.contains('template-index')){
          gtag('event', 'Homepage Tracking', {'event_category': 'homepage','event_label': 'add to wishlist click','value': productTitle.toLowerCase()});
        }
        if(document.querySelector('body').classList.contains('template-collection')){
          gtag('event', 'Collection Page Tracking', {'event_category': 'collection page','event_label': 'add to wishlist click','value': productTitle.toLowerCase()});
        }
        if(document.querySelector('body').classList.contains('template-product')){
          gtag('event', 'Product Page Tracking', {'event_category': 'product page','event_label': 'add to wishlist click','value': productTitle.toLowerCase()});       
        }
      }

      /* custom pages */
      /* halloween */
      if(evt.target.closest('.halloween-2023-section') != null){
        gtag('event', 'Halloween Landing Page Tracking', {'event_category': 'Landing Page','event_label': 'Halloween Landing Page','value': 'Halloween Landing Page Banner Clicked'});
      }
      if(evt.target.closest('.halloween-slide') != null){
        const title = evt.target.closest('.halloween-slide').querySelector('a').title;
        gtag('event', 'Halloween Landing Page Tracking', {'event_category': 'Landing Page','event_label': 'Product Carousel Click','value': title});
      }
      if(evt.target.closest('.halloween-full-block') != null || evt.target.classList.contains('halloween-shop-btn')){
        gtag('event', 'Halloween Landing Page Tracking', {'event_category': 'Landing Page','event_label': 'Bottom Banner Click','value': 'Bottom Banner Click Clicked'});
      }
      if(evt.target.closest('.halloween-block') != null){
        const title = evt.target.closest('.halloween-block').querySelector('a').title;
        gtag('event', 'Halloween Landing Page Tracking', {'event_category': 'Landing Page','event_label': 'Collections Click','value': title});
      }

      /* lux rave */
      if(evt.target.tagName == 'BUTTON' && evt.target.closest('.lux-form') != null ){
        gtag('event', 'lux rave landing page', {'event_category': 'lux rave landing page','event_label': 'lux rave email form click','value': "lux rave email submitted"});
      }
      if(evt.target.tagName == 'BUTTON' && evt.target.closest('.lux-gift-email-wrap') != null ){
        gtag('event', 'lux rave landing page', {'event_category': 'lux rave landing page','event_label': 'free gift form click','value': "free gift form submitted"});
      }
      if(evt.target.classList.contains('directions-btn')){
        gtag('event', 'lux rave landing page', {'event_category': 'lux rave landing page','event_label': 'get directions click','value': "get directions"});
      }
      if(evt.target.closest('.collection_list_slides') != null){
        const title = evt.target.closest('a').dataset.title;
        gtag('event', 'lux rave landing page', {'event_category': 'lux rave landing page','event_label': 'collections click','value': title.toLowerCase()});
      }
      
    }
    
    if(evt.target.classList.contains('mobile-show-search-link') || (evt.target.closest('a') != null && evt.target.closest('a').classList.contains('mobile-show-search-link'))){
      evt.preventDefault();
      // const searchButton = document.querySelector('.show-search-link');
      // searchButton.dispatchEvent(new Event("click"));
      document.body.classList.add('show-search');
      setTimeout(() => {
        this.querySelector('.main-search__input').focus();
      }, 500);
    }
    
    // console.log(evt, evt.target);
    if(evt.target.classList.contains('quick-view-overlay')){
      const quickBuyContainer = document.querySelector('.quickbuy-container .quickbuy-content');
      // console.log(quickBuyContainer);
      const closeButton = quickBuyContainer.closest('.quickbuy-container').querySelector('.close-detail');
      // console.log(closeButton);
      closeButton.dispatchEvent(new Event("click"));
    }
  });
  
  // Smooth scroll to an internal link
  const internalLinksSmoothScroll = (evt) => {
    const link = evt.target.tagName === 'A' ? evt.target : evt.target.closest('a');
    if (link && link.getAttribute('href') && link.getAttribute('href').length > 1 && link.getAttribute('href')[0] === '#') {
      const target = document.querySelector(link.getAttribute('href'));
      if (target && target.offsetParent) {
        evt.preventDefault();
        theme.scrollToRevealElement(target);
      }
    }
  };

  if (theme.settings.internalLinksSmoothScroll) {
    document.addEventListener('click', internalLinksSmoothScroll);
  }

  // Enable focus outlines after using the tab key, and remove scroll internal link scroll handler
  const tabCheck = (evt) => {
    if (evt.code === 'Tab') {
      document.body.classList.add('tab-used');
      document.removeEventListener('keydown', tabCheck);
      document.removeEventListener('click', internalLinksSmoothScroll);
    }
  };
  document.addEventListener('keydown', tabCheck);

  // Opening links in a new tab
  if (theme.settings.externalLinksNewTab) {
    document.addEventListener('click', (evt) => {
      const link = evt.target.tagName === 'A' ? evt.target : evt.target.closest('a');
      if (link && link.href && link.tagName === 'A' && window.location.hostname !== new URL(link.href).hostname) {
        link.target = '_blank';
      }
    });
  }

  // Mobile nav
  theme.addDelegateEventListener(document, 'click', '.mobile-nav-toggle', (evt) => {
    evt.preventDefault();
    if (document.body.classList.contains('enable-mobile-nav-transition')) {
      // hide nav
      document.body.classList.remove('reveal-mobile-nav', 'reveal-mobile-nav--revealed');
      if(typeof gtag != "undefined"){
        gtag('event', 'Navigation Tracking', {'event_category': 'navigation','event_label': 'menu close','value':'menu close click'});
      }
      setTimeout(() => {
        document.body.classList.remove('enable-mobile-nav-transition');
      }, 750);
    } else {
      // show nav
      theme.openMobileNav();
    }
  });

  theme.openMobileNav = () => {
    document.body.classList.add('enable-mobile-nav-transition');
    setTimeout(() => {
      document.body.classList.add('reveal-mobile-nav');

      // after reveal, add class
      const cs = getComputedStyle(document.querySelector('.mobile-navigation-drawer .navigation__tier-1 > .navigation__item > .navigation__link'));
      const delayS = parseFloat(cs.transitionDelay.split(',')[0]) + parseFloat(cs.transitionDuration.split(',')[0]);
      setTimeout(() => {
        document.body.classList.add('reveal-mobile-nav--revealed');
      }, delayS * 1000);

      // restore all disabled images
      theme.manuallyLoadImages(document.querySelector('.mobile-navigation-drawer'));
    }, 10);
  };

  // Window shade
  const shade = document.querySelector('.page-shade');
  if (shade) {
    shade.addEventListener('click', (evt) => {
      evt.preventDefault();
      document.body.classList.remove('reveal-mobile-nav', 'show-search');
      setTimeout(() => {
        document.body.classList.remove('enable-mobile-nav-transition');
      }, 750);
    });
  }

  // Footer Navigations Toggle
  if(window.innerWidth < 767){
    document.querySelectorAll('.section-footer__menu-title').forEach((el) => {
      el.addEventListener('click', function(){
        el.closest('.section-footer__menu-block').classList.toggle('is-show');
        // el.nextElementSibling.classList.toggle('is-show');
      });
    });
  }
  document.querySelector('#scroll-top-btn').addEventListener('click', function(evt){
    evt.preventDefault();
    // theme.scrollToRevealElement(document.querySelector('.section-header'))
    // window.scrollTo(0, this.scrollY);
    window.scrollTo({ top:0, behavior: 'smooth' });
  });

  /*if(getCookie("enable_currency_codes") == "true"){
    const cartItems = document.querySelectorAll('.cart-item-list__body > div');
    cartItems.forEach((el) => {
      const currencyCode = el.querySelector('.cart-item__total');
      if(currencyCode.dataset.currencyCode == 'CAD' || currencyCode.dataset.currencyCode == 'AUD'){
        el.querySelectorAll('.cart-item__total .theme-money').forEach((e) =>{
          e.innerHTML = e.dataset.currencyCode;
        })
      }
    });
    document.querySelectorAll('.price:not(.fully-soldout)').forEach((price) => {
      if(price.dataset.currencyCode == 'CAD' || price.dataset.currencyCode == 'AUD'){
        if(price.querySelector('.price__current') != undefined){
          mainPrice = price.querySelector('.price__current').dataset.currencyCode;
          price.querySelector('.price__current').innerHTML = mainPrice;
        }
        if(price.querySelector('.price__was') != null){
          compareAtPrice = price.querySelector('.price__was').dataset.currencyCode;
          price.querySelector('.price__was').innerHTML = compareAtPrice;
        }
      }
    });
  }*/

  /* A/B test hp collection order start d.dabhi 27-3-2025 */
  // if (getCookie("change_collections_order") === "true") {
  //       if (document.body.classList.contains("template-index")) {
  //           document.querySelectorAll(".section-collection-list").forEach(order => {
  //               order.classList.add("change-order");
  //           });
  //       }
  //   }
  /* A/B test hp collection order start d.dabhi 27-3-2025 */
});

window.onpageshow = () => {
  fetch(`${theme.routes.cart}.js`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      return response.json();
    })
    .then(async (data) => {
      const { items } = data;
      const cartString = items.map((item) => `${item.key}|${item.quantity}`).join(',');
      const cartUint8 = new TextEncoder().encode(cartString);
      const cartHashBuffer = await crypto.subtle.digest('SHA-256', cartUint8);
      const cartHashArray = Array.from(new Uint8Array(cartHashBuffer));
      const cartHashHex = cartHashArray.map((x) => x.toString(16).padStart(2, '0')).join('');
      const cartHash = cartHashHex.toString();
      const cartHashFromDom = document.querySelector('.cart-link').dataset.hash;
      if (cartHash !== cartHashFromDom) {
        document.dispatchEvent(
          new CustomEvent('on:cart:change', { bubbles: true, cancelable: false })
        );
      }
    })
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.log(error.message);
    });
};

/* A/B test new image position on mobile start d.dabhi 10-03-2025 */
document.addEventListener("DOMContentLoaded", function () {
    // Function to get cookie value by name
    function getCookie(name) {
        const cookies = document.cookie.split("; ");
        for (let cookie of cookies) {
            const [cookieName, cookieValue] = cookie.split("=");
            if (cookieName === name) return cookieValue;
        }
        return null;
    }


    /* A/B Reorder filetr size and type 13-8-2025 
  if (getCookie("reorder_filter") == 'true') {
    const filterGroups = document.querySelectorAll('.filters-group-container .filter-group');
    let typeFilterGroup = null;
    let sizeFilterGroup = null;
        for (const group of filterGroups) {
          const heading = group.querySelector('.filter-group__heading');
          if (heading && heading.classList.contains('type-filter')) { 
            typeFilterGroup = group;
          }
          if (heading && heading.classList.contains('Size-filter')) { // Note the uppercase 'S' in Size-filter
            sizeFilterGroup = group;
          }
        }
        if (typeFilterGroup && sizeFilterGroup ) {
          typeFilterGroup.parentNode.insertBefore(sizeFilterGroup, typeFilterGroup);
        }
  }
   A/B Reorder filetr size and type 13-8-2025 */
//console.log(' hello for hide outfit');
  /*const cookieValue = getCookie("hide_outfit");
 
  if(cookieValue != ''){
    const navLists = document.querySelectorAll('ul.navigation__tier-1');

    if ( !navLists.length) {
     
      return;
    }

    navLists.forEach(function (ul) {
      const directItems = Array.from(ul.children).filter(el => el.tagName.toLowerCase() === 'li');

      if (directItems.length < 4) return;

      if (cookieValue === 'change_with_sets') {
        console.log('replace with sets');
        const newLi = document.createElement('li');
        newLi.className = 'navigation__item';
        newLi.innerHTML = '<a href="/collections/two-piece-outfits" class="navigation__link">Sets</a>';
        ul.replaceChild(newLi, directItems[3]);

      } else if (cookieValue === 'only_hide') {
       console.log('hide outfit');
        directItems[3].style.display = 'none';
      } else {
        console.log('do nothing');
      }
    });
  }*/

     // Check conditions: cookie value & body class
     if (document.body.classList.contains("template-product")) {
          const mobileHeader = document.querySelector(".mobile_header_title");
          const productForm = document.querySelector(".product-form");
      
          const selectors = [
              ".title-with-promomessage",
              ".price-container",
              ".shopify-app-block",
              
          ];
      
          function cloneContentToMobileHeader() {
              selectors.forEach(selector => {
                  const original = productForm.querySelector(selector);
                  const mobileTarget = mobileHeader.querySelector(selector);
      
                  if (original && mobileTarget && mobileTarget.innerHTML.trim() === "") {
                      mobileTarget.innerHTML = original.innerHTML;
                      original.classList.add("desktop-only");
                  }
              });
            if (window.innerWidth < 768) { 
              const extraDiv = productForm.querySelector(".attentive-wp");
              if (extraDiv && !mobileHeader.contains(extraDiv)) {
                  mobileHeader.appendChild(extraDiv);
                  extraDiv.classList.remove("desktop-only"); // Replace with actual class
              }
            }
          }
      
          // Run on page load
          cloneContentToMobileHeader();
      
          // Optional: Run on resize if needed
          window.addEventListener("resize", function () {
              if (window.innerWidth < 768) {
                  cloneContentToMobileHeader();
              }
          });
      }

    

  

  

});

/* A/B test product horizontal scroll   on product page  page  FP start 1-05-2025 */
  /*if(getCookie("product_horizontal_slider") == 'true'){
      const productHorizontalSlider = document.querySelector('.related-products');
   

    if (productHorizontalSlider ) { 
    
      
   productHorizontalSlider.classList.add('product-horizontal-slider');
    }
  }*/
